import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap"
import QRCode from "react-qr-code";

//Components
import AddressForm from "./components/checkout/address/AddressForm";
import RadioCard from "./components/ui/RadioCard";
import Field from "./components/ui/Field";
import CartModal from "./components/checkout/cart/CartModal";
import MobileCartBanner from "./components/checkout/cart/MobileCartBanner";
import WeekCalendar from "./components/checkout/steps/WeekCalendar";
import TimePicker from "./components/checkout/steps/TimePicker";
import StepInvoice from "./components/checkout/steps/InvoiceStep";
import OrderSummary from "./components/checkout/OrderSummary";
import TableOrderConfirmation from "./components/checkout/TableOrderConfirmation";
import PaymentStep from "./components/checkout/steps/PaymentStep";
import SavedCardsPanel from "./components/checkout/SavedCardsPanel";
import TableSessionPayment from "./TableSessionPayment";
import StepperBar from "./components/checkout/stepper/StepperBar";
import MobileStepperBar from "./components/checkout/stepper/MobileStepperBar";
import ModalYappyBtn from "../../Yappy/ModalYappyBtn";
import ModalStatusPayment from "../ModalStatusPayment";
import LoginUser from "../../Profile/LoginUser";

//Alerts
import Swal from "sweetalert2";
import ErrorAlert from "../../Alerts/ErrorAlert";
import AlertSmall from "../../Alerts/AlertSmall";
import LoadingAlert from "../../Alerts/LoadingAlert";
// Used by the existing payment-status listeners below. Table-session events are
// deliberately handled only by TableSessionContext.
import Socket from "../../../utils/Socket.config";
//API
import { TableSessionsFetchAPI } from "../../../api/TableSessions";
import { DonleeFechtAPI } from "../../../api/Donlee";
import { DgiFetchAPI } from "../../../api/Dgi";
import { AuthFetchAPI } from "../../../api/Auth";
import { BusinessFetchAPI } from "../../../api/Business";
import { BranchOfficeFetchAPI } from "../../../api/BranchOffices";
import { MachinesFetchAPI } from "../../../api/Machines";
import { EcommerceContentFetchAPI } from "../../../api/EcommerceContent";
import { PreOrderFetchAPI } from "../../../api/PaymentPost/PreOrder";
import { TilopayFetchAPI } from "../../../api/Tilopay";
import TilopayPaymentModal from "../../Tilopay/TilopayPaymentModal";
import { isTilopayPayment } from "../../Tilopay/tilopayHelpers";
import { OrderNumberFetchAPI } from "../../../api/PaymentPost/OrderNumber";
import { OrdersFetchAPI } from "../../../api/Orders";
import { EvertechFetchAPI } from "../../../api/Evertech";
import { PowertranzFetchAPI } from "../../../api/Powertranz1";
import { YappyFetchAPI } from "../../../api/PaymentPost/Yappy";
import { DeliveryOrdersFetchAPI } from "../../../api/DeliveryOrders";
import { AsapFetchAPI } from "../../../api/Asap";
import { TookanFetchAPI } from "../../../api/Tookan";
import { ClientsFetchAPI } from "../../../api/Clients";
//Utils
import { generateRandomString } from "../../../utils/RandomString";
import { haversineKm, computeKilometerFee, resolveZoneFee } from '../../../utils/CalculateDistanceFee';
import { buildCloudinaryImageUrl } from "../../../utils/CloudinaryImageUrl";
import { RenderIf } from "../../../utils/RenderIf";
import validDomain from "../../../utils/validDomain";
import initData from "../../Home/initData";
import {
  getCartAvailabilityMessage,
  sanitizeStoredCartAgainstMenu
} from "../../../utils/cartAvailability";
import {
  isActiveTableSessionStatus,
  useTableSession
} from "../../../context/TableSessionContext";
import TuPedido from "./components/checkout/steps/TuPedido";
import CustomizeTableInvoiceModal from "./CustomizeTableInvoiceModal";
import AddMoreButton from "./AddMoreButton";

/* ════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════ */
const fmt = (n) => '$' + Number(n).toFixed(2);
const DIRECT_ORDER_CONTEXT_CLEARED_EVENT = "checkout:direct-order-context-cleared";
const CHECKOUT_CALCULATION_SESSION_KEY = "checkoutCalculationSessionId";

const createCheckoutCalculationSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizePaymentText = (value) => String(value || "").trim().toLowerCase();

const getPaymentIntegrationId = (payment = {}) => Number(
  payment?.integration?.id ?? payment?.integration_id ?? 0
);

const getPaymentIntegrationName = (payment = {}) => normalizePaymentText(
  payment?.integration?.name ?? payment?.integration_name ?? ""
);

const getPaymentName = (payment = {}) => normalizePaymentText(payment?.payment ?? "");

const isCreditOrBankTransfer = (payment = {}) => {
  const name = getPaymentName(payment);
  return name === "credito" || name.includes("transf./deposito") || name.includes("bancaria");
};

const paymentRequiresLogin = (payment = {}) => (
  Array.isArray(payment?.params) &&
  payment.params.some(param =>
    param?.name === "need_login_to_pay" &&
    String(param?.value || "").trim().toLowerCase() === "yes"
  )
);

const isYappyPayment = (payment = {}) => {
  const name = getPaymentName(payment);
  const integrationName = getPaymentIntegrationName(payment);
  const integrationId = getPaymentIntegrationId(payment);
  return name.includes("yappy") || (integrationId === 2 && integrationName.includes("banco general"));
};

const isEvertecPayment = (payment = {}) => {
  const name = getPaymentName(payment);
  const integrationName = getPaymentIntegrationName(payment);
  const integrationId = getPaymentIntegrationId(payment);
  return (
    name.includes("evertech") ||
    name.includes("evertec") ||
    (integrationId === 5 && integrationName.includes("pasarela") && !name.includes("powertranz"))
  );
};

const isPowerTranzPayment = (payment = {}) => {
  const name = getPaymentName(payment);
  return name.includes("powertranz");
};

const getGatewayStatus = (data = {}) => String(
  data?.status?.status ??
  data?.payment_status ??
  data?.status ??
  ""
).toUpperCase();

const getGatewayStatusMessage = (data = {}, fallback = "No se pudo procesar el pago") => (
  data?.status?.message ||
  data?.message ||
  data?.error ||
  fallback
);

const buildEvertecVoucher = (data = {}) => {
  const payment = Array.isArray(data?.payment) ? data.payment[0] : null;
  const statusDate = String(data?.status?.date || new Date().toISOString());
  const [datePart, timePart = ""] = statusDate.split("T");
  const processorFields = Array.isArray(payment?.processorFields)
    ? payment.processorFields
    : [];
  const detailParamsPayment = processorFields
    .map(process => `${String(process?.keyword || "").toUpperCase()}:                ${process?.value || ""}`)
    .join("\r\n");

  if (!payment) return data?.voucher || "";

  return `\r\n            ***  EVERTEC  ***             \r\n           ${payment?.issuerName || ""}           \r\nREF:                            ${payment?.reference || ""}\r\nDATE: ${datePart}               ${timePart}\r\n\r\nDESCRIPTION:   ${data?.request?.payment?.description || ""}\r\nTOTAL:                         ${payment?.amount?.from?.currency || ""}. ${payment?.amount?.from?.total || ""} \r\n            +++ DETAIL PAYMENT +++            \r\n\r\nPAYMENT METHOD:               ${String(payment?.paymentMethodName || "").toUpperCase()}\r\n${detailParamsPayment}\r\nSTATUS:                       ${data?.status?.status || ""}\r\nRECEIPT:                       ${payment?.receipt || ""}\r\n\r\n                +++ PAYER +++                \r\n\r\nNAME:                        ${data?.request?.payer?.name || ""}\r\nEMAIL:                        ${data?.request?.payer?.email || ""}\nPHONE:                        ${data?.request?.payer?.mobile || ""}\n           ****** FIN  ******            \r\n`;
};

const getYappyEventStatus = (event, detail) => {
  if (event === "create") return "CREATE";
  if (event === "success") return "SUCCESS";

  const eventPayload = event && typeof event === "object" ? event : {};
  const detailPayload = detail && typeof detail === "object" ? detail : {};
  const candidates = [
    typeof event === "string" ? event : "",
    eventPayload?.status,
    eventPayload?.Status,
    eventPayload?.transactionStatus,
    eventPayload?.paymentStatus,
    eventPayload?.code,
    eventPayload?.message,
    detailPayload?.status,
    detailPayload?.Status,
    detailPayload?.transactionStatus,
    detailPayload?.paymentStatus,
    detailPayload?.code,
    detailPayload?.message
  ];

  const found = candidates.find(value => value !== null && value !== undefined && value !== "");
  return String(found || "").toUpperCase();
};

const isYappyRejectedStatus = (status) => (
  status.includes("DENIED") ||
  status.includes("REJECT") ||
  status.includes("DECLIN") ||
  status.includes("FAILED") ||
  status.includes("CANCEL")
);




// Helper to determine payment method display info based on its name and integration
const normalizeAddressForCheckout = (address = {}) => {
  const labelMap = {
    home: "Casa",
    work: "Trabajo",
    other: "Otro"
  };

  const iconMap = {
    home: "🏠",
    work: "🏢",
    other: "📍"
  };

  const rawType =
    address.label_key ||
    address.type ||
    address.label ||
    "other";

  const normalizedType = ["home", "work", "other"].includes(String(rawType).toLowerCase())
    ? String(rawType).toLowerCase()
    : "other";

  return {
    ...address,
    id: address.id || address._id || address.address_id || `addr-${Date.now()}`,
    icon: address.icon || iconMap[normalizedType],
    label: address.label || labelMap[normalizedType] || "Dirección",
    label_key: normalizedType,
    line:
      address.line ||
      [address.house_num, address.name].filter(Boolean).join(", ") ||
      address.address ||
      "",
    sub:
      address.sub ||
      address.additional_info ||
      address.reference ||
      address.phone ||
      "",
    name: address.name || address.street || "",
    house_num: address.house_num || "",
    additional_info: address.additional_info || address.notes || "",
    phone: address.phone || "",
    lat: address.lat ?? address.latitude ?? null,
    lng: address.lng ?? address.longitude ?? null
  };
};


//Helper to safely parse JSON, returning a fallback value if parsing fails or if the value is null/undefined
const safeParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const toCheckoutAmount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? roundUpToTwoDecimals(number) : 0;
};

const normalizeOfficialCalculation = (payload = {}) => {
  const data = payload?.data || payload;
  const calculation = data?.calculation || data;

  return {
    raw: calculation,
    calculation_id: calculation?.calculation_id || calculation?.id || data?.calculation_id || data?.id,
    sub_total_order: toCheckoutAmount(calculation?.sub_total_order ?? calculation?.subtotal ?? data?.sub_total_order),
    total_tax_order: toCheckoutAmount(calculation?.total_tax_order ?? calculation?.itbms ?? data?.total_tax_order),
    total_discount_order: toCheckoutAmount(calculation?.total_discount_order ?? calculation?.discount ?? data?.total_discount_order),
    delivery_total: toCheckoutAmount(calculation?.delivery_total ?? calculation?.delivery?.value ?? data?.delivery_total),
    delivery_tax: toCheckoutAmount(calculation?.delivery_tax ?? calculation?.delivery?.tax ?? data?.delivery_tax),
    tip: toCheckoutAmount(calculation?.tip ?? calculation?.tips ?? data?.tip),
    total_order: toCheckoutAmount(calculation?.total_order ?? calculation?.total ?? data?.total_order),
    expires_at: calculation?.expires_at || data?.expires_at || null,
  };
};

const buildCheckoutTotalsFromCalculation = (calculation = {}) => ({
  subtotal: calculation.sub_total_order,
  itbms: calculation.total_tax_order,
  discount: calculation.total_discount_order,
  delivery: calculation.delivery_total,
  deliveryTax: calculation.delivery_tax,
  tip: calculation.tip,
  total: calculation.total_order,
});

const isCheckoutCalculationExpired = (calculation = {}) => {
  if (!calculation?.expires_at) return false;
  const expiresAt = new Date(calculation.expires_at).getTime();
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
};

const isCalculationCartMismatchPayload = (payload = {}) => {
  const text = String(payload?.message || payload?.error || "").toLowerCase();

  return (
    String(payload?.code || "").toUpperCase() === "CALCULATION_CART_MISMATCH" ||
    (text.includes("carrito") && text.includes("cambio") && text.includes("calcular"))
  );
};

const createCalculationCartMismatchError = (payload = {}, status) => {
  const error = new Error(payload?.message || payload?.error || "El carrito cambio, vuelve a calcular los totales");
  error.response = {
    status: status || 409,
    data: payload
  };
  error.isCalculationCartMismatch = true;
  return error;
};

const isCalculationCartMismatchError = (error) => (
  Boolean(error?.isCalculationCartMismatch) ||
  (
    Number(error?.response?.status) === 409 &&
    isCalculationCartMismatchPayload(error?.response?.data)
  ) ||
  isCalculationCartMismatchPayload(error?.response?.data)
);

const applyOfficialCalculationToOrderPayload = (payload, calculation) => {
  if (!payload || !calculation) return payload;

  payload.calculation_id = calculation.calculation_id;
  payload.Descuento = calculation.total_discount_order;
  payload.Total = calculation.total_order;
  payload.itbms = calculation.total_tax_order;
  payload.tips = calculation.tip;
  payload.subtotal = calculation.sub_total_order;

  if (payload.delivery) {
    payload.delivery = {
      ...payload.delivery,
      value: calculation.delivery_total,
      tax: calculation.delivery_tax,
    };
  }

  return payload;
};

const getOfficialTotalDifference = (preview = {}, official = {}) => {
  const fields = [
    ["Subtotal", preview.subtotal, official.sub_total_order],
    ["Impuestos", preview.itbms, official.total_tax_order],
    ["Descuento", preview.discount, official.total_discount_order],
    ["Delivery", preview.delivery, official.delivery_total],
    ["Impuesto delivery", preview.deliveryTax, official.delivery_tax],
    ["Propina", preview.tip, official.tip],
    ["Total", preview.total, official.total_order],
  ];

  return fields
    .map(([label, localValue, officialValue]) => ({
      label,
      localValue: toCheckoutAmount(localValue),
      officialValue: toCheckoutAmount(officialValue),
    }))
    .filter(item => Math.abs(item.localValue - item.officialValue) >= 0.01);
};

const getCheckoutBusiness = () => {
  const storedBusiness = safeParse(localStorage.getItem("business"), null);
  if (storedBusiness?.id) return storedBusiness;

  const storedGeneralData = safeParse(localStorage.getItem("generalData"), []);
  return storedGeneralData?.[0]?.data?.business || null;
};

const isInvalidAuthenticationError = (error) => {
  const message = String(
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    ""
  ).toUpperCase();

  return message.includes("INVALID AUTHENTICATION");
};

const ensureCheckoutToken = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    try {
      const response = await AuthFetchAPI.validToken();
      if (response?.data === true) return undefined;
    } catch (error) {
      // Generate a fresh public token below.
    }
  }

  let business = getCheckoutBusiness();

  if (!business?.id) {
    await validDomain();
    business = getCheckoutBusiness();
  }

  if (!business?.id) {
    throw new Error("No se encontro el negocio para renovar la autenticacion del checkout.");
  }

  const response = await AuthFetchAPI.generatePublicToken(business.id);

  if (response?.data?.success === false) {
    throw new Error("No se pudo generar un token publico para el checkout.");
  }

  return undefined;
};

const buildDirectOrderBusiness = (business = {}, businessResponse = {}) => ({
  id: businessResponse?.id || business?.id,
  name: businessResponse?.name || business?.name || "",
  description: businessResponse?.description || "",
  code: businessResponse?.code || business?.code || "",
  config: null,
  events_enabled: businessResponse?.events_enabled || 0
});

const buildDirectOrderBranch = (branch = {}, branchResponse = {}) => ({
  id: branchResponse?.id || branch?.id,
  name: branchResponse?.name || branch?.name || "",
  code: branchResponse?.code || branch?.code || "",
  order_printed: branchResponse?.printed_order,
  branchoffice_printer: branchResponse?.printer_name,
  branchoffice_support_phone: branchResponse?.supportPhone || "",
  branchoffice_open: branchResponse?.branchoffice_open,
  machine_ecommerce: branchResponse?.machine_ecommerce || [],
  machine_table: branchResponse?.machine_table || []
});

const ecommerceMachineConfig = {
  mainContainer: true,
  deliveryGroup: { actvPlus: true },
  buttons: {
    groupPlus: true,
    groupLite: false,
    menu: { groupPlus: true, topNav: false, actvLite: false },
    discounts: { actvPlus: true, actvLite: false },
    coupons: { actvPlus: true, actvLite: false },
    pendingOrders: { actvPlus: true, actvLite: false },
    return: { actvPlus: true, actvLite: false }
  }
};

const buildDirectOrderMachine = (machine = {}) => ({
  id: machine?.id || machine?.machine_per_branch_id,
  code: machine?.code || "",
  name: machine?.name || machine?.machine_name || "",
  is_logged: machine?.is_logged ?? 0,
  name_type_machine: machine?.name_type_machine || machine?.name || "Smartpay Ecommerce",
  machine_type_id: machine?.machine_type_id || machine?.machine_id || 10,
  printers: machine?.printers || [],
  machine_config: machine?.machine_config && Object.keys(machine.machine_config).length > 0
    ? machine.machine_config
    : ecommerceMachineConfig,
  machine_open: machine?.machine_open
});

const getMachinesFromResponse = (response) => {
  const candidates = [
    response?.data?.machines,
    response?.data?.data?.machines,
    response?.data?.data,
    response?.data
  ];

  return candidates.find(Array.isArray) || [];
};

const isEnabledConfig = (value) => (
  value === true ||
  value === 1 ||
  String(value).trim().toLowerCase() === "true" ||
  String(value).trim() === "1"
);



const buildCartItemsFromStorage = () => {
  const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
  const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

  const normalizeItem = (item, index, isCombo = false) => {
    const qty = Number(item.amount || item.qty || 1);
    const unitPrice = Number(
      item.price_total ??
      item.total ??
      item.price ??
      item.group_price ??
      0
    );

    return {
      id: `${isCombo ? "combo" : "product"}-${item.group_id || item.combo_id || item.id || index}-${item.segments_string || "plain"}-${index}`,
      sourceId: item.group_id || item.combo_id || item.id || null,
      type: isCombo ? "combo" : "product",
      name: item.name || item.group_name || item.combo_name || "Producto",
      desc: item.note || item.description || "",
      qty,
      price: unitPrice,
      img: item.img || item.image || item.url_image || "",
      raw: item
    };
  };

  return [
    ...products.map((item, index) => normalizeItem(item, index, false)),
    ...combos.map((item, index) => normalizeItem(item, index, true))

  ];
};

const createTableOrderIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  const hex = "0123456789abcdef";
  let key = "";
  for (let i = 0; i < 64; i += 1) {
    key += hex[Math.floor(Math.random() * hex.length)];
  }
  return key;
};

const getCartBranchMarker = (item = {}) => (
  item.branchoffice_id ||
  item.branch_id ||
  item.branchofficeId ||
  item.branchoffice_code ||
  item.branch_code ||
  item.branchofficeCode ||
  null
);

const hasDifferentBranchCartItems = ({ products = [], combos = [], branch }) => {
  const currentMarkers = new Set([
    branch?.id,
    branch?.code,
    branch?.branchoffice_id,
    branch?.branchoffice_code
  ].filter(Boolean).map(String));

  if (!currentMarkers.size) return false;

  return [...products, ...combos].some((item) => {
    const marker = getCartBranchMarker(item);
    return marker && !currentMarkers.has(String(marker));
  });
};

const sanitizeTableOrderLine = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeTableOrderLine);
  if (!value || typeof value !== "object") return value;

  const blockedKeys = new Set([
    "total",
    "subtotal",
    "itbms",
    "descuento",
    "Total",
    "Descuento"
  ]);

  const sanitized = Object.entries(value).reduce((acc, [key, entryValue]) => {
    if (!blockedKeys.has(key)) {
      acc[key] = sanitizeTableOrderLine(entryValue);
    }
    return acc;
  }, {});

  if (sanitized.price === undefined || sanitized.price === null || sanitized.price === "") {
    sanitized.price = value.price_total ?? value.group_price ?? value.base_price ?? value.total ?? 0;
  }

  if (sanitized.amount === undefined || sanitized.amount === null || sanitized.amount === "") {
    sanitized.amount = value.qty ?? value.cantidadItem ?? 1;
  }

  return sanitized;
};

const buildTableOrderCartPayload = () => {
  const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
  const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

  return {
    Orden: products.map(sanitizeTableOrderLine),
    combos: combos.map(sanitizeTableOrderLine),
    tipoOrden: "comer aqui",
    discounts: [],
    paymethods: [],
  };
};

// This list is exclusively "already sent to the kitchen" - anything still pending (DRAFT or
// SUBMITTED-but-not-cooking-yet) belongs only in the live "Pedido compartido" list above, never in
// both at once (see kitchen_status on tableSessionOrderSnapshot.js#sanitizeLine).
const normalizeAcceptedTableItems = (order) => {
  const normalize = (item, index, type) => ({
    id: `${type}-${item.id || item.group_id || item.combo_id || item.code || index}`,
    name: item.name || item.group_name || item.combo_name || "Producto",
    qty: Number(item.amount || item.qty || item.cantidadItem || 1),
    note: item.note || item.notes || "",
  });

  const isSentToKitchen = (item) => item.kitchen_status === "COOKING";

  return [
    ...(order?.groups || []).filter(isSentToKitchen).map((item, index) => normalize(item, index, "product")),
    ...(order?.combos || []).filter(isSentToKitchen).map((item, index) => normalize(item, index, "combo")),
  ];
};

// The collaborative API returns server-owned lines. Keep this separate from the
// local-cart normalizer above: a shared line is identified by line_uuid and may
// only be changed when the server marked it editable for this participant.
const normalizeSharedTableLines = (order) => [
  ...(order?.groups || []).map((item, index) => ({ ...item, type: "group", key: item.line_uuid || `group-${index}` })),
  ...(order?.combos || []).map((item, index) => ({ ...item, type: "combo", key: item.line_uuid || `combo-${index}` })),
];

const sharedLineName = (line) => line.name || line.group_name || line.combo_name || "Producto";
const sharedLineQuantity = (line) => Number(line.amount ?? line.qty ?? line.cantidadItem ?? 1);

const TableRestaurantPaymentSummary = ({
  tableLabel,
  branchName,
  areaName,
  sessionStatus,
  pendingCount,
  acceptedCount,
  orderStatus,
  paymentAuthorized,
  paymentRequested,
  paymentMode,
  hasOrderLines,
  onPay,
  onRequestPayment,
}) => {
  const { t } = useTranslation();
  // Pre-pay has no waiter to authorize payment - payment_authorized only flips true once the
  // customer taps this very button (see TableSessionPayment.jsx's dispatch effect calling
  // beginPrepayPayment), so gating the button on it here would make it permanently unclickable.
  // Gate on "there's something to pay for" instead.
  const isPrepay = paymentMode === "PRE_PAY";
  const canPay = isPrepay ? Boolean(hasOrderLines) : paymentAuthorized;
  return (
    <div className="ck-summary ck-table-summary">
      <p className="ck-summary-title">{t('checkout.tableSummaryTitle')}</p>
      <div className="alert alert-success py-2 mb-3">
        {t('checkout.tableActiveSession', { table: tableLabel })}
      </div>
      {branchName ? (
        <div className="ck-sum-row">
          <span>{t('nav.branch')}</span>
          <span className="ck-sum-row-val">{branchName}</span>
        </div>
      ) : null}
      {areaName ? (
        <div className="ck-sum-row">
          <span>{t('checkout.environment')}</span>
          <span className="ck-sum-row-val">{areaName}</span>
        </div>
      ) : null}
      {sessionStatus ? (
        <div className="ck-sum-row">
          <span>{t('checkout.session')}</span>
          <span className="ck-sum-row-val">{sessionStatus}</span>
        </div>
      ) : null}
      <div className="ck-sum-row">
        <span>{t('checkout.pendingToSend')}</span>
        <span className="ck-sum-row-val">{pendingCount}</span>
      </div>
      <div className="ck-sum-row">
        <span>{t('checkout.acceptedByRestaurant')}</span>
        <span className="ck-sum-row-val">{acceptedCount}</span>
      </div>
      {orderStatus ? (
        <div className="ck-sum-row">
          <span>{t('checkout.status')}</span>
          <span className="ck-sum-row-val">{orderStatus}</span>
        </div>
      ) : null}
      <div className="ck-sum-divider" />
      {/* <div className="ck-sum-row total">
        <span>{t('checkout.payment')}</span>
        <span>{paymentAuthorized ? t('checkout.payNowAvailable') : t('checkout.payAtRestaurant')}</span>
      </div> */}
      {/* Waiter-controlled payment gate: stays disabled until the restaurant explicitly authorizes
          it from the POS (Mesas/Precuentas) - see TableSessionContext's payment_authorized. Disabled
          state deliberately keeps looking exactly like the pre-existing "En el restaurante" copy
          above, so nothing changes visually for tables that never get authorized. */}
      <button
        type="button"
        className="btn btn-dark w-100 mt-2"
        disabled={!canPay}
        onClick={onPay}
      >
        {t('checkout.payButtonLabel')}
      </button>
      {/* Customer-initiated "solicitar pagar" - post-pay only. Pre-pay has no waiter to ask, the
          Pagar button above is always the only action. */}
      {!isPrepay && !paymentAuthorized && (
        paymentRequested ? (
          <p className="text-muted small mt-2 mb-0 text-center">{t('checkout.paymentRequestedWaiting')}</p>
        ) : (
          <button
            type="button"
            className="btn btn-outline-dark w-100 mt-2"
            onClick={onRequestPayment}
          >
            {t('checkout.requestPaymentButton')}
          </button>
        )
      )}
    </div>
  );
};

const reloadCartFromStorage = (setCartItems) => {
  setCartItems(buildCartItemsFromStorage());
};

function toISODateLocal(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeRuleDate(dateValue) {
  if (!dateValue) return null;

  if (typeof dateValue === "string") {
    const match = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return null;

  return toISODateLocal(d);
}

function parseLocalISODate(iso) {
  const normalized = normalizeRuleDate(iso);
  if (!normalized) return null;

  const [y, m, d] = normalized.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function stripTimeLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function sortByPriorityDesc(a, b) {
  const pa = Number.isFinite(+a.priority) ? +a.priority : -1;
  const pb = Number.isFinite(+b.priority) ? +b.priority : -1;
  return pb - pa;
}

function pickFirstByPriority(list = []) {
  const arr = Array.isArray(list) ? [...list] : [];
  arr.sort(sortByPriorityDesc);
  return arr[0] || null;
}

function isDateInsidePickRule(dateStr, cfg) {
  if (!dateStr || !cfg) return false;

  if (cfg.limit_mode === "date_range") {
    const start = normalizeRuleDate(cfg.start_date);
    const end = normalizeRuleDate(cfg.end_date);

    if (!start || !end) return false;
    return dateStr >= start && dateStr <= end;
  }

  const today = stripTimeLocal(new Date());
  const max = new Date(today);
  max.setDate(max.getDate() + Number(cfg.days_ahead || 7));

  const target = parseLocalISODate(dateStr);
  if (!target) return false;

  return stripTimeLocal(target) >= today && stripTimeLocal(target) <= stripTimeLocal(max);
}

function getPickCalendarBounds(cfg) {
  const today = stripTimeLocal(new Date());

  if (!cfg) {
    return { minDate: today, maxDate: today };
  }

  if (cfg.limit_mode === "date_range" && cfg.start_date && cfg.end_date) {
    const start = parseLocalISODate(cfg.start_date);
    const end = parseLocalISODate(cfg.end_date);

    if (!start || !end) {
      return { minDate: today, maxDate: today };
    }

    const effectiveMin = start > today ? start : today;

    return {
      minDate: effectiveMin,
      maxDate: end >= effectiveMin ? end : effectiveMin
    };
  }

  const max = new Date(today);
  max.setDate(max.getDate() + Number(cfg.days_ahead || 7));

  return {
    minDate: today,
    maxDate: max
  };
}

function generateSlots(dateStr, cfg) {
  if (!dateStr || !cfg) return [];

  const [sh, sm] = (cfg.window_start || "13:00").split(":").map(Number);
  const [eh, em] = (cfg.window_end || "20:00").split(":").map(Number);
  const step = Number(cfg.slot_minutes || 30);

  const now = new Date();
  const leadMs = Number(cfg.lead_time_minutes || 0) * 60 * 1000;

  let cursor = new Date(`${dateStr}T${String(sh).padStart(2, "0")}:${String(sm).padStart(2, "0")}:00`);
  const end = new Date(`${dateStr}T${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}:00`);

  const out = [];
  while (cursor < end) {
    const next = new Date(cursor.getTime() + step * 60 * 1000);
    const usable =
      new Date(cursor).getTime() - now.getTime() >= leadMs ||
      now.toDateString() !== new Date(cursor).toDateString();

    out.push({
      from: cursor.toTimeString().slice(0, 5),
      to: next.toTimeString().slice(0, 5),
      disabled: !usable
    });

    cursor = next;
  }

  return out;
}

function getDefaultPickDateAndSlot(cfg) {
  if (!cfg) return null;

  const now = new Date();
  const todayISO = toISODateLocal(now);

  let startDate = null;
  let endDate = null;

  if (cfg.limit_mode === "date_range" && cfg.start_date && cfg.end_date) {
    startDate = parseLocalISODate(cfg.start_date);
    endDate = parseLocalISODate(cfg.end_date);

    const today = stripTimeLocal(now);
    if (startDate && startDate < today) startDate = today;
  } else {
    startDate = stripTimeLocal(now);
    endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + Number(cfg.days_ahead || 7));
  }

  if (!startDate || !endDate || startDate > endDate) return null;

  for (let cursor = new Date(startDate); cursor <= endDate; cursor.setDate(cursor.getDate() + 1)) {
    const dateStr = toISODateLocal(cursor);

    if (!isDateInsidePickRule(dateStr, cfg)) continue;

    const slots = generateSlots(dateStr, cfg);
    const enabledSlots = slots.filter(s => !s.disabled);

    if (!enabledSlots.length) continue;

    if (dateStr === todayISO) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      let best = enabledSlots[0];
      let bestDiff = Infinity;

      enabledSlots.forEach(s => {
        const [h, m] = s.from.split(":").map(Number);
        const startMinutes = h * 60 + m;
        const diff = startMinutes - nowMinutes;

        if (diff >= 0 && diff < bestDiff) {
          best = s;
          bestDiff = diff;
        }
      });

      return {
        date: dateStr,
        slot: { from: best.from, to: best.to }
      };
    }

    return {
      date: dateStr,
      slot: { from: enabledSlots[0].from, to: enabledSlots[0].to }
    };
  }

  return null;
}

function getDefaultDeliveryDateAndSlot(rule) {
  return getDefaultPickDateAndSlot(rule);
}

function getFirstEnabledSlot(dateStr, rule) {
  const first = generateSlots(dateStr, rule).find(slot => !slot.disabled);
  return first ? { from: first.from, to: first.to } : null;
}

function getRuleBranchId(rule = {}) {
  const value =
    rule.branchoffice_id ??
    rule.branch_office_id ??
    rule.branch_id ??
    rule.branchoffice?.id ??
    rule.branch?.id ??
    null;

  return value === null || value === undefined ? null : Number(value);
}

function getStoredBranchId(branch = {}, machine = {}) {
  const value =
    branch?.id ??
    branch?.branchoffice_id ??
    branch?.branch_office_id ??
    branch?.branch_id ??
    branch?.branchoffice?.id ??
    machine?.branchoffice_id ??
    machine?.branch_office_id ??
    machine?.branch_id ??
    null;

  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function getRuleType(rule = {}) {
  return String(rule.type || rule.rule_type || "all").trim().toLowerCase();
}

function getRuleProductId(product = {}) {
  return Number(product.group_id ?? product.product_id ?? product.id ?? product.group?.id);
}

function filterApplicablePickNGoRules({ rules, branchofficeId, cartGroups }) {
  const active = (rules || []).filter(r => Number(r.is_active) === 1);
  if (!active.length) return [];

  const cartIds = new Set((cartGroups || []).map(g => Number(g.group_id)));
  const currentBranch =
    branchofficeId === null || branchofficeId === undefined
      ? null
      : Number(branchofficeId);

  const applicable = active.filter(r => {
    const ruleBranchId = getRuleBranchId(r);
    const ruleType = getRuleType(r);

    if (ruleBranchId !== null && ruleBranchId !== currentBranch) {
      return false;
    }

    if (ruleType === "product") {
      const appliesToCart = Array.isArray(r.products) && r.products.some(p => (
        cartIds.has(getRuleProductId(p))
      ));

      return appliesToCart;
    }

    if (ruleType === "all") {
      return true;
    }

    return false;
  });

  applicable.sort((a, b) => {
    const pa = Number.isFinite(+a.priority) ? +a.priority : -1;
    const pb = Number.isFinite(+b.priority) ? +b.priority : -1;
    if (pb !== pa) return pb - pa;

    const aSpecific = getRuleBranchId(a) !== null ? 1 : 0;
    const bSpecific = getRuleBranchId(b) !== null ? 1 : 0;
    if (bSpecific !== aSpecific) return bSpecific - aSpecific;

    const aProduct = getRuleType(a) === "product" ? 1 : 0;
    const bProduct = getRuleType(b) === "product" ? 1 : 0;
    if (bProduct !== aProduct) return bProduct - aProduct;

    return Number(a.id || 0) - Number(b.id || 0);
  });

  return applicable;
}

function choosePickNGoRule({ rules, branchofficeId, cartGroups, requireAvailableSlot = true }) {
  const applicableRules = filterApplicablePickNGoRules({
    rules,
    branchofficeId,
    cartGroups
  });

  const pickAvailable = (items) => items.find(rule => !!getDefaultPickDateAndSlot(rule));

  if (requireAvailableSlot) {
    return pickAvailable(applicableRules) || null;
  }

  return applicableRules[0] || null;
}


// Función para redondear hacia arriba si el tercer decimal es mayor a 0
function roundUpToTwoDecimals(value) {
  console.log("Nuevo redondeo de tax");
  console.log(value);
  // Verificamos si hay más de dos decimales
  const decimals = value.toString().split('.')[1];

  if (decimals && decimals.length > 2) {
    // Si el tercer decimal es mayor que 0, redondeamos hacia arriba
    if (parseInt(decimals[2]) > 0) {
      return Math.ceil(value * 100) / 100;
    }
  }

  console.log(parseFloat(value.toFixed(2)));

  // Si ya tiene dos decimales o menos, devolvemos el valor tal cual
  return parseFloat(value.toFixed(2));
}


/* ════════════════════════════════════════════
   ICONS
════════════════════════════════════════════ */
const IcoArrow = ({ dir = 'right' }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: dir === 'left' ? 'rotate(180deg)' : 'none' }}>
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IcoPlus = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IcoMinus = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IcoLock = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;

/* ════════════════════════════════════════════
   SHARED COMPONENTS
════════════════════════════════════════════ */

/* ── Date+Time Combined Step: CHeckout STay ── */
function StepDateTime({
  mode,
  selectedDate,
  selectedSlot,
  onDateChange,
  onSlotChange,
  minDate,
  maxDate,
  isDateDisabled,
  rule,
  hasActiveEventRules = false
}) {
  const { t } = useTranslation();
  const slots = selectedDate ? generateSlots(selectedDate, rule) : [];

  return (
    <div>
      <p className="ck-section-label">{t('checkout.selectDay')}</p>

      <WeekCalendar
        value={selectedDate || null}
        onChange={onDateChange}
        minDate={minDate}
        maxDate={maxDate}
        isDateDisabled={isDateDisabled}
      />

      {selectedDate && (
        <>
          <div className="ck-divider" />
          <p className="ck-section-label" style={{ marginBottom: ".75rem" }}>
            {mode === "pick"
              ? hasActiveEventRules
                ? t('checkout.slotsForEvent')
                : t('checkout.slotsForPickup')
              : t('checkout.slotsForDelivery')}
          </p>

          <TimePicker
            slots={slots}
            selectedSlot={selectedSlot}
            onSelect={onSlotChange}
          />
        </>
      )}
    </div>
  );
}

/* ── Shipping Methods : CHeckout STay ── */
function StepShippingMethods({
  rules = [],
  selectedRuleId,
  onSelect,
  quotes = {},
  loading = false
}) {
  const { t } = useTranslation();
  if (!rules.length) {
    return <div className="ck-empty">{t('checkout.noShippingMethods')}</div>;
  }

  return (
    <div className="ck-radio-grid">
      {rules.map(rule => {
        const quote = quotes[String(rule.id)];
        const badgeText = loading
          ? t('checkout.quoting')
          : quote?.waived
            ? t('checkout.free')
            : `$${Number(quote?.fee ?? 0).toFixed(2)}`;

        return (
          <RadioCard
            key={rule.id}
            id={String(rule.id)}
            selected={String(selectedRuleId) === String(rule.id)}
            onSelect={() => onSelect(rule)}
            icon={rule.allow_scheduled ? "📅" : "🛵"}
            title={rule.name || t('checkout.ruleNumber', { id: rule.id })}
            sub={
              quote?.error
                ? t('checkout.cannotQuoteOption')
                : rule.allow_scheduled
                  ? t('checkout.scheduledDelivery')
                  : t('checkout.immediateDelivery')
            }
            badge={badgeText}
            badgePaid
          />
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN CHECKOUT COMPONENT ¨¨¨¨¨¨
════════════════════════════════════════════ */
export const Checkout = ({ onContextReady }) => {
  const { t } = useTranslation();
  /* Cart Params -*/
  const { quote, code, PreOrder_id, keyproduct } = useParams();
  const {
    session: tableSession,
    status: tableSessionStatus,
    tableOrder,
    tableOrderLoading,
    submitTableOrder,
    participant,
    participants,
    currentInvite,
    inviteLoading,
    inviteError,
    sharedOrder,
    socketConnected,
    lastMutationError,
    createSessionInvite,
    revokeSessionInvite,
    addSharedItem,
    updateSharedItem,
    removeSharedItem,
    requestItemDiscount,
    lastDiscountResolvedEvent,
    closedSessionNotice,
    dismissClosedSessionNotice,
    refreshActiveTableOrder,
    requestPayment,
    finalizeAccount,
    setInvoiceData: saveTableInvoiceData,
  } = useTableSession();
  const hasActiveTableSession = isActiveTableSessionStatus(tableSessionStatus);
  // Pre-pago partial payment (Fase 4): reused everywhere below instead of re-deriving
  // tableSession?.payment_mode === "PRE_PAY" inline like the pre-existing spots in this file do.
  const isPrepayTable = tableSession?.payment_mode === "PRE_PAY";

  // DonLee "Paisanitos" (business_id 13 / business_code 9912 in prod and dev) - lets the customer
  // link their loyalty phone from the table cart itself, same idea as the POS's own ModalDonlee chip
  // in ModalPayMet.jsx (gated there on the identical business.id == 13). Only wired into the
  // whole-account checkout (TableSessionPayment's clientPhone prop below) - controller.newOrder
  // already does the DonLee/Tapmango lookup+points attach automatically once client_phone is present,
  // exactly as it does for a POS sale, so nothing else needs to change for the points to apply.
  const checkoutBusiness = getCheckoutBusiness();
  const isDonleeBusiness = String(checkoutBusiness?.id) === "13" || String(checkoutBusiness?.code) === "9912";
  const [showDonleeModal, setShowDonleeModal] = useState(false);
  const [donleePhoneDraft, setDonleePhoneDraft] = useState("");
  const [donleePhone, setDonleePhone] = useState("");
  const [donleeClientInfo, setDonleeClientInfo] = useState(null);
  const [donleeLoading, setDonleeLoading] = useState(false);

  const validateDonleePhone = async () => {
    const phone = donleePhoneDraft.trim();
    if (!phone) {
      ErrorAlert("", "Ingresa tu numero de Paisanitos", "warning");
      return;
    }
    setDonleeLoading(true);
    try {
      await ensureCheckoutToken();
      const response = await DonleeFechtAPI.getClientPhone(phone, undefined);
      const clients = response?.data?.client || [];
      if (clients.length === 0) {
        setDonleeClientInfo(false);
        ErrorAlert("", "No encontramos un cliente Paisanitos con ese numero", "warning");
      } else {
        setDonleeClientInfo(clients[0]);
        setDonleePhone(phone);
        ErrorAlert("", "Numero de Paisanitos vinculado a tu cuenta", "success");
        // Persist right away (not just at final checkout) so it shows up in the POS's own order
        // detail while the account is still open, and survives even if staff ends up closing the
        // account from the POS instead of the customer paying online - see setSessionClientPhone
        // on the backend. Best-effort: a failure here doesn't block the customer from continuing,
        // checkoutTableSession still forwards clientPhone as a fallback if this never landed.
        TableSessionsFetchAPI.setClientPhone(phone).catch(() => { });
      }
    } catch (err) {
      ErrorAlert("", "Ocurrio un error validando el numero", "error");
    } finally {
      setDonleeLoading(false);
    }
  };

  const clearDonleePhone = () => {
    setDonleePhone("");
    setDonleeClientInfo(null);
    setDonleePhoneDraft("");
    TableSessionsFetchAPI.setClientPhone("").catch(() => { });
  };

  // Lets the customer un-stick a claimed line themselves (e.g. after reloading the page mid "Pagar
  // con POS" wait, or if the waiter never got to the terminal) instead of it staying "Cobrando"
  // forever until the backend's own claim TTL sweeps it - see releasePrepayClaim below and
  // payment_pending_id on tableSessionOrderSnapshot.js#sanitizeLine.
  const [cancelingPendingLineId, setCancelingPendingLineId] = useState(null);
  const handleCancelPendingLinePayment = async (line) => {
    if (!line?.payment_pending_id || cancelingPendingLineId) return;
    setCancelingPendingLineId(line.payment_pending_id);
    try {
      await TableSessionsFetchAPI.releasePrepayClaim(line.payment_pending_id, "Cancelado por el cliente");
      AlertSmall("Cobro cancelado. Puedes intentar de nuevo.", "info");
    } catch (err) {
      ErrorAlert("", "No se pudo cancelar el cobro. Intenta de nuevo en unos segundos.", "error");
    } finally {
      setCancelingPendingLineId(null);
    }
  };
  // Waiter-controlled payment gate, closing side: once the waiter authorizes payment (POS
  // Mesas/Precuentas), the account is closing out - the backend already refuses add/edit/delete/
  // submit at this point (assertCollaborativeAccess -> PAYMENT_AUTHORIZED_LOCKED), this just keeps
  // the UI in sync so the customer sees why those controls disappeared instead of hitting an error.
  const tableOrderLocked = hasActiveTableSession && Boolean(tableSession?.payment_authorized);
  /* ── Cart state ── */
  const [cartItems, setCartItems] = useState([]);
  const [isSubmittingTableOrder, setIsSubmittingTableOrder] = useState(false);
  const [tableOrderNotice, setTableOrderNotice] = useState("");
  const [tableOrderWarning, setTableOrderWarning] = useState("");
  // "Pedir descuento" sobre una linea de la mesa (ej. jubilado): linea elegida, motivo y envio en curso.
  const [discountRequestLine, setDiscountRequestLine] = useState(null);
  const [discountReason, setDiscountReason] = useState("");
  const [isSubmittingDiscount, setIsSubmittingDiscount] = useState(false);
  // "Personalizar factura" de la cuenta de la mesa (nombre/RUC/DV con los que se emite la factura final).
  const [showTableInvoice, setShowTableInvoice] = useState(false);
  const [tableOrderConfirmation, setTableOrderConfirmation] = useState(null);
  const [showTableInvite, setShowTableInvite] = useState(false);
  const [showTablePaymentMethods, setShowTablePaymentMethods] = useState(false);
  const [selectedTablePayment, setSelectedTablePayment] = useState(null);
  const [showTableGatewayPayment, setShowTableGatewayPayment] = useState(false);
  // Pre-pago partial payment (Fase 4). selectedPaymentLineUuids is the customer's current checkbox
  // selection in "Pedido compartido"; paymentLineUuidsForModal is a snapshot of that taken the moment
  // "Pagar seleccionados" opens the payment-methods modal (null means "pay/close the whole account",
  // the pre-existing behavior via TableRestaurantPaymentSummary's onPay below).
  const [selectedPaymentLineUuids, setSelectedPaymentLineUuids] = useState([]);
  const [paymentLineUuidsForModal, setPaymentLineUuidsForModal] = useState(null);
  const [isFinalizingAccount, setIsFinalizingAccount] = useState(false);
  const [inviteClock, setInviteClock] = useState(Date.now());
  // Kept only until the legacy implementation below is removed in a dedicated
  // cleanup; the collaborative branch returns before it can run.
  const tableOrderInFlightRef = useRef(false);
  const tableOrderIdempotencyKeyRef = useRef(null);
  const checkedTableCartBranchRef = useRef(false);


  /* ── Flow state ── */
  const [currentStep, setCurrentStep] = useState(0);
  const currentStepRef = useRef(0);
  const [deliveryMethod, setDeliveryMethod] = useState('');    // 'pickup' | 'delivery'
  const [whenDelivery, setWhenDelivery] = useState('');    // 'now' | 'scheduled'
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceRuc, setInvoiceRuc] = useState('');
  const [invoiceDv, setInvoiceDv] = useState('');
  const [invoiceValidatedData, setInvoiceValidatedData] = useState(null);
  const [isValidatingInvoice, setIsValidatingInvoice] = useState(false);
  const [invoiceStepValidated, setInvoiceStepValidated] = useState(false);
  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [showAddressForm, setShowAddressForm] = useState(false);
  /* ── Payment state ── */
  const [payments, setPayments] = useState(() => {
    const generalData = safeParse(localStorage.getItem("generalData"), []);
    return generalData?.[0]?.data?.paymentMethods || [];
  });

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [modalUser, setModalUser] = useState(false);
  /* ── Data states ── */
  const [clientData, setClientData] = useState(() => {
    return safeParse(localStorage.getItem("client"), null);
  });

  const [addressNowLocation, setAddressNowLocation] = useState(() => {
    return safeParse(localStorage.getItem("addressNowLocation"), null);
  });

  const [selectedAddress, setSelectedAddress] = useState(() => {
    const savedAddress = safeParse(localStorage.getItem("addressNowLocation"), null);
    return savedAddress?.id || savedAddress?._id || savedAddress?.address_id || "";
  });

  const [addresses, setAddresses] = useState(() => {
    const currentAddress = safeParse(localStorage.getItem("addressNowLocation"), null);
    return currentAddress ? [currentAddress] : [];
  });

  const displayAddresses =
    addresses.length > 0
      ? addresses
      : addressNowLocation
        ? [normalizeAddressForCheckout(addressNowLocation)]
        : [];

  /* ── Contact info state ── */
  const [contact, setContact] = useState(() => {
    // Guests get the generic "Invitado" placeholder client — don't pre-fill the contact
    // step with it, the customer should type their own name/email/phone.
    if (clientData?.name === "Invitado") {
      return { email: "", name: "", phone: "" };
    }

    return {
      email: clientData?.email || "",
      name: clientData?.name || clientData?.client_name || "",
      phone: clientData?.phone || clientData?.client_phone || ""
    };
  });

  const [contactErrors, setContactErrors] = useState({});
  const [finished, setFinished] = useState(false);
  const [isDirectOrderLoading, setIsDirectOrderLoading] = useState(Boolean(PreOrder_id));
  const [cartModalOpen, setCartModalOpen] = useState(false);

  /* ── Rules and options state ── */
  const [option, setOption] = useState(""); // "pick" | "delivery"
  const [deliveryTiming, setDeliveryTiming] = useState("now");

  const [pickDate, setPickDate] = useState(null);
  const [pickSlot, setPickSlot] = useState(null);

  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState(null);

  const [selectedDeliveryRule, setSelectedDeliveryRule] = useState(null);
  const [selectedPickAndGoRule, setSelectedPickAndGoRule] = useState(null);
  const [usingEventPickRule, setUsingEventPickRule] = useState(false);

  const [pickAndGoRule, setPickAndGoRule] = useState(() => {
    return safeParse(localStorage.getItem("pickAndGoRule"), []) || [];
  });

  const [eventRules, setEventRules] = useState(() => {
    return safeParse(localStorage.getItem("eventRules"), []) || [];
  });

  const [standName, setStandName] = useState(() => {
    return localStorage.getItem("event_stand_name") || "";
  });

  const [standNumber, setStandNumber] = useState(() => {
    return localStorage.getItem("event_stand_number") || "";
  });

  const [requireStand, setRequireStand] = useState(false);
  const [standErrors, setStandErrors] = useState({});

  const [deliveryFeeRule, setDeliveryFeeRule] = useState(() => {
    const raw = safeParse(localStorage.getItem("deliveryFeeRule"), []) || [];
    return raw.filter(r => Number(r.allow_immediate) === 1);
  });

  const [deliveryFeeRuleSchedule, setDeliveryFeeRuleSchedule] = useState(() => {
    const raw = safeParse(localStorage.getItem("deliveryFeeRule"), []) || [];
    return raw.filter(r => Number(r.allow_scheduled) === 1);
  });
  /*-- UI state --*/
  const [deliveryButton, setDeliveryButton] = useState(false);
  const [pickupButton, setPickupButton] = useState(false);
  const [moduletypeOrder, setModuletypeOrder] = useState(true);

  //For payment processing states
  const navigate = useNavigate();

  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const paymentInFlightRef = useRef(false);

  const [showModalStatusPayment, setShowModalStatusPayment] = useState(false);
  const [paymentApproval, setPaymentApproval] = useState({
    status: 0,
    description: "Procesando pago..."
  });

  const [showYappyBtnModal, setShowYappyBtnModal] = useState(false);
  const [showTilopayModal, setShowTilopayModal] = useState(false);
  const tilopayCalculationRef = useRef(null);
  const [newTab, setNewTab] = useState(null);
  const [statePayment, setStatePayment] = useState(false);
  const [registerTurnOrder, setRegisterTurnOrder] = useState(0);
  const [orderNumber, setOrderNumber] = useState(() => {
    return localStorage.getItem("orderNumber") || "0";
  });
  const [powertranzData, setPowertranzData] = useState({ txId: "", spiToken: "" });
  const [orderIdPowertranz, setOrderIdPowertranz] = useState("");
  const [typePowertranz, setTypePowertranz] = useState("sale");
  const [tempPowertranz, setTempPowertranz] = useState(() => {
    return safeParse(localStorage.getItem("tempPowertranz"), null);
  });
  const [statusPowertranz, setStatusPowertranz] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [htmlData, sethtmlData] = useState("");//For powertranz iframe info
  // "Guardar esta tarjeta para futuras compras": solo aplica a PowerTranz con cliente logueado y la funcion activa
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);

  //Totals states 
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceTotalItbms, setInvoiceTotalItbms] = useState(0);
  const [invoiceSubtotal, setInvoiceSubtotal] = useState(0);
  const [invoiceTotalDiscount, setInvoiceTotalDiscount] = useState(0);
  const [invoiceDeliveryFee, setInvoiceDeliveryFee] = useState(0);
  const [invoiceTaxDeliveryFee, setInvoiceTaxDeliveryFee] = useState(0);
  const invoiceDeliveryFeeRef = useRef(0);
  const invoiceTaxDeliveryFeeRef = useRef(0);
  const [taxesArrayAll, setTaxesArrayAll] = useState([]);
  const [dataDicountApply, setdataDiscountApply] = useState([]);
  const [invoiceTip, setInvoiceTip] = useState(0);
  const [checkoutCalculation, setCheckoutCalculation] = useState(null);
  const checkoutCalculationRef = useRef(null);
  const calculateTotalsRequestRef = useRef(0);
  const calculateTotalsPromiseRef = useRef(null);
  const totalsDirtyRef = useRef(true);
  const isCalculatingTotalsRef = useRef(false);
  const [calculationSessionId, setCalculationSessionId] = useState(() => {
    const currentSessionId = sessionStorage.getItem(CHECKOUT_CALCULATION_SESSION_KEY);
    if (currentSessionId) return currentSessionId;

    const nextSessionId = createCheckoutCalculationSessionId();
    sessionStorage.setItem(CHECKOUT_CALCULATION_SESSION_KEY, nextSessionId);
    return nextSessionId;
  });
  const [calculationId, setCalculationId] = useState(null);
  const [calculationExpiresAt, setCalculationExpiresAt] = useState(null);
  const [checkoutTotals, setCheckoutTotals] = useState(null);
  const [isCalculatingTotals, setIsCalculatingTotals] = useState(false);
  const [totalsDirty, setTotalsDirty] = useState(true);
  const [shippingQuotes, setShippingQuotes] = useState({});
  const [shippingQuotesLoading, setShippingQuotesLoading] = useState(false);

  const [generalData, setGeneralData] = useState(() => {
    if (localStorage.getItem('generalData') != null && localStorage.getItem('generalData') != undefined && localStorage.getItem('generalData') != '') {
      return JSON.parse(localStorage.getItem('generalData'))
    } else {
      return []
    }
  });

  const [ecommerceContent, setEcommerceContent] = useState(() => {
    return safeParse(localStorage.getItem("ecommerceContent"), {}) || {};
  });



  /* ── Refs ── */
  const selectedDeliveryRuleRef = useRef(selectedDeliveryRule);
  const lastAutoRuleIdRef = useRef(null);
  const skipInvoiceResetRef = useRef(false);
  const directOrderContextRef = useRef("");
  /* ── Branch office context (for rules that depend on branch) ── */
  const branchofficeData = safeParse(localStorage.getItem("branchoffice"), null);
  const machineStorageData = safeParse(localStorage.getItem("machine"), null);
  const branchofficeId = getStoredBranchId(branchofficeData, machineStorageData);
  const tableLabel = tableSession?.table?.name || tableSession?.table?.number || "activa";
  const tableBranchName = tableSession?.branchoffice?.name || tableSession?.branchoffice?.code || "";
  const tableAreaName =
    tableSession?.area?.name ||
    tableSession?.environment?.name ||
    tableSession?.table?.area?.name ||
    tableSession?.table?.environment?.name ||
    "";
  const tableSessionDisplayStatus = tableSession?.status || tableSessionStatus || "";
  const collaborativeOrder = sharedOrder || tableOrder;

  // Re-hydrates the Paisanitos chip ("Paisanitos: <phone>" instead of resetting to "+ Paisanitos")
  // after a reload or a fresh order fetch - donleePhone otherwise only ever lived in this component's
  // own local state, which a reload always wipes even though the phone was already persisted
  // server-side the moment it was validated (see setSessionClientPhone on the backend). Only fires
  // while donleePhone is still empty, so it never fights a customer who already validated a different
  // number in this same session, and "Quitar" (which clears it both locally and server-side) doesn't
  // get silently re-filled on the next snapshot.
  useEffect(() => {
    if (!isDonleeBusiness || donleePhone) return;
    const serverPhone = collaborativeOrder?.client_phone || "";
    if (serverPhone) setDonleePhone(serverPhone);
  }, [isDonleeBusiness, donleePhone, collaborativeOrder?.client_phone]);

  const sharedTableLines = useMemo(
    // Una linea CANCELLED (ej. el mesero la elimino desde "Cuenta pre-pago" en vez de cobrarla - ver
    // cancelPendingPrepayLines en el backend) ya no cuenta para nada: el total de la mesa que llega en
    // collaborativeOrder.totals ya la excluye, pero normalizeSharedTableLines por si sola no filtraba
    // nada, asi que sin esto la linea se seguia viendo en "Tu pedido" (con su precio y checkbox de
    // siempre) aunque el total ya hubiera bajado a $0 - filtrar aqui, la unica fuente de la que salen
    // ownDraftLines/orderedSharedLines/claimablePendingLines/hasOrderLines mas abajo, lo corrige en
    // todos lados a la vez.
    () => normalizeSharedTableLines(collaborativeOrder).filter((line) => line.line_status !== "CANCELLED"),
    [collaborativeOrder]
  );
  const ownDraftLines = sharedTableLines.filter((line) => line.is_owner && line.line_status === "DRAFT" && line.line_uuid);
  // "Pedido compartido" shows every line in one list, still-pending ones first and whatever the
  // kitchen already has (COOKING - sent to kitchen, or paid for pre-pago) pushed to the end, instead
  // of two separate lists.
  const pendingSharedLines = sharedTableLines.filter((line) => line.kitchen_status !== "COOKING");
  const acceptedSharedLines = sharedTableLines.filter((line) => line.kitchen_status === "COOKING");
  const orderedSharedLines = [...pendingSharedLines, ...acceptedSharedLines];
  const acceptedTableItems = normalizeAcceptedTableItems(collaborativeOrder);
  // Pre-pago partial payment (Fase 4, PRE_PAY only): a line becomes selectable to pay once it has
  // left DRAFT (still the owner's own uncommitted cart - nobody else should be able to claim it yet)
  // and the backend still marks it claimable (UNPAID and not CANCELLED - see
  // tableSessionOrderSnapshot.js#sanitizeLine). "Cualquiera puede pagar cualquier linea" - this is
  // never limited to line.is_owner.
  const claimablePendingLines = isPrepayTable
    ? sharedTableLines.filter((line) => line.claimable && line.line_uuid && line.line_status !== "DRAFT")
    : [];
  const selectedPaymentSubtotal = sharedTableLines
    .filter((line) => selectedPaymentLineUuids.includes(line.line_uuid))
    .reduce((sum, line) => sum + (Number(line.total) || 0), 0);
  const pendingAccountBalance = Number(collaborativeOrder?.totals?.pending_balance ?? 0);
  const tableOrderStatus = collaborativeOrder?.status || collaborativeOrder?.order_status || "";
  const inviteExpiresIn = currentInvite?.expires_at
    ? Math.max(0, new Date(currentInvite.expires_at).getTime() - inviteClock)
    : null;
  const inviteCountdown = inviteExpiresIn === null
    ? ""
    : `${Math.floor(inviteExpiresIn / 60000)}:${String(Math.floor((inviteExpiresIn % 60000) / 1000)).padStart(2, "0")}`;

  /* ── Derived data ── */
  const cartGroupsForRules = cartItems
    .filter(item => item.type !== "combo")
    .map(item => ({
      group_id: item.raw?.group_id || item.sourceId
    }));

  /* ── Payment lock helper ── */
  const withPaymentLock = async (callback) => {
    if (paymentInFlightRef.current || isSubmittingPayment) {
      return;
    }

    paymentInFlightRef.current = true;
    setIsSubmittingPayment(true);

    try {
      await callback();
    } finally {
      // No liberamos el lock automáticamente al finalizar el proceso de pago porque en algunos casos (especialmente con pasarelas externas) el proceso de pago puede redirigir al usuario o abrir nuevas ventanas, lo que dificulta saber cuándo exactamente liberar el lock sin interferir con el flujo del pago. En su lugar, lo liberaremos manualmente en los casos de error o cancelación, y para los casos exitosos asumiremos que el proceso de pago se encargará de redirigir al usuario a una página de confirmación o similar, evitando que puedan volver a intentar pagar mientras el proceso está en curso.
      // Para pagos externos lo vamos a liberar manualmente en errores/cancelaciones.
    }
  };

  const releasePaymentLock = () => {
    paymentInFlightRef.current = false;
    setIsSubmittingPayment(false);
  };

  const setOfficialCheckoutCalculation = (calculation) => {
    checkoutCalculationRef.current = calculation;
    setCheckoutCalculation(calculation);
    setCalculationId(calculation?.calculation_id || null);
    setCalculationExpiresAt(calculation?.expires_at || null);
    setCheckoutTotals(calculation ? buildCheckoutTotalsFromCalculation(calculation) : null);
    totalsDirtyRef.current = false;
    setTotalsDirty(false);
  };

  const markCheckoutTotalsDirty = () => {
    calculateTotalsRequestRef.current += 1;
    totalsDirtyRef.current = true;
    setTotalsDirty(true);
  };

  const invalidateCheckoutCalculation = ({ dirty = true } = {}) => {
    checkoutCalculationRef.current = null;
    setCheckoutCalculation(null);
    setCalculationId(null);
    setCalculationExpiresAt(null);
    setCheckoutTotals(null);

    if (dirty) {
      markCheckoutTotalsDirty();
    }
  };

  const renewCheckoutCalculationSession = () => {
    const nextSessionId = createCheckoutCalculationSessionId();
    sessionStorage.setItem(CHECKOUT_CALCULATION_SESSION_KEY, nextSessionId);
    setCalculationSessionId(nextSessionId);
    invalidateCheckoutCalculation({ dirty: true });
  };

  const clearCheckoutPaymentStorage = ({ clearCart = true } = {}) => {
    if (clearCart) {
      localStorage.removeItem("shoppingCart");
      localStorage.removeItem("shoppingCartCombos");
      localStorage.removeItem("invoiceData");
      localStorage.removeItem("event_stand_name");
      localStorage.removeItem("event_stand_number");
    }

    localStorage.removeItem("orderNumber");
    localStorage.removeItem("yappy_transaction_id");
    localStorage.removeItem("YappyBtnOrderParams");
    localStorage.removeItem("YappyBtnPreOrder");
    localStorage.removeItem("evertechRequestId");
    renewCheckoutCalculationSession();
  };

  const clearDirectOrderContext = () => {
    localStorage.removeItem("PreOrder");
    localStorage.removeItem("PreOrderCode");
    directOrderContextRef.current = null;
    window.dispatchEvent(new Event(DIRECT_ORDER_CONTEXT_CLEARED_EVENT));
  };

  const resetExternalPaymentFlow = (description = "No se pudo procesar el pago") => {
    localStorage.removeItem("YappyBtnOrderParams");
    localStorage.removeItem("YappyBtnPreOrder");
    localStorage.removeItem("yappy_transaction_id");
    localStorage.removeItem("evertechRequestId");
    setStatePayment(false);
    renewCheckoutCalculationSession();
    setPaymentApproval({ status: 2, description });
    releasePaymentLock();
  };

  const isClientLoggedInForPayment = () => {
    const guestClient = localStorage.getItem("guestClient");
    const storedClient = safeParse(localStorage.getItem("client"), null);

    return (
      (guestClient === "0" || guestClient === 0) &&
      storedClient &&
      storedClient.name !== "Invitado"
    );
  };

  const openLoginToPay = () => {
    releasePaymentLock();
    setClientData(null);
    setModalUser(true);
    setLoginRequired(true);
  };

  const closedModalLoginAndRegister = () => {
    setClientData(safeParse(localStorage.getItem("client"), null));
    setModalUser(false);
    setLoginRequired(false);
  };

  const applyCartAvailabilityValidation = useCallback((sourceData = generalData, {
    notify = true,
    resetToCart = false
  } = {}) => {
    const result = sanitizeStoredCartAgainstMenu(sourceData);

    if (result.changed) {
      reloadCartFromStorage(setCartItems);

      if (resetToCart) {
        setCurrentStep(0);
      }

      const message = getCartAvailabilityMessage(result);
      if (notify && message) {
        AlertSmall(message, "info");
      }
    }

    return !result.changed;
  }, [generalData]);


  /* ── CSS inject ── */
  useEffect(() => {
    const id = 'ck-styles';
    if (!document.getElementById(id)) {
      const s = document.createElement('style');
      s.id = id; s.textContent = CSS;
      document.head.appendChild(s);
    }
    return () => { document.getElementById('ck-styles')?.remove(); };
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(CHECKOUT_CALCULATION_SESSION_KEY);
    };
  }, []);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  /* ── Use Effects ── */
  // On mount, determine which delivery options are enabled based on general data and set initial states accordingly
  useEffect(() => {
    const ecommerceUi =
      ecommerceContent?.ui_config ||
      generalData?.[0]?.data?.ecommerce_content?.ui_config;

    if (!ecommerceUi) return;

    const deliveryEnabled = isEnabledConfig(ecommerceUi?.delivery?.enabled);
    const pickupEnabled = isEnabledConfig(ecommerceUi?.pickup?.enabled);

    setDeliveryButton(deliveryEnabled);
    setPickupButton(pickupEnabled);
    setModuletypeOrder(true);

    if (!deliveryEnabled && !pickupEnabled) {
      setOption("");
      setDeliveryMethod("");
    } else if (!deliveryEnabled && option === "delivery") {
      setOption("");
      setDeliveryMethod("");
    } else if (!pickupEnabled && option === "pick") {
      setOption("");
      setDeliveryMethod("");
    }
  }, [ecommerceContent, generalData, option]);

  // Load direct-payment orders only after the public token has been validated or renewed.
  useEffect(() => {
    let cancelled = false;

    const loadPreOrderFromLink = async () => {
      try {
        if (!PreOrder_id) {
          setIsDirectOrderLoading(false);
          return;
        }

        setIsDirectOrderLoading(true);

        const requestPreOrder = async (forceRefresh = false) => {
          const token = await ensureCheckoutToken({ forceRefresh });
          return PreOrderFetchAPI.getPreOrderById(PreOrder_id, token);
        };

        let response;

        try {
          response = await requestPreOrder();
        } catch (error) {
          if (!isInvalidAuthenticationError(error)) throw error;
          response = await requestPreOrder(true);
        }

        const preorder = response?.data?.pre_order;
        if (!preorder) {
          throw new Error("La respuesta no contiene la orden temporal solicitada.");
        }

        if (directOrderContextRef.current !== preorder._id) {
          const businessResponse = await BusinessFetchAPI.getBusinessByCode(preorder.business.code);
          const businessData = buildDirectOrderBusiness(
            preorder.business,
            businessResponse?.data?.business
          );
          const businessConfig = businessResponse?.data?.businessConfig || {};

          localStorage.setItem("business", JSON.stringify(businessData));
          localStorage.setItem("businessConfig", JSON.stringify(businessConfig));
          localStorage.setItem("logo", businessConfig.logo || "");
          if (businessData.name) document.title = businessData.name;
          localStorage.setItem(
            "frecuentShopperConfig",
            businessResponse?.data?.frecuentShopperConfig
              ? JSON.stringify(businessResponse.data.frecuentShopperConfig)
              : "null"
          );

          const ownerTokenResponse = await AuthFetchAPI.generatePublicToken(businessData.id);
          if (ownerTokenResponse?.data?.success === false) {
            throw new Error("No se pudo autenticar el negocio de la orden.");
          }

          const branchResponse = await BranchOfficeFetchAPI.getBranchOfficeByCode(
            preorder.business.code,
            preorder.branchoffice.code
          );
          const branchData = buildDirectOrderBranch(
            preorder.branchoffice,
            branchResponse?.data?.branchoffice
          );

          let ecommerceMachines = Array.isArray(branchData.machine_ecommerce)
            ? branchData.machine_ecommerce
            : [];

          if (ecommerceMachines.length === 0) {
            const machinesResponse = await MachinesFetchAPI.getMachinesByBranchOfficeAndType(
              branchData.id,
              10
            );
            ecommerceMachines = getMachinesFromResponse(machinesResponse);
            branchData.machine_ecommerce = ecommerceMachines;
          }

          const ecommerceMachine = ecommerceMachines[0];
          if (!ecommerceMachine) {
            throw new Error("La sucursal de la orden no tiene una maquina ecommerce configurada.");
          }

          let machineInfo = ecommerceMachine;
          if (ecommerceMachine.code) {
            const machineResponse = await MachinesFetchAPI.getMachineByCode(
              ecommerceMachine.code,
              branchData.code
            );
            machineInfo = {
              ...ecommerceMachine,
              ...(machineResponse?.data?.machine || {})
            };
          } else {
            const machineId = ecommerceMachine.machine_per_branch_id || ecommerceMachine.id;
            if (machineId) {
              const machineResponse = await MachinesFetchAPI.getmachineByid(machineId);
              machineInfo = {
                ...ecommerceMachine,
                ...(machineResponse?.data?.data || {})
              };
            }
          }

          const machineData = buildDirectOrderMachine(machineInfo);
          if (!machineData.id || !machineData.code) {
            throw new Error("No se pudo obtener la maquina ecommerce de la sucursal.");
          }

          localStorage.setItem("branchoffice", JSON.stringify(branchData));
          localStorage.setItem("branchoffice_original", JSON.stringify(branchData));
          localStorage.setItem("machine", JSON.stringify(machineData));
          localStorage.setItem("parthner_order", "false");

          const ecommerceResponse = await EcommerceContentFetchAPI.getEcommerceContentByBusiness(
            businessData.id
          );
          const ownerEcommerceContent = ecommerceResponse?.data?.data || {};
          localStorage.setItem("ecommerceContent", JSON.stringify(ownerEcommerceContent));
          setEcommerceContent(ownerEcommerceContent);

          localStorage.removeItem("generalData");
          localStorage.removeItem("deliveryFeeRule");
          localStorage.removeItem("pickAndGoRule");
          localStorage.removeItem("eventRules");
          localStorage.removeItem("dataEvent");
          localStorage.removeItem("event_name");
          await initData();

          const refreshedGeneralData = safeParse(localStorage.getItem("generalData"), []) || [];
          const refreshedDeliveryRules = safeParse(localStorage.getItem("deliveryFeeRule"), []) || [];
          const refreshedPickRules = safeParse(localStorage.getItem("pickAndGoRule"), []) || [];
          const checkoutData = refreshedGeneralData.find(item => (
            Number(item?.branchoffice_id) === Number(branchData.id)
          ))?.data || refreshedGeneralData?.[0]?.data || {};

          const ecommercePaymentMethods = checkoutData.paymentMethods || [];
          setGeneralData(refreshedGeneralData);
          setPayments(ecommercePaymentMethods);
          setSelectedPayment(ecommercePaymentMethods[0] || null);
          setDeliveryFeeRule(
            refreshedDeliveryRules.filter(rule => Number(rule.allow_immediate) === 1)
          );
          setDeliveryFeeRuleSchedule(
            refreshedDeliveryRules.filter(rule => Number(rule.allow_scheduled) === 1)
          );
          setPickAndGoRule(refreshedPickRules);

          const configuration = checkoutData.configuration || {};
          if (configuration.containerColor) {
            document.documentElement.style.setProperty("--color-primary", configuration.containerColor);
          }
          if (configuration.layaoutColor) {
            document.documentElement.style.setProperty("--color-secondary", configuration.layaoutColor);
          }

          directOrderContextRef.current = preorder._id;
          onContextReady?.({ theme: localStorage.getItem("theme") || "light" });
        }

        if (quote !== true && quote !== "true") {
          localStorage.setItem("PreOrder", preorder._id || PreOrder_id);
          localStorage.setItem("PreOrderCode", preorder.code || code || "");
        }

        if (preorder.transaction_code) {
          localStorage.setItem("orderNumber", preorder.transaction_code);
          setOrderNumber(preorder.transaction_code);
        }

        // =============================
        // 1. SET CART (PRODUCTS)
        // =============================
        if (preorder.groups) {
          localStorage.setItem("shoppingCart", JSON.stringify(preorder.groups));
        }

        // =============================
        // 2. SET COMBOS
        // =============================
        if (preorder.combos) {
          localStorage.setItem("shoppingCartCombos", JSON.stringify(preorder.combos));
        }

        const directOrderGeneralData = safeParse(localStorage.getItem("generalData"), []) || [];
        const directOrderCartAvailability = sanitizeStoredCartAgainstMenu(directOrderGeneralData);
        if (directOrderCartAvailability.changed) {
          const message = getCartAvailabilityMessage(directOrderCartAvailability);
          if (message) AlertSmall(message, "info");
        }

        // =============================
        // 3. SET CLIENT (SI EXISTE)
        // =============================
        if (preorder.client) {
          localStorage.setItem("client", JSON.stringify(preorder.client));
          setClientData(preorder.client);

          setContact({
            email: preorder.client.email || "",
            name: preorder.client.name || preorder.client.client_name || "",
            phone: preorder.client.phone || preorder.client.client_phone || ""
          });
        }

        // =============================
        // 4. RECARGAR CARRITO
        // =============================
        reloadCartFromStorage(setCartItems);

        setOption("");
        setDeliveryMethod("");
        setDeliveryTiming("now");
        setDeliveryDate(null);
        setDeliverySlot(null);
        localStorage.removeItem("addressNowLocation");
        setAddressNowLocation(null);

      } catch (error) {
        console.error("Error loading preorder:", error);
        AlertSmall(
          isInvalidAuthenticationError(error)
            ? "No se pudo autenticar el enlace de pago. Intenta nuevamente."
            : "No se pudo cargar la orden del enlace de pago.",
          "error"
        );
      } finally {
        if (!cancelled) {
          setIsDirectOrderLoading(false);
        }
      }
    };

    loadPreOrderFromLink();

    return () => {
      cancelled = true;
    };
  }, [PreOrder_id, code, quote, onContextReady]);

  // Check if any active event rules require stand info on mount and whenever general data changes (which contains the event config)
  useEffect(() => {
    const eventConfigRequireStand =
      ecommerceContent?.ui_config?.events?.require_stand ??
      generalData?.[0]?.data?.ecommerce_content?.ui_config?.events?.require_stand ??
      false;

    setRequireStand(isEnabledConfig(eventConfigRequireStand));
  }, [ecommerceContent, generalData]);

  // Whenever delivery-related options change, re-quote delivery fees
  useEffect(() => {
    const runQuotes = async () => {
      if (option !== "delivery") {
        setShippingQuotes({});
        return;
      }

      if (!addressNowLocation?.lat || !addressNowLocation?.lng) {
        setShippingQuotes({});
        return;
      }

      const rulesToQuote =
        deliveryTiming === "scheduled"
          ? deliveryFeeRuleSchedule
          : deliveryFeeRule;

      if (!rulesToQuote.length) {
        setShippingQuotes({});
        return;
      }

      try {
        setShippingQuotesLoading(true);

        const quotesEntries = await Promise.all(
          rulesToQuote.map(async (rule) => {
            try {
              const quote = await quoteDeliveryRule(rule, invoiceSubtotal);
              return [String(rule.id), quote];
            } catch (error) {
              console.error("Error quoting rule", rule.id, error);
              return [String(rule.id), { fee: 0, tax: 0, total: 0, error: true }];
            }
          })
        );

        setShippingQuotes(Object.fromEntries(quotesEntries));
      } finally {
        setShippingQuotesLoading(false);
      }
    };

    runQuotes();
  }, [
    option,
    deliveryTiming,
    addressNowLocation?.lat,
    addressNowLocation?.lng,
    invoiceSubtotal,
    deliveryFeeRule,
    deliveryFeeRuleSchedule
  ]);


  // Sync payment methods from localStorage on mount and whenever they change in other tabs/windows
  useEffect(() => {
    const syncPayments = () => {
      const generalData = safeParse(localStorage.getItem("generalData"), []);
      const paymentMethods = generalData?.[0]?.data?.paymentMethods || [];
      setPayments(paymentMethods);
    };

    syncPayments();
    window.addEventListener("storage", syncPayments);

    return () => window.removeEventListener("storage", syncPayments);
  }, []);

  //Hook of the webhook of powertranz transactions
  useEffect(() => {
    if (hasActiveTableSession) return undefined;

    let branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    // Connection with the sockets server
    if (orderIdPowertranz !== "") {
      const socketPowetranz = Socket({ "branchoffice_id": branchoffice.id, "order_code": orderIdPowertranz })
      //we listen the powertranz payment
      socketPowetranz.on("payment_update", (data) => {
        console.log("data powertranz", data);

        if (data.status === 'APPROVED') {
          setStatusPowertranz("APPROVED")
          console.log("Pago aprobado");
        } else if (data.status === "PENDING") {
          powertranzMethodPayment(powertranzData.TransactionIdentifier, powertranzData.SpiToken)
          console.log("Pago pendiente de aprobación");

        } else {
          setStatusPowertranz("REJECTED")
          console.log("Pago no aprobado");
          cancelPowertranzPayment(data.status, data.message)

        }

      })

      return () => {
        socketPowetranz.disconnect();
      }
    }
  }, [hasActiveTableSession, orderIdPowertranz, powertranzData])

  //Hook of the webhook of evertech transactions place to play
  useEffect(() => {
    if (hasActiveTableSession) return undefined;

    let machine = safeParse(localStorage.getItem("machine"), null);
    let branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    //We verify the machine code
    if (machine !== undefined && machine !== null && branchoffice !== undefined && branchoffice !== null) {
      // Connection with the sockets server
      const socket = Socket({ "machine_code": machine.code, "branchoffice_code": branchoffice.code });

      //We listen the evertech payment
      socket.on(`evertechpay`, (data) => {
        let evertechRequestId = localStorage.getItem('evertechRequestId');
        //We check if the evertech request on this dispositive is the same that the socket notify is payed
        if (evertechRequestId && evertechRequestId == data?.requestId) {
          const status = getGatewayStatus(data);

          if (status === 'APPROVED') {
            //We get the payment reference
            let paymentInternalReference = Array.isArray(data?.payment) ? data.payment.map(element => {
              //We check wich payment was successfull to return the payment reference
              if (getGatewayStatus(element) === 'APPROVED') {
                return element.internalReference
              }
            }) : [];
            //console.log("data evertec", data)
            //console.log("paymentInternalReference", paymentInternalReference);

            //We check if the payment reference is defined
            if (paymentInternalReference[0] !== undefined && paymentInternalReference[0] != null && paymentInternalReference[0] != '') {
              //We set the payment internal reference on data object
              data.evertechPaymentReference = paymentInternalReference[0];
            }
            data.voucher = buildEvertecVoucher(data);

            //Set the payment status aprroval
            closeTab()
            setPaymentApproval({ status: 1, description: "Registrando ..." });

            setTimeout(async () => {
              await registerPendingApprovalOrder(data, selectedPayment, checkoutCalculationRef.current)
            }, 3000);
          } else if (status === "PENDING") {
            //console.log("Pago pendiente de aprobación");
            //console.log(data)

            //Set the payment status aprroval
            closeTab()
            setPaymentApproval({ status: 4, description: "Su pago está pendiente de aprobación." });
            //We redirect to the pending order page
            setTimeout(() => {
              clearCheckoutPaymentStorage();
              releasePaymentLock();
              if (data?.tempOrder?.code) {
                navigate(`/pending/order/${data.tempOrder.code}`);
              }
            }, 3000);

          } else {
            closeTab();
            //console.log("Pago no aprobado");
            const message = getGatewayStatusMessage(data);
            ErrorAlert('', message, 'error')
            resetExternalPaymentFlow(message);
            setSelectedPayment({ payment: "", payment_id: 0 })

            setTimeout(() => {
              setShowModalStatusPayment(false)
              setStatePayment(false)
            }, 3000);
          }
        } else {
          return;
        }
      });

      //We listen the yappy order
      socket.on("yappyorder", (data) => {
        let yappyTransactionId = localStorage.getItem('yappy_transaction_id');
        //We check if the transaction is the same
        if (yappyTransactionId && yappyTransactionId === data?.yappy_transaction_id) {
          setRegisterTurnOrder(1);

          if (data?.code) {
            ErrorAlert("Orden registrada con exito", "Orden generada", "success")
            clearDirectOrderContext();
            navigate(`/order/${data.code}`);
          }
          clearCheckoutPaymentStorage();
          releasePaymentLock();
        }

      })

      return () => {
        socket.disconnect();
      }
    }
  }, [hasActiveTableSession, selectedPayment, statePayment, paymentApproval])



  // Whenever payment options load or change, set the first available payment method as selected by default
  useEffect(() => {
    if (payments.length > 0 && !selectedPayment) {
      setSelectedPayment(payments[0]);
    }
  }, [payments, selectedPayment]);

  // Sync selected rules to refs for access inside non-reactive contexts (e.g. event handlers, timeouts)
  useEffect(() => {
    selectedDeliveryRuleRef.current = selectedDeliveryRule;
  }, [selectedDeliveryRule]);
  // Keep delivery fee refs in sync so the debounced calculateCheckoutTotals() timer
  // (below) always reads the freshly computed fee instead of a stale closure value.
  useEffect(() => {
    invoiceDeliveryFeeRef.current = invoiceDeliveryFee;
  }, [invoiceDeliveryFee]);
  useEffect(() => {
    invoiceTaxDeliveryFeeRef.current = invoiceTaxDeliveryFee;
  }, [invoiceTaxDeliveryFee]);
  // Load cart items from localStorage and validate them against the latest menu snapshot.
  useEffect(() => {
    if (PreOrder_id) return undefined;

    let cancelled = false;

    const loadAndValidateCart = async () => {
      let checkoutData = generalData?.[0]?.data || {};

      try {
        checkoutData = await initData();
      } catch (error) {
        console.error("Error refreshing checkout menu data:", error);
      }

      if (cancelled) return;

      const refreshedGeneralData = safeParse(localStorage.getItem("generalData"), []) || [];
      const refreshedDeliveryRules = safeParse(localStorage.getItem("deliveryFeeRule"), []) || [];
      const refreshedPickRules = safeParse(localStorage.getItem("pickAndGoRule"), []) || [];

      setGeneralData(refreshedGeneralData);
      setDeliveryFeeRule(refreshedDeliveryRules.filter(rule => Number(rule.allow_immediate) === 1));
      setDeliveryFeeRuleSchedule(refreshedDeliveryRules.filter(rule => Number(rule.allow_scheduled) === 1));
      setPickAndGoRule(refreshedPickRules);

      const paymentMethods = checkoutData?.paymentMethods || refreshedGeneralData?.[0]?.data?.paymentMethods || [];
      if (paymentMethods.length > 0) {
        setPayments(paymentMethods);
        setSelectedPayment(prev => prev || paymentMethods[0]);
      }

      applyCartAvailabilityValidation(checkoutData, { notify: true, resetToCart: false });
      reloadCartFromStorage(setCartItems);
    };

    loadAndValidateCart();

    return () => {
      cancelled = true;
    };
  }, [PreOrder_id]);

  useEffect(() => {
    if (!hasActiveTableSession) {
      const hadTableConfirmation = Boolean(tableOrderConfirmation);
      setTableOrderNotice("");
      setTableOrderWarning("");
      setTableOrderConfirmation(null);
      checkedTableCartBranchRef.current = false;
      if (hadTableConfirmation) {
        AlertSmall("La sesion de mesa ya no esta disponible.", "warning");
        navigate("/menu");
      }
      return;
    }

    setCurrentStep(0);
    currentStepRef.current = 0;
    setOption("");
    setDeliveryMethod("");
    setWhenDelivery("");
    setDeliveryTiming("now");
    refreshActiveTableOrder();
  }, [hasActiveTableSession, refreshActiveTableOrder, tableOrderConfirmation, navigate]);

  useEffect(() => {
    if (!hasActiveTableSession || checkedTableCartBranchRef.current) return;

    const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];
    if ((products.length + combos.length) === 0) return;

    checkedTableCartBranchRef.current = true;
    if (!hasDifferentBranchCartItems({ products, combos, branch: tableSession?.branchoffice })) return;

    const clearCart = window.confirm("Tu carrito pertenece a otra sucursal. Para usar esta mesa debes vaciarlo. ¿Deseas vaciarlo ahora?");
    if (clearCart) {
      localStorage.removeItem("shoppingCart");
      localStorage.removeItem("shoppingCartCombos");
      reloadCartFromStorage(setCartItems);
      setTableOrderWarning("Se vacio el carrito anterior para mantener la sucursal de la mesa.");
    } else {
      setTableOrderWarning("No se mezclaran productos de otra sucursal con esta mesa.");
    }
  }, [hasActiveTableSession, tableSession?.branchoffice]);

  useEffect(() => {
    if (!currentInvite?.expires_at) return undefined;
    const timer = setInterval(() => setInviteClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [currentInvite?.expires_at]);

  /*- Invoice totals calculation effect -*/
  useEffect(() => {
    const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

    calculateInvoiceTotals(products, combos, option);
  }, [cartItems, selectedDeliveryRule, option, addressNowLocation, deliveryTiming, deliveryDate, deliverySlot]);

  useEffect(() => {
    invalidateCheckoutCalculation();

    const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

    if ((products.length + combos.length) === 0 || currentStepRef.current === 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      calculateCheckoutTotals({ silent: true }).catch(error => {
        console.error("No se pudo recalcular los totales oficiales:", error);
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [
    cartItems,
    selectedDeliveryRule,
    option,
    addressNowLocation,
    deliveryTiming,
    deliveryDate,
    deliverySlot,
    invoiceTip,
    dataDicountApply
  ]);

  // Whenever cart items change, re-evaluate applicable rules and save to localStorage
  useEffect(() => {
    if (clientData) {
      localStorage.setItem("client", JSON.stringify(clientData));
    }
  }, [clientData]);
  // Whenever cart items change, re-evaluate applicable rules and save to localStorage
  useEffect(() => {
    if (addressNowLocation) {
      localStorage.setItem("addressNowLocation", JSON.stringify(addressNowLocation));
    }
  }, [addressNowLocation]);
  // Whenever invoice data changes, save to localStorage
  useEffect(() => {
    const saved = safeParse(localStorage.getItem("invoiceData"), null);
    if (saved) {
      setWantsInvoice(true);
      setInvoiceName(saved.name || '');
      setInvoiceRuc(saved.ruc || '');
      setInvoiceDv(saved.dv || '');
    }
  }, []);
  // Whenever invoice data changes, save to localStorage
  useEffect(() => {
    if (wantsInvoice) {
      localStorage.setItem("invoiceData", JSON.stringify({
        name: invoiceName,
        ruc: invoiceRuc,
        dv: invoiceDv
      }));
    }
  }, [wantsInvoice, invoiceName, invoiceRuc, invoiceDv]);


  // Whenever cart groups or branch office context change, re-evaluate applicable pick-and-go rule
  useEffect(() => {
    const selectedEventRule = choosePickNGoRule({
      rules: eventRules,
      branchofficeId,
      cartGroups: cartGroupsForRules,
      requireAvailableSlot: true
    });
    const selectedNormalRule = choosePickNGoRule({
      rules: pickAndGoRule,
      branchofficeId,
      cartGroups: cartGroupsForRules,
      requireAvailableSlot: true
    });
    const selectedRule = selectedEventRule || selectedNormalRule;

    setUsingEventPickRule(!!selectedEventRule);
    setSelectedPickAndGoRule(selectedRule);

    if (!selectedRule) {
      if (option === "pick") {
        setOption("");
        setDeliveryMethod("");
      }
      setPickDate(null);
      setPickSlot(null);
      return;
    }

    const def = getDefaultPickDateAndSlot(selectedRule);
    setPickDate(def?.date || null);
    setPickSlot(def?.slot || null);
  }, [branchofficeId, eventRules, pickAndGoRule, cartItems, option]);

  // Whenever delivery timing or delivery fee rules change, re-evaluate selected delivery rule and corresponding date/slot
  useEffect(() => {
    if (deliveryTiming === "now") {
      const first = pickFirstByPriority(deliveryFeeRule);
      setSelectedDeliveryRule(first);
      lastAutoRuleIdRef.current = first?.id || null;
      setDeliveryDate(null);
      setDeliverySlot(null);
      return;
    }

    if (deliveryTiming === "scheduled") {
      const first = pickFirstByPriority(deliveryFeeRuleSchedule);
      setSelectedDeliveryRule(first);
      lastAutoRuleIdRef.current = first?.id || null;

      const def = first ? getDefaultDeliveryDateAndSlot(first) : null;
      setDeliveryDate(def?.date || null);
      setDeliverySlot(def?.slot || null);
    }
  }, [deliveryTiming, deliveryFeeRule, deliveryFeeRuleSchedule]);

  // Whenever selected delivery rule changes (due to timing change or rules update), update the delivery date and slot if the change was triggered by a different rule than the currently selected one (to avoid overwriting user selection when rules change but the selected one remains the same)
  useEffect(() => {
    if (deliveryTiming !== "scheduled") return;
    if (!selectedDeliveryRule?.id) return;

    if (lastAutoRuleIdRef.current !== selectedDeliveryRule.id) {
      lastAutoRuleIdRef.current = selectedDeliveryRule.id;

      const def = getDefaultDeliveryDateAndSlot(selectedDeliveryRule);
      setDeliveryDate(def?.date || null);
      setDeliverySlot(def?.slot || null);
    }
  }, [deliveryTiming, selectedDeliveryRule?.id]);

  /*- Reset invoice validation data when invoice fields change -*/
  useEffect(() => {
    if (skipInvoiceResetRef.current) {
      skipInvoiceResetRef.current = false;
      return;
    }

    setInvoiceValidatedData(null);
    setInvoiceStepValidated(false);
  }, [invoiceName, invoiceRuc, invoiceDv, wantsInvoice]);

  /*- Order number effect -*/
  useEffect(() => {
    getOrderNumber();
  }, []);


  /*- Whenever delivery option is selected and client data is available, fetch client's addresses -*/
  useEffect(() => {
    if (option === "delivery" && clientData?.id) {
      getAddressClientNow(clientData.id);
    }
  }, [option, clientData?.id]);
  /*- Whenever delivery option is deselected, hide address form -*/
  useEffect(() => {
    if (option !== "delivery") {
      setShowAddressForm(false);
    }
  }, [option]);

  /*- Order number helper -*/
  const getOrderNumber = async () => {
    const token = undefined;
    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);

    if (!branchoffice?.id) return;

    if (localStorage.getItem("orderNumber")) {
      setOrderNumber(localStorage.getItem("orderNumber"));
      return;
    }

    try {
      const response = await OrderNumberFetchAPI.getOrderNumber(branchoffice.id, token);
      const nextOrder = response.data.next_transaction_code;
      localStorage.setItem("orderNumber", nextOrder);
      setOrderNumber(nextOrder);
    } catch (error) {
      console.error(error);
    }
  };
  /*- Order payload builder -*/
  const buildOrderPayload = ({ paymentInfo, detailEMVStreamResponse = 0, officialCalculation }) => {
    paymentInfo = paymentInfo || {};
    officialCalculation = officialCalculation || checkoutCalculationRef.current;

    if (!officialCalculation?.calculation_id) {
      throw new Error("No existe un calculo oficial vigente para registrar la orden.");
    }

    const machine = safeParse(localStorage.getItem("machine"), null);
    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    const eventData = safeParse(localStorage.getItem("dataEvent"), null);

    const shoppingCartProducts = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const shoppingCartCombos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

    const invoiceClientData = getInvoiceClientData();

    const payload = {
      user_id: 0,
      EMVStreamResponse: detailEMVStreamResponse,
      evertechPaymentReference: detailEMVStreamResponse?.evertechPaymentReference,
      transaction_code: orderNumber,
      client_name: resolveOrderClientName(invoiceClientData),
      id_Client: 0,
      ruc: clientData?.ruc || invoiceClientData.ruc,
      dv: clientData?.dv || invoiceClientData.dv,
      localizador: "00",
      client_phone: contact.phone || clientData?.phone || "",
      client_email: contact.email || clientData?.email || "",
      dgi_qr: "0",
      Orden: shoppingCartProducts,
      combos: shoppingCartCombos,
      calculation_id: officialCalculation.calculation_id,
      Descuento: officialCalculation.total_discount_order,
      Total: officialCalculation.total_order,
      itbms: officialCalculation.total_tax_order,
      tips: officialCalculation.tip,
      taxes: taxesArrayAll,
      subtotal: officialCalculation.sub_total_order,
      machine_id: machine?.id || 0,
      branchoffice_id: branchoffice?.id || 0,
      tipoOrden: option === "delivery" ? "delivery" : "pickngo",
      paymethod: paymentInfo.payment_id,
      paymenthod_id: paymentInfo.payment_id,
      paymenthod: paymentInfo.payment || "",
      paymethods: [],
      discounts: dataDicountApply,
      paymenthod_integration_id:
        paymentInfo.integration?.id ??
        paymentInfo.integration_id ??
        0,
      table_name: "",
      table_id: "",
      table_code: "",
      parthner_order: false,
      ecommerce_order: true,
      order_status: 10,
      client_id: clientData?.id || 0,
      is_discount_notification: false,
      is_cupon: false,
      is_devolution_notification: false,
    };

    if (eventData) {
      payload.event_id = eventData.id;
      payload.event_name = eventData.name;
      payload.stand_name = requireStand
        ? `${(standName || "").trim()}#${(standNumber || "").trim()}`
        : null;
    }
    if (addressNowLocation && option === "delivery") {
      payload.direction = {
        id: addressNowLocation.id,
        lat: addressNowLocation.lat,
        lng: addressNowLocation.lng,
        name: addressNowLocation.name || "",
        house_num: addressNowLocation.house_num || "",
        phone: addressNowLocation.phone || "",
        additional_info: addressNowLocation.additional_info || "",
        label: addressNowLocation.label || "",
        line: addressNowLocation.line || ""
      };
      payload.client_address = addressNowLocation.line
        || [addressNowLocation.house_num, addressNowLocation.name].filter(Boolean).join(", ")
        || "";

      if (!payload.client_phone && addressNowLocation.phone) {
        payload.client_phone = addressNowLocation.phone;
      }
    }

    if (option === "delivery") {
      payload.delivery = {
        platform:
          selectedDeliveryRule?.fee_type === "kilometer" &&
            selectedDeliveryRule?.distance_source !== "haversine"
            ? selectedDeliveryRule.distance_source
            : "smartpay",
        value: officialCalculation.delivery_total,
        tax: officialCalculation.delivery_tax,
        taxes: selectedDeliveryRule?.taxes || [],
      };

      if (deliveryTiming === "scheduled" && deliveryDate && deliverySlot) {
        const startISO = new Date(`${deliveryDate}T${deliverySlot.from}:00`).toISOString();
        const endISO = new Date(`${deliveryDate}T${deliverySlot.to}:00`).toISOString();

        payload.is_scheduled = true;
        payload.scheduled_for = startISO;
        payload.scheduled_window = { start: startISO, end: endISO };
        payload.scheduled_timezone = "America/Panama";
      }

      const client = safeParse(localStorage.getItem("client"), null);
      // Depending on the delivery rule's fee type and distance source, we may need to include different details in the payload for the delivery
      if (selectedDeliveryRule?.fee_type === "kilometer" && selectedDeliveryRule?.distance_source === "asap") {
        payload.delivery = {
          ...payload.delivery,
          details: {
            branchoffice_id: branchoffice?.id || 0,
            type_id: 1,
            is_personal: 0,
            is_oneway: 1,
            source_address: generalData?.[0]?.data?.branchoffice?.direction,
            source_seller_name: generalData?.[0]?.data?.branchoffice?.name,
            source_seller_phone: generalData?.[0]?.data?.branchoffice?.phone,
            special_inst: "1.⁠ ⁠Obligatorio tomar foto de recogida y de entrega de pedidos ",
            source_lat: generalData?.[0]?.data?.branchoffice?.lat,
            source_long: generalData?.[0]?.data?.branchoffice?.lng,
            desti_lat: addressNowLocation?.lat,
            desti_long: addressNowLocation?.lng,
            desti_address: addressNowLocation?.additional_info || addressNowLocation?.line || "",
            desti_customer_name: clientData?.name || contact.name || "Cliente",
            desti_customer_phone:
              client?.phone && client.phone !== "0000000"
                ? client.phone
                : (addressNowLocation?.phone || contact.phone || ""),
            dest_special_inst: "1.⁠ ⁠Obligatorio tomar foto de recogida y de entrega de pedidos ",
            vehicle_type: "bike"
          }
        };
      } else if (selectedDeliveryRule?.fee_type === "kilometer" && selectedDeliveryRule?.distance_source === "tookan") {
        payload.delivery = {
          ...payload.delivery,
          details: {
            branchoffice_id: branchoffice?.id || 0,
            special_inst: "Obligatorio tomar foto de recogida y de entrega de pedidos",
            source_seller_phone: generalData?.[0]?.data?.branchoffice?.phone,
            source_seller_name: generalData?.[0]?.data?.branchoffice?.name,
            source_address: generalData?.[0]?.data?.branchoffice?.direction,
            source_lat: generalData?.[0]?.data?.branchoffice?.lat,
            source_long: generalData?.[0]?.data?.branchoffice?.lng,
            customer_email: client?.email || contact.email || "",
            desti_customer_phone:
              client?.phone && client.phone !== "0000000"
                ? client.phone
                : (addressNowLocation?.phone || contact.phone || ""),
            desti_address: addressNowLocation?.additional_info || addressNowLocation?.line || "",
            desti_customer_name: clientData?.name || contact.name || "Cliente",
            desti_lat: addressNowLocation?.lat,
            desti_long: addressNowLocation?.lng,
          }
        };
      }
    }

    if (option === "pick") {
      if (pickDate && pickSlot) {
        const startISO = new Date(`${pickDate}T${pickSlot.from}:00`).toISOString();
        const endISO = new Date(`${pickDate}T${pickSlot.to}:00`).toISOString();

        payload.is_scheduled = true;
        payload.scheduled_for = startISO;
        payload.scheduled_window = { start: startISO, end: endISO };
        payload.scheduled_timezone = "America/Panama";
      } else {
        payload.is_scheduled = false;
        payload.scheduled_for = null;
        payload.scheduled_window = { start: null, end: null };
        payload.scheduled_timezone = "America/Panama";
      }
    }

    if (Number(paymentInfo.payment_id) === 23 && (Number(paymentInfo.integration?.id) === 3 || Number(paymentInfo.integration_id) === 3)) {
      payload.payment_info = {
        bank_name: paymentInfo.params?.find(param => param.name === "bank_name")?.value || 0,
        account_number: paymentInfo.params?.find(param => param.name === "account_number")?.value || 0,
        account_holder: paymentInfo.params?.find(param => param.name === "account_holder")?.value || 0,
        account_type: paymentInfo.params?.find(param => param.name === "account_type")?.value || 0,
      };
    }

    return payload;
  };
  /* - DGI validation - */
  const validateInvoiceWithDgi = async () => {
    if (!wantsInvoice) {
      setInvoiceErrors({});
      setInvoiceValidatedData({
        client_name: "CONSUMIDOR FINAL",
        ruc: 0,
        dv: 0,
      });
      setInvoiceStepValidated(true);
      return true;
    }

    const e = {};

    if (!invoiceName.trim()) e.name = "Nombre requerido";
    if (!invoiceRuc.trim()) e.ruc = "RUC/Cédula requerido";

    if (invoiceRuc.trim() && invoiceRuc.includes("-") && !invoiceDv.trim()) {
      e.dv = "DV requerido para RUC";
    }

    if (Object.keys(e).length > 0) {
      setInvoiceErrors(e);
      setInvoiceStepValidated(false);
      return false;
    }

    const manualData = {
      client_name: invoiceName.trim(),
      ruc: invoiceRuc.trim(),
      dv: invoiceDv.trim() || 0,
      validated_by_dgi: false,
    };

    try {
      setIsValidatingInvoice(true);
      setInvoiceErrors({});

      const response = await DgiFetchAPI.checkDGIContribuyent(invoiceRuc.trim());
      const dataDgi = response.data;
      if (
        dataDgi?.dgi_response?.gResProc &&
        dataDgi.dgi_response.gResProc.dCodRes === "0680"
      ) {
        let validatedDv = dataDgi.dgi_response.dDV || "";
        if (validatedDv && String(validatedDv).length < 2) {
          validatedDv = "0" + validatedDv;
        }

        const validatedData = {
          client_name: dataDgi.dgi_response.dNomb || invoiceName.trim(),
          ruc: dataDgi.dgi_response.dRuc || invoiceRuc.trim(),
          dv: validatedDv || invoiceDv.trim() || 0,
          validated_by_dgi: true,
        };

        skipInvoiceResetRef.current = true;
        setInvoiceName(validatedData.client_name);
        setInvoiceRuc(validatedData.ruc);
        setInvoiceDv(validatedData.dv);
        setInvoiceValidatedData(validatedData);
        setInvoiceStepValidated(true);

        return true;
      }

      // Flexible: si DGI no valida, seguimos con lo manual
      setInvoiceValidatedData(manualData);
      setInvoiceErrors({});
      setInvoiceStepValidated(true);
      return true;
    } catch (error) {
      console.error(error);

      // Flexible: si falla la consulta, seguimos con lo manual
      setInvoiceValidatedData(manualData);
      setInvoiceErrors({});
      setInvoiceStepValidated(true);
      return true;
    } finally {
      setIsValidatingInvoice(false);
    }
  };
  /*- ASAP fee calculation -*/
  const calculateAsapFee = async () => {
    try {
      const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
      const token = undefined;

      let data = {
        branchoffice_id: branchoffice?.id || 0,
        type_id: 1,
        is_personal: 0,
        is_oneway: 1,
        source_lat: generalData?.[0]?.data?.branchoffice?.lat,
        source_long: generalData?.[0]?.data?.branchoffice?.lng,
        desti_lat: addressNowLocation?.lat,
        desti_long: addressNowLocation?.lng,
        vehicle_type: "bike",
        order_id: orderNumber
      };

      const response = await AsapFetchAPI.getAsapFareOrder(token, data);
      return response.data.asap_response;
    } catch (err) {
      ErrorAlert("No se pudo calcular la tarifa de delivery de asap, puede estar fuera de rango", "Alerta", "error");
      return null;
    }
  };

  //Function to calculate the tookan fee 
  const calculateTookanFee = async () => {
    try {
      const business = safeParse(localStorage.getItem("business"), null);
      const token = undefined;

      let data = {
        business_id: business?.id || 0,
        pickup_latitude: generalData?.[0]?.data?.branchoffice?.lat,
        pickup_longitude: generalData?.[0]?.data?.branchoffice?.lng,
        delivery_latitude: addressNowLocation?.lat,
        delivery_longitude: addressNowLocation?.lng
      };

      const response = await TookanFetchAPI.getTookanFareOrder(token, data);
      return response.data.estimate;
    } catch (err) {
      return null;
    }
  };
  // Function to calculate the delivery fee based on the selected rule and current context (e.g. cart subtotal, distance)
  const quoteDeliveryRule = async (rule, subtotalBase) => {
    if (!rule) {
      return { fee: 0, tax: 0, total: 0 };
    }

    let feeBase = 0;
    let deliveryFeeTax = 0;

    const subtotal = Number(subtotalBase || 0);
    const waiveMin = Number(rule.min_subtotal_to_waive || 0);

    if (waiveMin > 0 && subtotal >= waiveMin) {
      return { fee: 0, tax: 0, total: 0, waived: true };
    }

    if (rule.fee_type === "fixed") {
      feeBase = Number(rule.fee_amount || rule.min_fee || 0);
    } else if (rule.fee_type === "percent") {
      feeBase = (Number(rule.fee_percent || 0) / 100) * subtotal;
    } else if (rule.fee_type === "kilometer" && rule.distance_source === "haversine") {
      const branch = safeParse(localStorage.getItem("generalData"), [])?.[0]?.data?.branchoffice || {};
      const branchCoord = { lat: Number(branch.lat), lng: Number(branch.lng) };
      const destCoord = addressNowLocation
        ? { lat: Number(addressNowLocation.lat), lng: Number(addressNowLocation.lng) }
        : null;

      let distanceKm = 0;

      if (branchCoord.lat && branchCoord.lng && destCoord?.lat && destCoord?.lng) {
        distanceKm = haversineKm(branchCoord, destCoord);
      }

      const calc = computeKilometerFee(rule, distanceKm || 0);
      feeBase = Number(calc?.fee || 0);
    } else if (rule.fee_type === "kilometer" && rule.distance_source === "asap") {
      const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
      const token = undefined;

      const data = {
        branchoffice_id: branchoffice?.id || 0,
        type_id: 1,
        is_personal: 0,
        is_oneway: 1,
        source_lat: generalData?.[0]?.data?.branchoffice?.lat,
        source_long: generalData?.[0]?.data?.branchoffice?.lng,
        desti_lat: addressNowLocation?.lat,
        desti_long: addressNowLocation?.lng,
        vehicle_type: "bike",
        order_id: orderNumber
      };

      const response = await AsapFetchAPI.getAsapFareOrder(token, data);
      feeBase = Number(response?.data?.asap_response?.fareTotal?.total || 0);
    } else if (rule.fee_type === "kilometer" && rule.distance_source === "tookan") {
      const business = safeParse(localStorage.getItem("business"), null);
      const token = undefined;

      const data = {
        business_id: business?.id || 0,
        pickup_latitude: generalData?.[0]?.data?.branchoffice?.lat,
        pickup_longitude: generalData?.[0]?.data?.branchoffice?.lng,
        delivery_latitude: addressNowLocation?.lat,
        delivery_longitude: addressNowLocation?.lng
      };

      const response = await TookanFetchAPI.getTookanFareOrder(token, data);
      feeBase = Number(response?.data?.estimate?.amount || 0);
    } else if (rule.fee_type === "zone") {
      const zoneMatch = resolveZoneFee(rule, addressNowLocation);

      if (!zoneMatch) {
        return { fee: 0, tax: 0, total: 0, outOfCoverage: true };
      }

      feeBase = Number(zoneMatch.fee || 0);
    }

    if (Number(rule.taxable) === 1 && Array.isArray(rule.taxes)) {
      rule.taxes.forEach(tax => {
        deliveryFeeTax += roundUpToTwoDecimals(feeBase * Number(tax.taxtValue || 0));
      });
    }

    return {
      fee: roundUpToTwoDecimals(feeBase),
      tax: roundUpToTwoDecimals(deliveryFeeTax),
      total: roundUpToTwoDecimals(feeBase + deliveryFeeTax),
      waived: false
    };
  };

  /* ── Calendar bounds based on selected rules ── */
  const pickCalendarBounds = getPickCalendarBounds(selectedPickAndGoRule);
  const pickMinDate = pickCalendarBounds.minDate;
  const pickMaxDate = pickCalendarBounds.maxDate;

  const deliveryMinDate = new Date();
  const deliveryMaxDate = new Date(
    Date.now() + Number(selectedDeliveryRuleRef.current?.days_ahead || 3) * 24 * 60 * 60 * 1000
  );

  const isDateDisabledPick = (d) => {
    const iso = toISODateLocal(d);
    if (!iso) return true;

    const rule = selectedPickAndGoRule;
    if (!rule) return true;

    if (!isDateInsidePickRule(iso, rule)) return true;

    const slots = generateSlots(iso, rule);
    return slots.length === 0 || slots.every(s => s.disabled);
  };

  const isDateDisabledDelivery = (d) => {
    const iso = toISODateLocal(d);
    if (!iso) return true;

    const rule = selectedDeliveryRuleRef.current;
    if (!rule) return true;

    const slots = generateSlots(iso, rule);
    return slots.length === 0 || slots.every(s => s.disabled);
  };


  /* ── Dynamic steps based on flow ── */
  const buildSteps = () => {
    if (hasActiveTableSession) {
      return [{ key: "cart", label: t('checkout.stepTable') }];
    }

    const base = [
      { key: "cart", label: t('checkout.stepCart') },
      { key: "contact", label: t('checkout.stepContact') }
    ];

    if (deliveryButton || selectedPickAndGoRule) {
      base.push({ key: "delivery", label: t('checkout.stepDelivery') });
    }

    if (option === "delivery") {
      base.push({ key: "address", label: t('checkout.stepAddress') });
      base.push({ key: "when", label: t('checkout.stepWhen') });

      if (
        (deliveryTiming === "now" && deliveryFeeRule.length > 0) ||
        (deliveryTiming === "scheduled" && deliveryFeeRuleSchedule.length > 0)
      ) {
        base.push({ key: "shipping", label: t('checkout.stepMethod') });
      }

      if (deliveryTiming === "scheduled") {
        base.push({ key: "datetime", label: t('checkout.stepDate') });
      }
    }

    if (option === "pick" && selectedPickAndGoRule) {
      base.push({ key: "datetime", label: t('checkout.stepDate') });
    }

    if (requireStand) {
      base.push({ key: "stand", label: t('checkout.stepStand') });
    }

    base.push({ key: "invoice", label: t('checkout.stepInvoice') });
    base.push({ key: "payment", label: t('checkout.stepPayment') });

    return base;
  };

  const steps = buildSteps();
  const currentStepKey = steps[currentStep]?.key;

  const goNext = () => setCurrentStep(s => Math.min(s + 1, steps.length - 1));
  const goPrev = () => setCurrentStep(s => Math.max(s - 1, 0));

  /* ── Qty controls ── */
  const updateQty = (id, delta) => {
    const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

    const productIndex = products.findIndex((item, index) => {
      const itemId = `product-${item.group_id || item.id || index}-${item.segments_string || "plain"}-${index}`;
      return itemId === id;
    });

    if (productIndex >= 0) {
      const product = { ...products[productIndex] };
      const nextQty = Number(product.amount || 1) + delta;

      if (nextQty <= 0) {
        products.splice(productIndex, 1);
      } else {
        product.amount = nextQty;
        product.cantidadItem = nextQty;

        const unitDiscount = Number(product.unit_discount || 0);
        product.descuento = unitDiscount * nextQty;
        product.discount = unitDiscount * nextQty;

        products[productIndex] = product;
      }

      localStorage.setItem("shoppingCart", JSON.stringify(products));
      invalidateCheckoutCalculation();
      reloadCartFromStorage(setCartItems);
      return;
    }

    const comboIndex = combos.findIndex((item, index) => {
      const itemId = `combo-${item.combo_id || item.id || index}-${item.segments_string || "plain"}-${index}`;
      return itemId === id;
    });

    if (comboIndex >= 0) {
      const combo = { ...combos[comboIndex] };
      const nextQty = Number(combo.amount || 1) + delta;

      if (nextQty <= 0) {
        combos.splice(comboIndex, 1);
      } else {
        combo.amount = nextQty;
        combo.cantidadItem = nextQty;

        const unitDiscount = Number(combo.unit_discount || 0);
        combo.descuento = unitDiscount * nextQty;
        combo.discount = unitDiscount * nextQty;

        combos[comboIndex] = combo;
      }

      localStorage.setItem("shoppingCartCombos", JSON.stringify(combos));
      invalidateCheckoutCalculation();
      reloadCartFromStorage(setCartItems);
    }
  };

  const submitCartToTable = async () => {
    if (!hasActiveTableSession) return;

    // Legacy local-cart contents can exist only when they were added before
    // the table session was established. Import them as individual commands;
    // never replace the shared order with a client-side cart snapshot.
    if (cartItems.length === 0) {
      setTableOrderWarning("Agrega productos desde el menu antes de enviarlos a la mesa.");
      return;
    }

    setIsSubmittingTableOrder(true);
    setTableOrderNotice("");
    setTableOrderWarning("");
    try {
      for (const item of cartItems) {
        await addSharedItem(sanitizeTableOrderLine(item.raw));
      }
      localStorage.removeItem("shoppingCart");
      localStorage.removeItem("shoppingCartCombos");
      reloadCartFromStorage(setCartItems);
      invalidateCheckoutCalculation({ dirty: false });
      setTableOrderNotice("Tus productos se agregaron al pedido compartido.");
    } catch (err) {
      setTableOrderWarning("No fue posible agregar todos los productos al pedido compartido. Revisa el pedido antes de reintentar.");
    } finally {
      setIsSubmittingTableOrder(false);
    }
    return;

    const products = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const combos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];
    if ((products.length + combos.length) === 0) {
      setTableOrderWarning("Agrega productos antes de enviar el pedido a la mesa.");
      return;
    }

    if (hasDifferentBranchCartItems({ products, combos, branch: tableSession?.branchoffice })) {
      const clearCart = window.confirm("El carrito actual pertenece a otra sucursal. ¿Deseas vaciarlo antes de continuar?");
      if (clearCart) {
        localStorage.removeItem("shoppingCart");
        localStorage.removeItem("shoppingCartCombos");
        reloadCartFromStorage(setCartItems);
        setTableOrderWarning("Carrito vaciado. Agrega productos de esta sucursal para enviarlos a la mesa.");
      } else {
        setTableOrderWarning("No se envio el pedido para evitar mezclar sucursales.");
      }
      return;
    }

    tableOrderInFlightRef.current = true;
    if (!tableOrderIdempotencyKeyRef.current) {
      tableOrderIdempotencyKeyRef.current = createTableOrderIdempotencyKey();
    }

    setIsSubmittingTableOrder(true);
    setTableOrderNotice("");
    setTableOrderWarning("");

    try {
      const acceptedOrder = await submitTableOrder({
        cart: buildTableOrderCartPayload(),
        idempotencyKey: tableOrderIdempotencyKeyRef.current,
      });

      localStorage.removeItem("shoppingCart");
      localStorage.removeItem("shoppingCartCombos");
      reloadCartFromStorage(setCartItems);
      invalidateCheckoutCalculation({ dirty: false });

      setTableOrderNotice(`Pedido enviado a Mesa ${tableLabel}. Estado: ${acceptedOrder?.status || "recibido"}.`);
      setTableOrderConfirmation({
        status: acceptedOrder?.status || acceptedOrder?.order_status || "recibido",
        acceptedCount: normalizeAcceptedTableItems(acceptedOrder).length || acceptedTableItems.length,
      });
      if (acceptedOrder?.warnings?.length) {
        setTableOrderWarning("Algunos productos no pudieron modificarse y se mantienen como los acepto el backend.");
      }
      tableOrderIdempotencyKeyRef.current = null;
    } catch (err) {
      setTableOrderWarning("No fue posible enviar el pedido a la mesa. Intenta nuevamente.");
    } finally {
      tableOrderInFlightRef.current = false;
      setIsSubmittingTableOrder(false);
    }
  };

  /* ── Validations ── */
  const changeSharedLineQuantity = async (line, delta) => {
    if (!line?.editable || !line?.line_uuid) return;
    setTableOrderWarning("");
    try {
      const nextQuantity = sharedLineQuantity(line) + delta;
      if (nextQuantity <= 0) await removeSharedItem(line.line_uuid);
      else await updateSharedItem(line.line_uuid, { amount: nextQuantity, cantidadItem: nextQuantity });
    } catch (err) {
      setTableOrderWarning("No fue posible actualizar este producto. El pedido se sincronizara con el servidor.");
    }
  };

  // Direct removal from the trash icon - same server-side guard as reaching qty 0 (editable is
  // false the moment a waiter sends the line to the kitchen, see tableSessionOrderSnapshot.js).
  const deleteSharedTableLine = async (line) => {
    if (!line?.editable || !line?.line_uuid) return;
    setTableOrderWarning("");
    try {
      await removeSharedItem(line.line_uuid);
    } catch (err) {
      setTableOrderWarning("No fue posible eliminar este producto. El pedido se sincronizara con el servidor.");
    }
  };

  // ---- Descuento por linea: el cliente lo pide, el personal lo aprueba o rechaza en el POS (Mesas) ----
  const DISCOUNT_REASON_OPTIONS = ["Jubilado / tercera edad", "Otro"];

  const openDiscountRequest = (line) => {
    if (!line?.can_request_discount || !line?.line_uuid) return;
    setTableOrderWarning("");
    setDiscountReason("");
    setDiscountRequestLine(line);
  };

  const closeDiscountRequest = () => {
    if (isSubmittingDiscount) return;
    setDiscountRequestLine(null);
    setDiscountReason("");
  };

  const submitDiscountRequest = async () => {
    if (!discountRequestLine?.line_uuid || isSubmittingDiscount) return;
    setIsSubmittingDiscount(true);
    setTableOrderWarning("");
    try {
      await requestItemDiscount(discountRequestLine.line_uuid, discountReason.trim());
      AlertSmall("Solicitud enviada. El personal la revisará en breve.", "success");
      setDiscountRequestLine(null);
      setDiscountReason("");
    } catch (err) {
      setTableOrderWarning(err?.response?.data?.error || "No fue posible enviar la solicitud de descuento. Intenta de nuevo.");
    } finally {
      setIsSubmittingDiscount(false);
    }
  };

  // Aviso cuando el personal resuelve un descuento que YO pedi (el estado de la linea ya se actualiza solo).
  useEffect(() => {
    if (!lastDiscountResolvedEvent) return;
    if (lastDiscountResolvedEvent.status === "APPROVED") {
      AlertSmall(`Descuento aprobado${lastDiscountResolvedEvent.rate ? ` (${lastDiscountResolvedEvent.rate}%)` : ""}`, "success");
    } else if (lastDiscountResolvedEvent.status === "REJECTED") {
      AlertSmall(`Tu solicitud de descuento no fue aprobada${lastDiscountResolvedEvent.rejection_note ? `: ${lastDiscountResolvedEvent.rejection_note}` : ""}`, "info");
    }
  }, [lastDiscountResolvedEvent]);

  // ---- "Personalizar factura" de la mesa: la cuenta se factura a este nombre/RUC en vez de consumidor final ----
  const savedTableInvoice = collaborativeOrder?.invoice_data || null;

  const openTableInvoice = () => setShowTableInvoice(true);
  const closeTableInvoice = () => setShowTableInvoice(false);

  // El modal (CustomizeTableInvoiceModal) hace la validacion con la DGI; aqui solo se guarda en la cuenta.
  // Si falla, el error se propaga al modal para mostrarlo ahi.
  const saveTableInvoice = async (invoiceData) => {
    await saveTableInvoiceData(invoiceData);
    AlertSmall("Factura personalizada", "success");
    setShowTableInvoice(false);
  };

  const removeTableInvoice = async () => {
    await saveTableInvoiceData({ client_name: "", ruc: "", dv: "" });
    AlertSmall("Factura a consumidor final", "success");
    setShowTableInvoice(false);
  };

  const handleRequestPayment = async () => {
    setTableOrderWarning("");
    try {
      await requestPayment();
    } catch (err) {
      setTableOrderWarning("No fue posible solicitar el pago. Intenta de nuevo.");
    }
  };

  // ---- Pre-pago partial payment (Fase 4) ----

  const togglePaymentLineSelection = (lineUuid) => {
    if (!lineUuid) return;
    setSelectedPaymentLineUuids((prev) => (
      prev.includes(lineUuid) ? prev.filter((id) => id !== lineUuid) : [...prev, lineUuid]
    ));
  };

  // Opens the SAME payment-methods -> gateway modal flow the whole-account "Pagar" button already
  // uses (see TableRestaurantPaymentSummary's onPay below), but scoped to just the current selection -
  // paymentLineUuidsForModal is what TableSessionPayment receives as its `lineUuids` prop.
  const openPaySelectedLines = () => {
    if (selectedPaymentLineUuids.length === 0) return;
    setPaymentLineUuidsForModal(selectedPaymentLineUuids);
    setShowTablePaymentMethods(true);
  };

  // "Finalizar cuenta": succeeds (closes the account, same destination as a normal checkout) only
  // once nothing is left owed - a 409 PENDING_BALANCE is expected, not an error state, and is used to
  // point the customer at exactly what's left to pay instead of just failing silently.
  const handleFinalizeAccount = async () => {
    setTableOrderWarning("");
    setIsFinalizingAccount(true);
    try {
      const finalOrder = await finalizeAccount();
      AlertSmall("Cuenta cerrada. Gracias por tu visita.", "success");
      if (finalOrder?.order_code) navigate(`/order/${finalOrder.order_code}`);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "PENDING_BALANCE") {
        const pendingBalance = Number(err.response.data.pending_balance) || 0;
        const unpaidLineUuids = Array.isArray(err.response.data.unpaid_line_uuids) ? err.response.data.unpaid_line_uuids : [];
        setSelectedPaymentLineUuids(unpaidLineUuids);
        setTableOrderWarning(`Aun tienes ${fmt(pendingBalance)} pendiente. Selecciona los productos y presiona "Pagar seleccionados" para completar el pago.`);
      } else {
        ErrorAlert("", err?.response?.data?.error || "No fue posible finalizar la cuenta. Intenta de nuevo.", "error");
      }
    } finally {
      setIsFinalizingAccount(false);
    }
  };

  const openTableInvite = async () => {
    setShowTableInvite(true);
    if (currentInvite) return;
    try {
      await createSessionInvite();
    } catch (err) {
      // The dialog renders inviteError from TableSessionContext.
    }
  };

  const validateContact = () => {
    const e = {};
    if (!contact.email.trim() || !contact.email.includes('@')) e.email = 'Email inválido';
    if (!contact.name.trim()) e.name = 'Nombre requerido';
    if (!contact.phone.trim()) e.phone = 'Teléfono requerido';
    setContactErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStand = () => {
    if (!requireStand) {
      setStandErrors({});
      return true;
    }

    const e = {};

    if (!standName.trim()) e.standName = "Nombre del stand requerido";
    if (!standNumber.trim()) e.standNumber = "Número del stand requerido";

    setStandErrors(e);

    if (Object.keys(e).length > 0) {
      return false;
    }

    localStorage.setItem("event_stand_name", standName.trim());
    localStorage.setItem("event_stand_number", standNumber.trim());

    return true;
  };

  /*-- Address management ── */
  const handleSelectAddress = (address) => {
    const normalized = normalizeAddressForCheckout(address);

    localStorage.setItem("addressNowLocation", JSON.stringify(normalized));
    setAddressNowLocation(normalized);
    setSelectedAddress(normalized.id);

    setAddresses(prev => {
      const exists = prev.some(item => String(item.id) === String(normalized.id));
      if (exists) {
        return prev.map(item =>
          String(item.id) === String(normalized.id) ? normalized : item
        );
      }
      return [...prev, normalized];
    });
  };

  const getAddressClientNow = async (client_id) => {
    const client = client_id || clientData?.id;
    const token = undefined;

    if (!client) {
      if (addressNowLocation) {
        setAddresses([normalizeAddressForCheckout(addressNowLocation)]);
      } else {
        setAddresses([]);
      }
      return;
    }

    try {
      const response = await ClientsFetchAPI.getAddressClient(client, token);
      const rawAddresses = response?.data?.data || [];
      const normalizedAddresses = rawAddresses.map(normalizeAddressForCheckout);

      setAddresses(normalizedAddresses);

      const savedAddress = safeParse(localStorage.getItem("addressNowLocation"), null);
      const savedId = savedAddress?.id || savedAddress?._id || savedAddress?.address_id;

      if (savedId) {
        const matched = normalizedAddresses.find(addr => String(addr.id) === String(savedId));
        if (matched) {
          handleSelectAddress(matched);
          return;
        }
      }

      if (normalizedAddresses.length === 1) {
        handleSelectAddress(normalizedAddresses[0]);
      }
    } catch (error) {
      console.error("Error loading client addresses:", error);
      setAddresses([]);
    }
  };

  const deleteAddress = async (addressId) => {
    const token = undefined;

    if (!addressId) return;

    try {
      await ClientsFetchAPI.deleteAddressClient(addressId, token);

      setAddresses(prev => prev.filter(addr => String(addr.id) !== String(addressId)));

      if (String(selectedAddress) === String(addressId)) {
        setSelectedAddress("");
        setAddressNowLocation(null);
        localStorage.removeItem("addressNowLocation");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      ErrorAlert("", "No se pudo eliminar la dirección", "error");
    }
  };

  /* ── Can proceed ── */
  const canProceed = () => {
    switch (currentStepKey) {
      case "cart":
        return cartItems.length > 0;

      case "contact":
        return contact.email && contact.name && contact.phone;

      case "delivery":
        return !!option;

      case "address":
        return !!selectedAddress || !!addressNowLocation;

      case "when":
        return option === "pick" ? true : !!deliveryTiming;

      case "shipping":
        if (!selectedDeliveryRule) return false;

        if (
          selectedDeliveryRule.fee_type === "kilometer" &&
          (!addressNowLocation?.lat || !addressNowLocation?.lng)
        ) {
          return false;
        }

        if (selectedDeliveryRule.fee_type === "zone") {
          if (!addressNowLocation?.lat || !addressNowLocation?.lng) return false;
          if (!resolveZoneFee(selectedDeliveryRule, addressNowLocation)) return false;
        }

        return true;

      case "datetime":
        if (option === "pick") {
          return !!pickDate && !!pickSlot;
        }

        if (option === "delivery" && deliveryTiming === "scheduled") {
          return !!deliveryDate && !!deliverySlot;
        }

        return true;

      case "stand":
        return !requireStand || (!!standName.trim() && !!standNumber.trim());

      case "invoice":
        return wantsInvoice ? invoiceStepValidated : true;

      case "payment":
        return !!selectedPayment;

      default:
        return true;
    }
  };

  /* ── Process Selected Payment ── */
  const buildCalculateTotalsPayload = () => {
    const machine = safeParse(localStorage.getItem("machine"), null);
    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    const shoppingCartProducts = safeParse(localStorage.getItem("shoppingCart"), []) || [];
    const shoppingCartCombos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

    return {
      calculation_session_id: calculationSessionId,
      Orden: shoppingCartProducts,
      combos: shoppingCartCombos,
      delivery: {
        value: invoiceDeliveryFeeRef.current,
        tax: invoiceTaxDeliveryFeeRef.current,
        taxes: selectedDeliveryRule?.taxes || []
      },
      tips: invoiceTip,
      discounts: dataDicountApply,
      machine_id: machine?.id,
      branchoffice_id: branchoffice?.id
    };
  };

  const calculateCheckoutTotals = async ({ silent = false, retryOnMismatch = true } = {}) => {
    if (calculateTotalsPromiseRef.current) {
      return calculateTotalsPromiseRef.current.catch(error => {
        if (isCalculationCartMismatchError(error) && totalsDirtyRef.current) {
          return calculateCheckoutTotals({ silent, retryOnMismatch });
        }

        throw error;
      });
    }

    const promise = (async () => {
      isCalculatingTotalsRef.current = true;
      setIsCalculatingTotals(true);
      const attempts = retryOnMismatch ? 2 : 1;

      try {
        for (let attempt = 1; attempt <= attempts; attempt += 1) {
          const requestId = calculateTotalsRequestRef.current + 1;
          calculateTotalsRequestRef.current = requestId;
          const response = await OrdersFetchAPI.calculateTotals(buildCalculateTotalsPayload());
          const responseData = response?.data || {};

          if (requestId !== calculateTotalsRequestRef.current) {
            if (attempt < attempts) {
              continue;
            }

            throw createCalculationCartMismatchError({
              success: false,
              error: "El carrito cambio, vuelve a calcular los totales"
            }, 409);
          }

          if (responseData.success !== true) {
            if (isCalculationCartMismatchPayload(responseData) && attempt < attempts) {
              invalidateCheckoutCalculation();
              continue;
            }

            if (isCalculationCartMismatchPayload(responseData)) {
              throw createCalculationCartMismatchError(responseData, response?.status);
            }

            throw new Error(responseData.message || responseData.error || "No se pudo calcular el total oficial del pedido.");
          }

          const calculation = normalizeOfficialCalculation(responseData);

          if (!calculation.calculation_id) {
            throw new Error("El backend no devolvio un calculation_id valido para continuar con el pago.");
          }

          setOfficialCheckoutCalculation(calculation);

          if (!silent) {
            console.log("Calculo oficial de checkout:", calculation);
          }

          return calculation;
        }

        throw new Error("No se pudo calcular el total oficial. Intenta nuevamente.");
      } finally {
        isCalculatingTotalsRef.current = false;
        setIsCalculatingTotals(false);
        calculateTotalsPromiseRef.current = null;
      }
    })();

    calculateTotalsPromiseRef.current = promise;
    return promise;
  };

  const getValidCheckoutCalculation = async () => {
    if (isCalculatingTotalsRef.current && calculateTotalsPromiseRef.current) {
      try {
        return await calculateTotalsPromiseRef.current;
      } catch (error) {
        if (!isCalculationCartMismatchError(error)) {
          throw error;
        }
      }
    }

    const currentCalculation = checkoutCalculationRef.current;

    if (
      totalsDirtyRef.current ||
      !currentCalculation?.calculation_id ||
      isCheckoutCalculationExpired(currentCalculation)
    ) {
      return calculateCheckoutTotals();
    }

    return currentCalculation;
  };

  const runWithCalculationRetry = async (operation) => {
    const executeWithCalculation = async (calculation) => {
      const result = await operation(calculation);
      const responseData = result?.data;

      if (responseData?.success === false && isCalculationCartMismatchPayload(responseData)) {
        throw createCalculationCartMismatchError(responseData, result?.status);
      }

      return result;
    };

    try {
      const calculation = await getValidCheckoutCalculation();
      return await executeWithCalculation(calculation);
    } catch (error) {
      if (!isCalculationCartMismatchError(error)) {
        throw error;
      }

      invalidateCheckoutCalculation();
      const recalculation = await calculateCheckoutTotals({ retryOnMismatch: false });
      return executeWithCalculation(recalculation);
    }
  };

  const showOfficialCalculationDifferences = async (officialCalculation) => {
    const differences = getOfficialTotalDifference({
      subtotal: invoiceSubtotal,
      itbms: invoiceTotalItbms,
      discount: invoiceTotalDiscount,
      delivery: invoiceDeliveryFee,
      deliveryTax: invoiceTaxDeliveryFee,
      tip: invoiceTip,
      total: invoiceTotal + invoiceTip
    }, officialCalculation);

    if (!differences.length) return;

    const html = differences
      .map(item => `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0;"><span>${item.label}</span><strong>${fmt(item.officialValue)}</strong></div>`)
      .join("");

    await Swal.fire({
      title: t('checkout.totalsUpdatedTitle'),
      html: `<p>${t('checkout.totalsUpdatedText')}</p>${html}`,
      icon: "info",
      confirmButtonText: t('common.continue')
    });
  };

  const processSelectedPayment = async () => {
    if (!selectedPayment) return;

    const invoiceClientData = getInvoiceClientData();

    const checkoutPayload = {
      client_name: resolveOrderClientName(invoiceClientData),
      ruc: invoiceClientData.ruc,
      dv: invoiceClientData.dv,
      contact,
      option,
      deliveryTiming,
      selectedAddress,
      addressNowLocation,
      selectedDeliveryRule,
      selectedPickAndGoRule,
      pickDate,
      pickSlot,
      deliveryDate,
      deliverySlot,
      payment: selectedPayment
    };

    console.log("Checkout payload listo para pago:", checkoutPayload);

    try {
      setPaymentApproval({ status: 0, description: "Calculando total oficial..." });
      const officialCalculation = await getValidCheckoutCalculation();
      await showOfficialCalculationDifferences(officialCalculation);
      await processPaymentLegacy(selectedPayment, checkoutPayload, officialCalculation);
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || error?.message || "No se pudo calcular el total oficial. Intenta nuevamente.";
      setPaymentApproval({ status: 2, description: message });
      ErrorAlert("", message, "error");
      releasePaymentLock();
    }
  };

  const processPaymentLegacy = async (pays, checkoutPayload, officialCalculation) => {
    const namePayment = pays?.payment || "";
    const integrationId = getPaymentIntegrationId(pays);
    const integrationName = pays?.integration?.name || pays?.integration_name || "";
    console.log(pays)
    console.log("Procesando método:", {
      namePayment,
      integrationId,
      integrationName,
      checkoutPayload
    });

    // A rejected/expired gateway session (Evertech, etc.) resets selectedPayment to
    // { payment: "", payment_id: 0 } as a way to clear a broken selection - but that's
    // still a truthy object, so nothing upstream catches it. Without this guard, a customer
    // clicking "Pagar" again after a failed payment falls through every isXPayment() check
    // below and lands on the catch-all at the end of this function, which silently registers
    // a pending-approval order with no payment info at all (paymethod 0, no gateway response)
    // as if it were a legitimate manual-payment order.
    if (!pays?.payment_id) {
      const message = "No se detectó un método de pago seleccionado. Selecciona uno e intenta de nuevo.";
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow(message);
      ErrorAlert("", message, "error");
      return;
    }

    if (paymentRequiresLogin(pays) && !isClientLoggedInForPayment()) {
      openLoginToPay();
      return;
    }

    if (isCreditOrBankTransfer(pays)) {
      setPaymentApproval({ status: 1, description: "Registrando ..." });
      await registerPendingApprovalOrder(0, pays, officialCalculation);
      return;
    }

    // Tilopay se revisa antes que Evertec: ambas son "pasarelas" y el nombre de la integracion podria coincidir.
    if (isTilopayPayment(pays)) {
      setShowTilopayModal(true);
      return;
    }

    if (isYappyPayment(pays)) {
      setShowYappyBtnModal(true);
      return;
    }

    if (isEvertecPayment(pays)) {
      setShowModalStatusPayment(true);
      setPaymentApproval({ status: 0, description: "Procesando pago..." });
      await evertechPaymentPlaceToPay(pays, officialCalculation);
      return;
    }
    if (isPowerTranzPayment(pays)) {
      await powertranz({
        "CardPan": " ",
        "CardExpiration": " ",
        "CardCvv": " ",
        "CardHolder": " "
      }, { "FirstName": " ", "LastName": " ", "Line1": " ", "Line2": " ", "City": " ", "State": " ", "PostalCode": " ", "CountryCode": " ", "EmailAddress": " ", "PhoneNumber": " " },
        pays,
        officialCalculation);
      return;
    }
    await registerPendingApprovalOrder(0, pays, officialCalculation);
  };
  //Function to request the payment with place to pay (Pasarela de pago of evertech)
  const openTab = (url) => {
    const tab = window.open(url, "_blank");
    if (!tab) {
      const message = "El navegador bloqueó la ventana de pago. Habilita las ventanas emergentes e intenta nuevamente.";
      ErrorAlert("", message, "warning");
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow(message);
      return null;
    }
    setNewTab(tab);
    return tab;
  };

  const closeTab = () => {
    if (newTab) {
      newTab.close();
      setNewTab(null);
    }
  };
  //Function to request the payment with place to pay (Pasarela de pago of evertech)
  const evertechPaymentPlaceToPay = async (pays, officialCalculationArg) => {
    let secretKey, loginKey, language;
    let officialCalculation = officialCalculationArg || checkoutCalculationRef.current;
    const randomString = generateRandomString(4);
    const machine = safeParse(localStorage.getItem("machine"), null);
    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    const client = safeParse(localStorage.getItem("client"), null);

    if (statePayment === true) return;

    if (!officialCalculation?.calculation_id) {
      const message = "El calculo oficial expiro o fue consumido. Recalcula el pedido antes de pagar.";
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow(message);
      ErrorAlert("", message, "error");
      return;
    }

    if (!branchoffice?.id || !machine?.id) {
      const message = "No se encontró la sucursal o la caja para iniciar el pago.";
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow(message);
      ErrorAlert("", message, "error");
      return;
    }

    setStatePayment(true);

    try {
      pays.params?.forEach(param => {
        switch (param.name) {
          case "LANGUAGE":
            language = param.value;
            break;
          case "SECRET_KEY":
            secretKey = param.value;
            break;
          case "LOGIN_KEY":
            loginKey = param.value;
            break;
          default:
            break;
        }
      });

      const payer = client && client.name !== "Invitado"
        ? {
          name: client.name?.split(" ")[0] || "",
          surname: client.name?.split(" ").slice(1).join(" ") || "",
          email: client.email || "",
          phone: client.phone || "",
          ruc: client.ruc || ""
        }
        : {
          name: contact.name?.split(" ")[0] || "",
          surname: contact.name?.split(" ").slice(1).join(" ") || "",
          email: contact.email || "",
          phone: contact.phone || "",
          ruc: getInvoiceClientData().ruc || ""
        };

      const evertechResponse = await runWithCalculationRetry(async (calculation) => {
        officialCalculation = calculation;
        return EvertechFetchAPI.newPayment({
          branchoffice_id: branchoffice.id,
          branch_machine_id: machine.id,
          secretKey,
          loginKey,
          language,
          transaction_code: orderNumber,
          evertech_code: orderNumber + randomString,
          description: `Smartpay order ${orderNumber}`,
          calculation_id: calculation.calculation_id,
          total: calculation.total_order,
          token: undefined,
          client: payer
        });
      });

      const evertecData = evertechResponse?.data?.data;

      if (!evertecData?.requestId || !evertecData?.processUrl) {
        throw new Error("Respuesta inválida de Evertec");
      }

      localStorage.setItem("evertechRequestId", evertecData.requestId);

      await runWithCalculationRetry(async (calculation) => {
        officialCalculation = calculation;
        const preOrderPayload = {
          ...buildOrderPayload({
            paymentInfo: pays,
            detailEMVStreamResponse: 0,
            officialCalculation: calculation
          }),
          is_evertech_pay: true,
          evertech_transaction_id: evertecData.requestId,
          evertech_code: orderNumber + randomString,
          reference_evertech_transaction_code: evertecData.reference_evertech_transaction_code,
          is_pre_order: false,
          consume_calculation: false
        };

        return PreOrderFetchAPI.createPreOrder(
          preOrderPayload,
          undefined
        );
      });

      setStatePayment(false);

      Swal.fire({
        title: "Se abrirá una pestaña nueva para completar el pago",
        text: "Placetopay Confirmation",
        icon: "info",
        showCancelButton: false,
        allowOutsideClick: false,
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          openTab(evertecData.processUrl);
        }
      });
    } catch (err) {
      console.error(err);
      setStatePayment(false);
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow("Pago rechazado");
      ErrorAlert("", "No se pudo procesar el pago", "error");
    }
  };
  // ---- Tilopay (SDK JavaScript V2) ----
  // El servidor decide el monto: la sesion se crea sobre el calculo oficial y la orden temporal se crea con ese mismo calculo.
  const prepareTilopaySession = async () => {
    const machine = safeParse(localStorage.getItem("machine"), null);
    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    if (!branchoffice?.id || !machine?.id) {
      throw new Error("No se encontró la sucursal o la caja para iniciar el pago.");
    }

    let usedCalculation = null;
    const response = await runWithCalculationRetry(async (calculation) => {
      usedCalculation = calculation;
      return TilopayFetchAPI.createSession({
        calculation_id: calculation.calculation_id,
        branchoffice_id: branchoffice.id,
        branch_machine_id: machine.id,
        client_id: clientData?.id || undefined,
      });
    });

    const session = response?.data?.data;
    if (!session) throw new Error("Respuesta inválida del servidor");
    tilopayCalculationRef.current = usedCalculation;
    return session;
  };

  // Se crea justo antes de cobrar (no al abrir el modal) para no dejar ordenes temporales de pagos que nunca se intentan.
  const createTilopayPreOrder = async (session) => {
    const calculation = tilopayCalculationRef.current;
    if (!calculation?.calculation_id) {
      throw new Error("El cálculo oficial expiró. Cierra esta ventana y vuelve a intentar el pago.");
    }

    const preOrderPayload = {
      ...buildOrderPayload({
        paymentInfo: selectedPayment,
        detailEMVStreamResponse: 0,
        officialCalculation: calculation
      }),
      is_tilopay_pay: true,
      tilopay_order_number: session.order_number,
      is_pre_order: false,
      consume_calculation: false
    };

    return PreOrderFetchAPI.createPreOrder(preOrderPayload, undefined);
  };

  const closeTilopayModal = () => {
    setShowTilopayModal(false);
    tilopayCalculationRef.current = null;
    releasePaymentLock();
  };

  // El servidor confirmo el cobro y registro el pedido en delivery (pendiente de aprobacion del gerente): se cierra el
  // pago y se lleva al cliente a su pedido, igual que con el resto de pasarelas. Hasta aqui nunca salio del checkout.
  const handleTilopayApproved = (view) => {
    setShowTilopayModal(false);
    tilopayCalculationRef.current = null;
    clearCheckoutPaymentStorage();
    clearDirectOrderContext();
    setStandName("");
    setStandNumber("");
    AlertSmall("Tu pedido está pendiente de aprobación", "success");
    releasePaymentLock();
    navigate(view?.order_code ? `/order/pending/${view.order_code}` : "/orders");
  };

  //Function to request the payment with powertranz (Pasarela de pago of powertranz)
  const powertranz = async (source, BillingAddress, paysArg, officialCalculationArg) => {
    const pays = paysArg || selectedPayment;
    let officialCalculation = officialCalculationArg || checkoutCalculationRef.current;
    const preparation = false;
    const randomString = generateRandomString(4);
    const params = pays?.params || selectedPayment?.params || [];

    const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
    const machine = safeParse(localStorage.getItem("machine"), null);

    const X_PowerTranz_BaseUrl = "https://staging.ptranz.com";

    const OrderIdentifier = orderNumber + "X" + randomString;

    if (!officialCalculation?.calculation_id) {
      const message = "El calculo oficial expiro o fue consumido. Recalcula el pedido antes de pagar.";
      setShowModalStatusPayment(true);
      resetExternalPaymentFlow(message);
      ErrorAlert("", message, "error");
      return;
    }

    setOrderIdPowertranz(OrderIdentifier);
    localStorage.setItem("OrderIdentifier_powertranz", OrderIdentifier);

    await runWithCalculationRetry(async (calculation) => {
      officialCalculation = calculation;
      let data = {
        branchoffice_id: branchoffice?.id,
        branch_machine_id: machine?.id,
        calculation_id: calculation.calculation_id,
        TotalAmount: calculation.total_order,
        CurrencyCode: "840",
        ThreeDSecure: true,
        OrderIdentifier,
        Source: source,
        BillingAddress,
        AddressMatch: false,
        ExtendedData: {
          MerchantResponseUrl: "",
        }
      };

      // Solo una marca de intencion: el backend la valida contra el JWT del cliente y decide si guarda algo.
      // La tarjeta sigue capturandose unicamente en la HPP de PowerTranz.
      if (saveCardForFuture === true && isClientLoggedInForPayment()) {
        data.save_card = true;
      }

      if (preparation === true) {
        data.flow = "auth";
        setTypePowertranz("auth");
      } else {
        data.flow = "sale";
        setTypePowertranz("sale");
      }

      return powertranzSaleAndAuth(
        data,
        X_PowerTranz_BaseUrl,
        calculation
      );
    });
  };
  const cancelPowertranzPayment = (statusDescription, message) => {
    let description = "Pago rechazado";

    if (statusDescription && message) {
      description = `${statusDescription} - ${message}`;
    }

    setShowIframe(false);
    sethtmlData("");
    setStatePayment(false);
    setPaymentApproval({ status: 2, description });

    localStorage.removeItem("tempPowertranz");
    setTempPowertranz(null);

    setTimeout(() => {
      setSelectedPayment(null);
      setPaymentApproval({ status: 0, description: "Procesando pago..." });
      setShowModalStatusPayment(false);
      releasePaymentLock();
    }, 3000);
  };

  const startPowertranzIframeFlow = async (pays) => {
    try {
      setShowModalStatusPayment(true);
      setPaymentApproval({ status: 0, description: "Procesando pago..." });

      const token = undefined;

      // aquí armas el payload que tu backend necesite
      //const response = await PowertranzFetchAPI.algunMetodoIframe(...);
      const response = []
      const dataNow = response.data;

      if (dataNow?.raw?.RedirectData) {
        await createTemporalOrderPowertranz(dataNow, false);
        return;
      }

      cancelPowertranzPayment("Sin respuesta 3DS", "No se recibió RedirectData");
    } catch (err) {
      console.error(err);
      cancelPowertranzPayment();
    }
  };

  const closeIframe = () => {
    cancelPowertranzPayment();
  };

  const createTemporalOrderPowertranz = async (powertranzResponse, pending, officialCalculationArg) => {
    try {
      const token = undefined;

      const responseCreatePreOrder = await runWithCalculationRetry(async (calculation) => {
        const payload = {
          ...buildOrderPayload({
            paymentInfo: selectedPayment,
            detailEMVStreamResponse: 0,
            officialCalculation: calculation
          }),
          is_pre_order: pending || false,
          is_evertech_pay: false,
          is_powertranz_pay: true,
          consume_calculation: false,
          powertranz: {
            ...powertranzResponse,
            calculation_id: calculation.calculation_id
          },
          powertranz_spiToken: powertranzResponse.spiToken,
        };

        return PreOrderFetchAPI.createPreOrder(payload, token);
      });

      localStorage.setItem("tempPowertranz", JSON.stringify(responseCreatePreOrder.data.newTempOrder));
      setTempPowertranz(responseCreatePreOrder.data.newTempOrder);

      if (powertranzResponse?.raw?.RedirectData) {
        actionPowertranzPayment(powertranzResponse.raw);
      } else {
        cancelPowertranzPayment("Sin respuesta 3DS", "No se recibió RedirectData");
      }
    } catch (err) {
      console.error(err);
      cancelPowertranzPayment();
    }
  };

  const actionPowertranzPayment = (data) => {
    setPowertranzData(data);

    const htmlTemplate = `
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>PowerTranz Payment</title>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            width: 100vw;
            min-height: 100vh;
            background-color: transparent !important;
            overflow: hidden !important;
          }
          .bg-white {
            background-color: transparent !important;
          }
        </style>
      </head>
      <body>
        <div class="form-container">
          ${data.RedirectData}
        </div>
      </body>
    </html>
  `;

    sethtmlData(htmlTemplate);
    setShowIframe(true);
  };

  const powertranzMethodPayment = async (txid, SpiToken) => {
    setStatusPowertranz("PENDING")
    let data = {
      SpiToken,
      TransactionIdentifier: txid,
      AutoComplete: true
    };

    const token = undefined;
    const params = selectedPayment?.params || [];
    try {
      const response = await PowertranzFetchAPI.payment(data, token);

      data.X_PowerTranz_Password = params.find(param => param.name === "Password")?.value || "";
      data.X_PowerTranz_Id = params.find(param => param.name === "Id")?.value || "";
      data.X_PowerTranz_BaseUrl = "https://staging.ptranz.com";
      data.powertranzResponse = response.data.raw;

      const datafilter = response.data.raw;

      if (datafilter?.Approved === true) {
        setShowIframe(false);
        sethtmlData("");
        setPaymentApproval({ status: 1, description: "Registrando ..." });

        setTimeout(async () => {
          const now = new Date();
          const formattedDate = `${now.getDate().toString().padStart(2, "0")}/${(now.getMonth() + 1).toString().padStart(2, "0")}/${now.getFullYear()}`;
          const formattedTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;

          data.voucher = `\r\n        ***  POWERTRANZ  ***        \r\n\r\nOrden:                       ${datafilter.OrderIdentifier.split("X")[0]}\r\nDATE: ${formattedDate}            ${formattedTime}\r\n\r\nDESCRIPTION:\r\n   ${datafilter.ResponseMessage}\r\nTOTAL:                      ${datafilter.TotalAmount} \r\n\r\nPAYMENT METHOD:             ${datafilter.CardBrand}\r\nSTATUS:                     APROVADO\r\nAUTH:                       ${datafilter.AuthorizationCode}\r\nTXID:\r\n${datafilter.TransactionIdentifier}\r\n\n           ****** FIN  ******            \r\n`;

          await registerPendingApprovalOrder(data, selectedPayment, checkoutCalculationRef.current);
        }, 3000);
      } else {
        cancelPowertranzPayment();
      }
    } catch (err) {
      console.error(err);
      if (isCalculationCartMismatchError(err)) {
        throw err;
      }
      cancelPowertranzPayment();
    }
  };

  const powertranzSaleAndAuth = async (
    data,
    X_PowerTranz_BaseUrl,
    officialCalculationArg
  ) => {
    setShowModalStatusPayment(true);
    setPaymentApproval({ status: 0, description: "Procesando pago..." });

    try {
      const token = undefined;

      const response = await PowertranzFetchAPI.sale(
        data,
        X_PowerTranz_BaseUrl,
        token
      );

      let dataNow = response.data;
      dataNow.X_PowerTranz_BaseUrl = X_PowerTranz_BaseUrl;

      await createTemporalOrderPowertranz(dataNow, data.flow === "auth", officialCalculationArg);
    } catch (err) {
      console.error(err);
      cancelPowertranzPayment();
    }
  };

  //Function to handle the different events of the yappy button
  const nextStepYappy = (btnYappy, phoneClientYappy, event, detail) => {
    const yappyStatus = getYappyEventStatus(event, detail);

    if (yappyStatus === "CREATE") {
      requestYappyBtnOrder(btnYappy, phoneClientYappy);
      return;
    }

    if (isYappyRejectedStatus(yappyStatus)) {
      setShowModalStatusPayment(true);
      setShowYappyBtnModal(false);
      const description = yappyStatus.includes("CANCEL")
        ? "Pago cancelado por el usuario"
        : "Pago rechazado por el usuario";
      setPaymentApproval({ status: 2, description });
      localStorage.removeItem("YappyBtnOrderParams");
      localStorage.removeItem("YappyBtnPreOrder");
      localStorage.removeItem("yappy_transaction_id");

      setTimeout(() => {
        setShowModalStatusPayment(false);
        releasePaymentLock();
      }, 3000);
      return;
    }

    if (yappyStatus.includes("EXPIRED")) {
      setShowModalStatusPayment(true);
      setShowYappyBtnModal(false);
      setPaymentApproval({ status: 2, description: "Pago expirado" });
      localStorage.removeItem("YappyBtnOrderParams");
      localStorage.removeItem("YappyBtnPreOrder");
      localStorage.removeItem("yappy_transaction_id");

      setTimeout(() => {
        setShowModalStatusPayment(false);
        releasePaymentLock();
      }, 3000);
      return;
    }

    // The temp order (with the full cart/delivery/address/scheduling data) was already created
    // in requestYappyBtnOrder, before the customer paid - unlike other payment methods, we must
    // NOT call registerPendingApprovalOrder here, since that would create a second, duplicate
    // temp order. The delivery order itself is registered by the backend when Yappy's signed IPN
    // webhook arrives (see getYappyBtnOrderNotification), so this only handles the UI: we
    // deliberately don't tell the backend "it was paid" from the browser, since nothing here can
    // be verified.
    const yappyPreOrder = safeParse(localStorage.getItem("YappyBtnPreOrder"), null);

    setPaymentApproval({ status: 1, description: "Realizado" });
    setShowModalStatusPayment(true);
    setShowYappyBtnModal(false);

    setTimeout(() => {
      clearCheckoutPaymentStorage();
      clearDirectOrderContext();
      AlertSmall("Tu pedido está pendiente de aprobación", "success");
      releasePaymentLock();
      setShowModalStatusPayment(false);

      if (yappyPreOrder?.pre_order_code) {
        navigate(`/order/pending/${yappyPreOrder.pre_order_code}`);
      }
    }, 3000);
  };

  const requestYappyBtnOrder = async (btnYappy, phoneClientYappy) => {
    try {
      let officialCalculation = checkoutCalculationRef.current;
      const generalData = safeParse(localStorage.getItem("generalData"), []);
      const params =
        generalData?.[0]?.data?.paymentMethods?.find(payment => isYappyPayment(payment));

      const machine = generalData?.[0]?.data?.machine;

      if (!params || !machine?.machine_per_branch_id) {
        throw new Error("No se encontro la configuracion de Yappy para esta caja.");
      }

      if (!officialCalculation?.calculation_id) {
        throw new Error("El calculo oficial expiro o fue consumido. Recalcula el pedido antes de pagar.");
      }

      const response = await runWithCalculationRetry(async (calculation) => {
        officialCalculation = calculation;
        return YappyFetchAPI.newYappyBtnOrder({
          params: [],
          calculation_id: calculation.calculation_id,
          total: calculation.total_order,
          subtotal: calculation.total_order,
          taxes: 0,
          discount: 0,
          transaction_code: orderNumber,
          description: "Smartpay - orden:" + orderNumber,
          phone: phoneClientYappy,
          machine_id: machine?.machine_per_branch_id,
        }, undefined);
      });

      if (
        response?.data?.yappy_order &&
        response?.data?.yappy_order?.body?.documentName &&
        response?.data?.yappy_order?.body?.transactionId
      ) {
        const yappyParams = {
          transactionId: response.data.yappy_order.body.transactionId,
          documentName: response.data.yappy_order.body.documentName,
          token: response.data.yappy_order.body.token
        };

        localStorage.setItem("YappyBtnOrderParams", JSON.stringify(yappyParams));

        // We register the temp order now, before the customer approves the payment in the Yappy
        // app - not after eventSuccess like before. This mirrors what Evertech already does
        // (build the payload, createPreOrder, then hand off to the gateway) and is what lets the
        // backend (IPN webhook) register the delivery order on its own even if this tab closes
        // right after the customer pays: it looks up the temp order by yappy.transactionId,
        // which now already exists.
        const preOrderPayload = {
          ...buildOrderPayload({
            paymentInfo: params,
            detailEMVStreamResponse: 0,
            officialCalculation
          }),
          is_yappy_pay: true,
          yappy_transaction_id: yappyParams.transactionId,
          yappy_document_name: yappyParams.documentName,
          yappy_order_token: yappyParams.token,
          is_pre_order: false,
          consume_calculation: false
        };

        const preOrderResponse = await PreOrderFetchAPI.createPreOrder(preOrderPayload, undefined);

        const preOrderInfo = {
          pre_order_id: preOrderResponse?.data?.newTempOrder?._id,
          pre_order_code: preOrderResponse?.data?.newTempOrder?.code
        };

        if (!preOrderInfo.pre_order_id) {
          throw new Error("No se pudo registrar la orden antes del pago.");
        }

        localStorage.setItem("YappyBtnPreOrder", JSON.stringify(preOrderInfo));

        if (btnYappy && typeof btnYappy.eventPayment === "function") {
          btnYappy.eventPayment(yappyParams);
        }
      } else {
        throw new Error("Yappy no devolvio los datos para iniciar el pago.");
      }
    } catch (err) {
      console.error(err);
      const description = err?.message || "Algo salio mal";
      setShowYappyBtnModal(false);
      localStorage.removeItem("YappyBtnOrderParams");
      localStorage.removeItem("YappyBtnPreOrder");
      localStorage.removeItem("yappy_transaction_id");
      setShowModalStatusPayment(true);
      setPaymentApproval({ status: 2, description });

      setTimeout(() => {
        setShowModalStatusPayment(false);
        releasePaymentLock();
      }, 3000);
    }
  };


  //Function to calculate the invoice totals
  const calculateInvoiceTotals = async (products, combos, deliveryOption) => {

    //For every new calculate we set the totals
    let taxesArray = [];
    let invoiceTotalDiscount = 0
    let invoiceSubtotal = 0
    let invoiceTotalItbms = 0
    let invoiceTotal = 0;
    let deliveryFeeTax = 0;
    let invoiceDeliveryFee = 0;
    //if we dont we products we put the totals in 0 for default
    if ((products.length + combos.length) === 0) {
      //We calculate the totals of the invoice
      setInvoiceTotalDiscount(0)
      setInvoiceSubtotal(0);
      setInvoiceTotalItbms(0)
      setInvoiceTotal(0);
      setInvoiceDeliveryFee(0);
      setInvoiceTaxDeliveryFee(0);
      setTaxesArrayAll(taxesArray)
    } else {

      //For every combos we calculate
      combos.forEach((combo) => {

        byProductandCombo(combo)
      })

      products.forEach((group) => {
        byProductandCombo(group)
      })

      //Esta funcion calcula los valores por producto o combo por posicion
      function byProductandCombo(item) {

        //Variables
        let total_product_price = 0;
        let total_product_itbms = 0;
        let product_total_discount = 0;
        //We set the total discount of the product
        product_total_discount = (item.unit_discount * item.amount);
        //We set the total of the product
        total_product_price = (item.amount * (item.price - item.unit_discount));

        //Calculate tax by group--------------------------------
        let taxesGroup = Array.isArray(item.taxes) ? item.taxes : [];
        taxesGroup.forEach(tax => {
          //calculate tax  value and add it to array
          let valueTax = roundUpToTwoDecimals((item.amount * ((item.price - item.unit_discount) * tax.taxtValue)));
          console.log(valueTax);
          tax.total = valueTax;
          //Identifier tax of the group position
          var index = taxesArray.findIndex(function (info, i) {
            return info.tax_id === tax.tax_id
          });

          if (index < 0) {
            let taxInfoNow = {
              "dTasaITBMSValue": tax.dTasaITBMSValue,
              "percentage": tax.percentage,
              "tax": tax.tax,
              "tax_id": tax.tax_id,
              "taxtValue": tax.taxtValue,
              "total": valueTax
            }
            taxesArray.push(taxInfoNow);
          } else {
            taxesArray[index].total = (taxesArray[index].total + valueTax)
          }
          total_product_itbms = (total_product_itbms + valueTax)

        });

        //We calculate the totals of the invoice
        invoiceTotalDiscount = invoiceTotalDiscount + product_total_discount;
        invoiceSubtotal = invoiceSubtotal + total_product_price;
        invoiceTotalItbms = invoiceTotalItbms + total_product_itbms;
        invoiceTotal = invoiceTotal + total_product_price + total_product_itbms;
      }

      // We check the delivery fee rule, delivery option, and if the free shipping threshold was not met
      const deliveryWaiveMin = Number(selectedDeliveryRule?.min_subtotal_to_waive || 0);
      const isDeliveryFeeWaived = deliveryWaiveMin > 0 && invoiceSubtotal >= deliveryWaiveMin;

      if (selectedDeliveryRule && deliveryOption === "delivery" && !isDeliveryFeeWaived) {
        let feeBase = 0;

        if (selectedDeliveryRule.fee_type === "fixed") {
          feeBase = Number(selectedDeliveryRule.fee_amount || 0);
        } else if (selectedDeliveryRule.fee_type === "percent") {
          // % of the subtotal
          feeBase = (Number(selectedDeliveryRule.fee_percent || 0) / 100) * invoiceSubtotal;
        } else if (selectedDeliveryRule.fee_type === "kilometer" && selectedDeliveryRule.distance_source === "haversine") {
          // Calculate distance: branchoffice vs selected address
          const branch = JSON.parse(localStorage.getItem('generalData'))[0].data.branchoffice || {};
          const branchCoord = { lat: Number(branch.lat), lng: Number(branch.lng) };
          const destCoord = addressNowLocation ? { lat: Number(addressNowLocation.lat), lng: Number(addressNowLocation.lng) } : null;

          let distanceKm = null;
          if (branchCoord.lat && branchCoord.lng && destCoord?.lat && destCoord?.lng) {
            // We use harversine rule (for now) to calculate the distance
            distanceKm = haversineKm(branchCoord, destCoord);
          }
          // We round the distance based on rule setting
          const calc = computeKilometerFee(selectedDeliveryRule, distanceKm ?? 0);
          feeBase = calc.fee;
        } else if (selectedDeliveryRule.fee_type === "kilometer" && selectedDeliveryRule.distance_source === "asap") {
          // Calculate ASAP fee (if applicable)
          let asapFeeResponse = await calculateAsapFee();
          //We set the fee base
          if (asapFeeResponse !== null) {
            feeBase = asapFeeResponse.fareTotal?.total || 0;
          } else {
            feeBase = 0;
          }
        } else if (selectedDeliveryRule.fee_type === "kilometer" && selectedDeliveryRule.distance_source === "tookan") {
          // Calculate Tookan fee (if applicable)
          let tookanFeeResponse = await calculateTookanFee();
          //We set the fee base
          if (tookanFeeResponse !== null) {
            feeBase = tookanFeeResponse.amount;
          } else {
            feeBase = 0;
          }
        } else if (selectedDeliveryRule.fee_type === "zone") {
          // Resolve which coverage zone the selected address falls into
          const zoneMatch = resolveZoneFee(selectedDeliveryRule, addressNowLocation);
          feeBase = zoneMatch ? Number(zoneMatch.fee || 0) : 0;
        }

        // Delivery Fee taxes (if taxable)
        if (Number(selectedDeliveryRule.taxable) === 1 && Array.isArray(selectedDeliveryRule.taxes) && selectedDeliveryRule.taxes.length > 0) {
          selectedDeliveryRule.taxes.forEach(tax => {
            const val = roundUpToTwoDecimals(feeBase * Number(tax.taxtValue || 0));
            deliveryFeeTax += val;
            //Identifier tax of the delivery
            var index = taxesArray.findIndex(function (info, i) {
              return info.tax_id === tax.tax_id
            });
            if (index < 0) {
              let taxInfoNow = {
                "dTasaITBMSValue": tax.dTasaITBMSValue,
                "percentage": tax.percentage,
                "tax": tax.tax,
                "tax_id": tax.tax_id,
                "taxtValue": tax.taxtValue,
                "total": val
              }
              taxesArray.push(taxInfoNow);
            } else {
              taxesArray[index].total = (taxesArray[index].total + val)
            }
          });
          invoiceTotalItbms += deliveryFeeTax;
        }

        invoiceDeliveryFee = feeBase;
        invoiceTotal += (invoiceDeliveryFee + deliveryFeeTax);
      }

      console.log("incoiveDeliveryFee", invoiceDeliveryFee);
      console.log("deliveryFeeTax", deliveryFeeTax);
      console.log("invoiceTotalDiscount:" + invoiceTotalDiscount);
      console.log("invoiceSubtotal:" + invoiceSubtotal);
      console.log("invoiceTotalItbms:" + invoiceTotalItbms);
      console.log("invoiceTotal:" + invoiceTotal);
      //We set the totals
      setInvoiceTotalDiscount(roundUpToTwoDecimals(invoiceTotalDiscount))
      setInvoiceSubtotal(roundUpToTwoDecimals(invoiceSubtotal));
      setInvoiceTotalItbms(roundUpToTwoDecimals(invoiceTotalItbms));
      setInvoiceTotal(roundUpToTwoDecimals(invoiceTotal));
      setTaxesArrayAll(taxesArray);
      setInvoiceDeliveryFee(roundUpToTwoDecimals(invoiceDeliveryFee));
      setInvoiceTaxDeliveryFee(roundUpToTwoDecimals(deliveryFeeTax));

      let newArray = []
      //valid discount report
      dataDicountApply.forEach((discount, index) => {
        let discountvalid = 0
        discount.groups.forEach((group) => {
          let productfind = products.find(({ group_id }) => group_id === group.group_id)
          if (productfind) {
            discountvalid = discountvalid + productfind.discount
          }
        })

        discount.combos.forEach((combo) => {
          let combofind = combos.find(({ combo_id }) => combo_id === combo.combo_id)
          if (combofind) {
            discountvalid = discountvalid + combofind.discount
          }
        })


        discount.index = index
        if (discountvalid == 0) {
          let indexRemove = index
          dataDicountApply.forEach((item, index) => {
            if (item.index != indexRemove) {
              newArray.push(item)
            }
          })

        } else {
          if (discountvalid != discount.total) {
            discount.total = discountvalid
          }
        }
      })


      if (newArray.length > 0) {
        setdataDiscountApply(newArray)
      } else {
        setdataDiscountApply(dataDicountApply)
      }
    }

  }

  /*- Register pending approval Order -*/
  const registerPendingApprovalOrder = async (detailEMVStreamResponse, paymentInfo, officialCalculationArg) => {
    paymentInfo = paymentInfo || selectedPayment || {};
    let officialCalculation = officialCalculationArg || checkoutCalculationRef.current;
    const token = undefined;

    if (!officialCalculation?.calculation_id) {
      releasePaymentLock();
      ErrorAlert("", "El calculo oficial expiro o fue consumido. Recalcula el pedido antes de pagar.", "error");
      return;
    }

    if (registerTurnOrder === 1) {
      return;
    }

    try {
      setRegisterTurnOrder(1);
      LoadingAlert("Registrando orden...", "Espere un momento por favor...");

      const branchoffice = safeParse(localStorage.getItem("branchoffice"), null);
      const machine = safeParse(localStorage.getItem("machine"), null);
      const eventData = safeParse(localStorage.getItem("dataEvent"), null);
      const yappyBtnResponseOrderParams = safeParse(localStorage.getItem("YappyBtnOrderParams"), null);

      const shoppingCartProducts = safeParse(localStorage.getItem("shoppingCart"), []) || [];
      const shoppingCartCombos = safeParse(localStorage.getItem("shoppingCartCombos"), []) || [];

      const invoiceClientData = getInvoiceClientData();

      let json = {
        user_id: 0,
        EMVStreamResponse: detailEMVStreamResponse,
        evertechPaymentReference: detailEMVStreamResponse?.evertechPaymentReference,
        // Lets the backend dedupe against a delivery order the server may have already
        // registered on its own as soon as Evertech confirmed the payment (see evertech.js).
        evertech_transaction_id: detailEMVStreamResponse?.requestId,
        transaction_code: orderNumber,
        client_name: invoiceClientData.client_name,
        id_Client: 0,
        ruc: clientData?.ruc || invoiceClientData.ruc,
        dv: clientData?.dv || invoiceClientData.dv,
        localizador: "00",
        client_phone: contact.phone || clientData?.phone || "",
        client_email: contact.email || clientData?.email || "",
        dgi_qr: "0",
        Orden: shoppingCartProducts,
        combos: shoppingCartCombos,
        calculation_id: officialCalculation.calculation_id,
        Descuento: officialCalculation.total_discount_order,
        Total: officialCalculation.total_order,
        itbms: officialCalculation.total_tax_order,
        tips: officialCalculation.tip,
        taxes: taxesArrayAll,
        subtotal: officialCalculation.sub_total_order,
        machine_id: machine?.id || 0,
        branchoffice_id: branchoffice?.id || 0,
        tipoOrden: option === "delivery" ? "delivery" : "pickngo",
        paymethod: paymentInfo.payment_id,
        paymenthod_id: paymentInfo.payment_id,
        paymethods: [],
        discounts: [],
        paymenthod_integration_id:
          paymentInfo.integration?.id ??
          paymentInfo.integration_id ??
          0,
        table_name: "",
        table_id: "",
        table_code: "",
        parthner_order: false,
        ecommerce_order: true,
        order_status: 10,
        client_id: clientData?.id || 0,
        is_discount_notification: false,
        is_cupon: false,
        is_devolution_notification: false,
        approval_pending: true,
        approval_status: "pending_manager",
        requires_approval_reason: "RULE_MATCH",
        consume_calculation: true
      };

      if (eventData) {
        json.event_id = eventData.id;
        json.event_name = eventData.name;
        json.stand_name = requireStand
          ? `${(standName || "").trim()}#${(standNumber || "").trim()}`
          : null;
      }

      if (
        Number(paymentInfo.payment_id) === 23 &&
        (Number(paymentInfo.integration?.id) === 3 || Number(paymentInfo.integration_id) === 3)
      ) {
        json.payment_info = {
          bank_name: paymentInfo.params?.find(param => param.name === "bank_name")?.value || 0,
          account_number: paymentInfo.params?.find(param => param.name === "account_number")?.value || 0,
          account_holder: paymentInfo.params?.find(param => param.name === "account_holder")?.value || 0,
          account_type: paymentInfo.params?.find(param => param.name === "account_type")?.value || 0,
        };
      }

      if (addressNowLocation && option !== "pick") {
        json.direction = {
          id: addressNowLocation.id,
          lat: addressNowLocation.lat,
          lng: addressNowLocation.lng,
          name: addressNowLocation.name || "",
          house_num: addressNowLocation.house_num || "",
          phone: addressNowLocation.phone || "",
          additional_info: addressNowLocation.additional_info || "",
          label: addressNowLocation.label || "",
          line: addressNowLocation.line || ""
        };
        json.client_address = addressNowLocation.line
          || [addressNowLocation.house_num, addressNowLocation.name].filter(Boolean).join(", ")
          || "";
      }

      if (Number(paymentInfo.payment_id) === 6 && yappyBtnResponseOrderParams) {
        json.is_yappy_pay = true;
        json.yappy_transaction_id = yappyBtnResponseOrderParams.transactionId;
        json.yappy_document_name = yappyBtnResponseOrderParams.documentName;
        json.yappy_order_token = yappyBtnResponseOrderParams.token;
      }

      if (option === "delivery") {
        json.delivery = {
          platform:
            selectedDeliveryRule?.fee_type === "kilometer" &&
              selectedDeliveryRule?.distance_source !== "haversine"
              ? selectedDeliveryRule.distance_source
              : "smartpay",
          value: officialCalculation.delivery_total,
          tax: officialCalculation.delivery_tax,
          taxes: selectedDeliveryRule?.taxes || [],
        };

        if (deliveryTiming === "scheduled" && deliveryDate && deliverySlot) {
          const startISO = new Date(`${deliveryDate}T${deliverySlot.from}:00`).toISOString();
          const endISO = new Date(`${deliveryDate}T${deliverySlot.to}:00`).toISOString();

          json.is_scheduled = true;
          json.scheduled_for = startISO;
          json.scheduled_window = { start: startISO, end: endISO };
          json.scheduled_timezone = "America/Panama";
        }

        json.tipoOrden = "delivery";
      }

      if (option === "pick") {
        if (pickDate && pickSlot) {
          const startISO = new Date(`${pickDate}T${pickSlot.from}:00`).toISOString();
          const endISO = new Date(`${pickDate}T${pickSlot.to}:00`).toISOString();

          json.is_scheduled = true;
          json.scheduled_for = startISO;
          json.scheduled_window = { start: startISO, end: endISO };
          json.scheduled_timezone = "America/Panama";
        } else {
          json.is_scheduled = false;
          json.scheduled_for = null;
          json.scheduled_window = { start: null, end: null };
          json.scheduled_timezone = "America/Panama";
        }

        json.tipoOrden = "pickngo";
      }

      const preOrderResponse = await runWithCalculationRetry(async (calculation) => {
        officialCalculation = calculation;
        applyOfficialCalculationToOrderPayload(json, calculation);
        return PreOrderFetchAPI.createPreOrder(json, token);
      });

      json.pre_order_id = preOrderResponse.data.newTempOrder._id;
      json.pre_order_code = preOrderResponse.data.newTempOrder.code;

      const approvalPayload = {
        branchoffice_id: branchoffice?.id || 0,
        order_id: orderNumber,
        metadata: json,
        platform: "ECOMMERCE",
        // Mirrors tipoOrden's check (option === "delivery" ? ... : pickup-like) instead of the
        // inverse (option === "pick" ? ...). With the inverse check, an unexpected option value
        // (e.g. "" before the selection settles) silently produced tipoOrden "pickngo" alongside
        // fulfillment_type "delivery" - a contradictory pair that leaked delivery-only UI (address
        // card, WhatsApp resend) onto Pick&Go orders. Both fields now share one source of truth.
        fulfillment_type: option === "delivery" ? "delivery" : "pickup",
        customer_name: resolveOrderClientName(invoiceClientData),
        customer_phone: json.client_phone || "",
      };

      await runWithCalculationRetry(async (calculation) => {
        officialCalculation = calculation;
        applyOfficialCalculationToOrderPayload(json, calculation);
        approvalPayload.metadata = json;
        return DeliveryOrdersFetchAPI.registerDeliveryOrder(token, approvalPayload);
      });

      setPaymentApproval({ status: 1, description: "Orden enviada para aprobación." });

      clearCheckoutPaymentStorage();
      clearDirectOrderContext();

      setStandName("");
      setStandNumber("");

      AlertSmall("Tu pedido está pendiente de aprobación", "success");

      releasePaymentLock();

      navigate(`/order/pending/${preOrderResponse.data.newTempOrder.code}`);
    } catch (error) {
      console.error(error);
      setRegisterTurnOrder(0);
      releasePaymentLock();
      ErrorAlert("No pudimos registrar la orden para aprobación", "Error", "error");
    }
  };

  /* ── Get invoice client data to send to backend ── */
  const getInvoiceClientData = () => {
    if (!wantsInvoice) {
      return {
        client_name: "CONSUMIDOR FINAL",
        ruc: 0,
        dv: 0,
        validated_by_dgi: false,
      };
    }

    if (invoiceValidatedData) {
      return invoiceValidatedData;
    }

    return {
      client_name: invoiceName.trim(),
      ruc: invoiceRuc.trim(),
      dv: invoiceDv.trim() || 0,
      validated_by_dgi: false,
    };
  };

  // Resolves the client-facing name for the order: the personalized invoice name when the
  // customer entered a RUC, otherwise the name typed in the contact step, otherwise "CONSUMIDOR FINAL".
  const resolveOrderClientName = (invoiceData) => {
    const invoiceName = (invoiceData?.client_name || "").trim();
    if (invoiceName && invoiceName !== "CONSUMIDOR FINAL") {
      return invoiceName;
    }
    return contact.name?.trim() || clientData?.name?.trim() || "CONSUMIDOR FINAL";
  };

  /* ── Handle next with validation ── */
  const handleNext = async () => {
    const cartIsAvailable = applyCartAvailabilityValidation(generalData, {
      notify: true,
      resetToCart: true
    });

    if (!cartIsAvailable) return;

    if (currentStepKey === "contact" && !validateContact()) return;

    if (currentStepKey === "stand") {
      const ok = validateStand();
      if (!ok) return;
    }

    const needsTotalsBeforeContinuing =
      currentStepKey === "cart" &&
      cartItems.length > 0 &&
      (
        totalsDirtyRef.current ||
        isCalculatingTotalsRef.current ||
        !checkoutCalculationRef.current?.calculation_id ||
        isCheckoutCalculationExpired(checkoutCalculationRef.current)
      );

    if (needsTotalsBeforeContinuing) {
      try {
        await getValidCheckoutCalculation();
      } catch (error) {
        console.error(error);
        ErrorAlert("", error?.message || "No se pudo calcular el total oficial del pedido.", "error");
        releasePaymentLock();
        return;
      }
    }

    if (currentStepKey === "invoice") {
      if (!wantsInvoice) {
        goNext();
        return;
      }

      if (!invoiceStepValidated) {
        const ok = await validateInvoiceWithDgi();
        if (!ok) return;
        return;
      }

      goNext();
      return;
    }

    if (currentStepKey === "payment") {
      if (paymentInFlightRef.current || isSubmittingPayment) {
        return;
      }

      await withPaymentLock(async () => {
        /* const ok = await validateInvoiceWithDgi();
        if (!ok) {
          releasePaymentLock();
          return;
        } */

        await processSelectedPayment();
      });

      return;
    }

    goNext();
  };

  /* ── Save new address ── */
  const handleSaveAddress = async (addr) => {
    const normalizedAddress = normalizeAddressForCheckout(addr);

    localStorage.setItem("addressNowLocation", JSON.stringify(normalizedAddress));
    setSelectedAddress(normalizedAddress.id);
    setAddressNowLocation(normalizedAddress);
    setAddresses(prev => {
      const exists = prev.some(item => String(item.id) === String(normalizedAddress.id));

      if (exists) {
        return prev.map(item =>
          String(item.id) === String(normalizedAddress.id) ? normalizedAddress : item
        );
      }

      return [...prev, normalizedAddress];
    });

    setShowAddressForm(false);

    if (clientData?.id) {
      await getAddressClientNow(clientData.id);
    }
    console.log("la direccion es", addresses)
  };


  /* ── Totals ── */
  const hasValidCalculation = Boolean(calculationId) && !isCheckoutCalculationExpired({ expires_at: calculationExpiresAt });
  const totalsNeedRefresh = totalsDirty || isCalculatingTotals || !hasValidCalculation;
  const subtotal = checkoutTotals?.subtotal ?? checkoutCalculation?.sub_total_order ?? invoiceSubtotal;
  const shipCost = checkoutTotals?.delivery ?? checkoutCalculation?.delivery_total ?? invoiceDeliveryFee;
  const totalItbms = checkoutTotals?.itbms ?? checkoutCalculation?.total_tax_order ?? invoiceTotalItbms;
  const totalDiscount = checkoutTotals?.discount ?? checkoutCalculation?.total_discount_order ?? invoiceTotalDiscount;
  const tip = checkoutTotals?.tip ?? checkoutCalculation?.tip ?? invoiceTip;
  const total = checkoutTotals?.total ?? checkoutCalculation?.total_order ?? (invoiceTotal + invoiceTip);

  /* ── Success screen ── */
  if (hasActiveTableSession && tableOrderConfirmation) {
    return (
      <TableOrderConfirmation
        tableLabel={tableLabel}
        orderStatus={tableOrderConfirmation.status || tableOrderStatus}
        acceptedCount={tableOrderConfirmation.acceptedCount || acceptedTableItems.length}
        onBackToMenu={() => navigate("/menu")}
        onViewTableOrder={() => setTableOrderConfirmation(null)}
      />
    );
  }

  if (finished) {
    const orderId = '#DL-2024-' + Math.floor(Math.random() * 9000 + 1000);
    return (
      <div className="ck-root">
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          {/* <div className="ck-header">
            <p className="ck-logo">Enterprise</p>
          </div> */}
          <div className="ck-card">
            <div className="ck-success">
              <div className="ck-success-icon">🎉</div>
              <p className="ck-success-title">{t('checkout.orderConfirmed')}</p>
              <p className="ck-success-sub">
                {t('checkout.orderConfirmedSubtitlePrefix')} <strong>{contact.email}</strong> {t('checkout.orderConfirmedSubtitleSuffix')}
              </p>
              <p className="ck-success-id">{orderId}</p>
              <div className="ck-actions" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                <button
                  className="ck-btn-primary"
                  onClick={() => {
                    setFinished(false);
                    setCurrentStep(0);
                    setDeliveryMethod("");
                    setWhenDelivery("");
                    setSelectedDate(null);
                    setSelectedTime("");
                    setSelectedAddress("");
                    setSelectedPayment(payments[0] || null);
                  }}
                >
                  {t('checkout.viewMyOrders')} <IcoArrow />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasDeliveryOption =
    deliveryFeeRule.length > 0 || deliveryFeeRuleSchedule.length > 0;

  const hasPickOption = !!selectedPickAndGoRule;
  const conn = {
    online: { dot: "#16A34A", text: "Sincronizado en tiempo real" },
    reconnecting: { dot: "#D97706", text: "Reconectando…" },
    offline: { dot: "#DC2626", text: "Sin conexión" },
  };

  /* ── Render step content ── */
  const renderStep = () => {
    switch (currentStepKey) {

      /* ── STEP: CART ── */
      case 'cart':
        return (
          <div>

            <div className={`d-flex justify-content-between align-items-center w-100${hasActiveTableSession ? " rcv-x-cart-head" : ""}`}>
              {!hasActiveTableSession ? <p className="ck-card-title">{t('cart.yourCart')} </p> : null}

              <div>
                {cartItems.length > 0
                  ? <>
                    <button className={hasActiveTableSession ? 'rcv-x-empty' : 'ck-btn-back py-1 px-2 border-0'} onClick={() => {
                      localStorage.removeItem("shoppingCart");
                      localStorage.removeItem("shoppingCartCombos");
                      navigate("/");
                    }} role='button'>
                      <i className="uil uil-trash"></i> {t('checkout.emptyCart')}
                    </button>
                  </>
                  : <>
                  </>}

              </div>
            </div>





            {hasActiveTableSession && (

              <>


                {/* <div className="mb-3 border border-danger rounded p-2">
                  <div className="alert alert-success py-2 mb-2">
                    {t('checkout.tableActiveSession', { table: tableLabel })} xdddd
                  </div>
                  {tableAreaName ? (
                    <div className="alert alert-light py-2 mb-2">
                      {tableAreaName ? <div>{t('checkout.environmentLabel', { name: tableAreaName })}</div> : null} frtyy
                    </div>
                  ) : null}
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <span className={`badge ${socketConnected ? "bg-success" : "bg-secondary"}`}>
                      {socketConnected ? "Actualizaciones en tiempo real" : "Reconectando pedido"} jujujujujujuj
                    </span>
                    {participant?.role === "PRIMARY" ? (
                      <button className="btn btn-outline-dark btn-sm" type="button" onClick={openTableInvite}>
                        Invitar a la mesa xdexde
                      </button>
                    ) : null}
                  </div>
                  {tableOrderNotice ? <div className="alert alert-info py-2 mb-2">{tableOrderNotice} aaa</div> : null}
                  {tableOrderWarning ? <div className="alert alert-warning py-2 mb-2">{tableOrderWarning} bbb</div> : null}
                  {lastMutationError ? <div className="alert alert-warning py-2 mb-2">{lastMutationError.message} cc</div> : null}
                </div> */}

                {/* La tarjeta de la mesa ahora se renderiza dentro de .rcv-x-layout (más abajo) */}
              </>
            )}

            {/* <TuPedido /> */}

            <Modal
              show={showTableInvite}
              onHide={() => setShowTableInvite(false)}
              centered
              dialogClassName="ck-table-invite-modal"
            >
              <Modal.Header closeButton><Modal.Title>Invitar a la Mesa {tableLabel}</Modal.Title></Modal.Header>
              <Modal.Body>
                {inviteLoading && !currentInvite ? <p className="mb-0">Generando invitacion...</p> : null}
                {inviteError ? <div className="alert alert-warning">{inviteError}</div> : null}
                {currentInvite && inviteExpiresIn > 0 ? (
                  <div className="text-center">
                    <p>Escanea este codigo desde el otro telefono.</p>
                    {/* join_url comes from the backend's TABLE_SESSION_PUBLIC_BASE_URL/ECOMMERCE_URL
                        env var and falls back to a bare relative path ("/table/join/<token>") when
                        that isn't set - useless in a QR, which needs an absolute URL to open. This
                        browser tab is already on the right host (custom domain, subdomain, whatever
                        resolved to load this page), so build the absolute link from it directly
                        instead of trusting the backend's env-var guess. */}
                    {/* colorScheme: "light" evita que el "oscurecer paginas web" de Android/Chrome
                        recoloree el SVG del QR (negro sobre blanco) y lo deje gris/ilegible en
                        algunos telefonos. */}
                    <div className="d-inline-block bg-white p-3 border rounded" style={{ colorScheme: "light" }}>
                      <QRCode value={`${window.location.origin}/table/join/${currentInvite.join_token}`} size={220} />
                    </div>
                    <p className="text-muted mt-3 mb-0">Expira en {inviteCountdown}</p>
                  </div>
                ) : currentInvite ? <div className="alert alert-warning mb-0">Esta invitacion expiro. Genera una nueva para continuar.</div> : null}
              </Modal.Body>
              <Modal.Footer>
                {currentInvite ? <button className="btn btn-outline-danger" type="button" disabled={inviteLoading} onClick={() => revokeSessionInvite(currentInvite.invite_uuid)}>Cancelar invitacion</button> : null}
                {!currentInvite || inviteExpiresIn === 0 ? <button className="btn btn-dark" type="button" disabled={inviteLoading} onClick={createSessionInvite}>Generar invitacion</button> : null}
              </Modal.Footer>
            </Modal>

            <Modal
              show={showDonleeModal}
              onHide={() => setShowDonleeModal(false)}
              centered
              dialogClassName="ck-donlee-modal"
            >
              <Modal.Header closeButton><Modal.Title>Paisanitos</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted small mb-2">
                  Ingresa tu numero de Paisanitos para vincular tus puntos a esta cuenta.
                </p>
                <div className="d-flex gap-2">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Ingrese numero..."
                    value={donleePhoneDraft}
                    onChange={(e) => setDonleePhoneDraft(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={donleeLoading}
                    onClick={validateDonleePhone}
                  >
                    {donleeLoading ? "..." : "Validar"}
                  </button>
                </div>

                {donleeClientInfo === false ? (
                  <div className="alert alert-warning mt-3 mb-0 py-2 small">No se encontraron datos</div>
                ) : null}

                {donleeClientInfo ? (
                  <div className="alert alert-success mt-3 mb-0 py-2 small">
                    <div><strong>Nombre:</strong> {donleeClientInfo.name}</div>
                    <div><strong>Telefono:</strong> {donleeClientInfo.phone}</div>
                    {donleeClientInfo.points !== undefined ? <div><strong>Puntos:</strong> {donleeClientInfo.points}</div> : null}
                  </div>
                ) : null}
              </Modal.Body>
              <Modal.Footer>
                {donleePhone ? (
                  <button className="btn btn-outline-danger" type="button" onClick={clearDonleePhone}>Quitar</button>
                ) : null}
                <button className="btn btn-dark" type="button" onClick={() => setShowDonleeModal(false)}>Listo</button>
              </Modal.Footer>
            </Modal>

            {/* ── PERSONALIZAR FACTURA ── */}
            {collaborativeOrder?.totals?.total !== undefined ? (
              <div className="rcv-x-invoice">
                <div className="rcv-x-invoice-info">
                  <span className="rcv-x-invoice-label">Factura</span>
                  {savedTableInvoice ? (
                    <>
                      <strong className="rcv-x-invoice-name">{savedTableInvoice.client_name}</strong>
                      {savedTableInvoice.ruc ? (
                        <span className="rcv-x-invoice-sub">
                          RUC/Cédula {savedTableInvoice.ruc}{savedTableInvoice.dv ? ` DV ${savedTableInvoice.dv}` : ""}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="rcv-x-invoice-sub">Se emitirá a consumidor final</span>
                  )}
                </div>
                <button type="button" className="rcv-x-invoice-btn" onClick={openTableInvoice}>
                  {savedTableInvoice ? "Editar" : "Personalizar factura"}
                </button>
              </div>
            ) : null}

            <CustomizeTableInvoiceModal
              open={showTableInvoice}
              saved={savedTableInvoice}
              onClose={closeTableInvoice}
              onSave={saveTableInvoice}
              onRemove={removeTableInvoice}
            />


            {/* Waiter-controlled payment gate: reachable only once payment_authorized is true (the
                Pagar button is disabled otherwise - see TableRestaurantPaymentSummary). Picking a
                method here and pressing "Continuar" hands off to TableSessionPayment - a fully
                isolated module (see that file's header comment) that runs the real gateway charge
                and closes the table's account, without touching this modal's own state further. */}
            <Modal
              show={showTablePaymentMethods}
              onHide={() => setShowTablePaymentMethods(false)}
              centered
              dialogClassName="ck-table-payment-modal"
            >
              <Modal.Header closeButton><Modal.Title>Metodos de pago - Mesa {tableLabel}</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted small">
                  {paymentLineUuidsForModal
                    ? "Elige como quieres pagar los productos seleccionados:"
                    : tableSession?.payment_mode === "PRE_PAY"
                      ? "Elige como quieres pagar:"
                      : "El mesero habilito el pago para tu mesa. Elige como quieres pagar:"}
                </p>
                <PaymentStep
                  methods={
                    // "Pagar con POS" only makes sense when the customer is paying a specific
                    // selection of products from an active prepay table (not the whole account,
                    // and not the regular delivery/pickup cart, which reuses this same `payments`
                    // list) - see TableSessionPayment.jsx#isPosPayment for the dispatch side.
                    isPrepayTable && paymentLineUuidsForModal
                      ? [...payments.filter((method) => !isTilopayPayment(method)), { payment_id: "POS_PHYSICAL", payment: "Pagar con POS", integration: { id: 0, name: "POS_PHYSICAL" } }]
                      : payments.filter((method) => !isTilopayPayment(method))
                  }
                  selected={
                    selectedTablePayment
                      ? `${selectedTablePayment.payment_id}-${selectedTablePayment.integration?.id || selectedTablePayment.integration_id || 0}`
                      : ""
                  }
                  onSelect={setSelectedTablePayment}
                />
                <div style={{ padding: '.75rem 1rem', background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)', marginTop: '.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem', fontWeight: 600 }}>
                    <span>{paymentLineUuidsForModal ? "Total seleccionado" : "Total de la mesa"}</span>
                    <span>{fmt(paymentLineUuidsForModal ? selectedPaymentSubtotal : (collaborativeOrder?.totals?.total ?? 0))}</span>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button className="btn btn-outline-secondary" type="button" onClick={() => setShowTablePaymentMethods(false)}>Cerrar</button>
                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={!selectedTablePayment}
                  onClick={() => {
                    setShowTablePaymentMethods(false);
                    setShowTableGatewayPayment(true);
                  }}
                >
                  Continuar
                </button>
              </Modal.Footer>
            </Modal>

            <TableSessionPayment
              show={showTableGatewayPayment}
              payment={selectedTablePayment}
              order={collaborativeOrder}
              paymentMode={tableSession?.payment_mode}
              tableLabel={tableLabel}
              customerName={tableSession?.customer?.name}
              customerEmail={tableSession?.customer?.email}
              clientPhone={isDonleeBusiness ? donleePhone : ""}
              lineUuids={paymentLineUuidsForModal}
              onClose={() => {
                setShowTableGatewayPayment(false);
                setPaymentLineUuidsForModal(null);
              }}
              onSuccess={(result, { partial } = {}) => {
                setShowTableGatewayPayment(false);
                setSelectedTablePayment(null);
                setPaymentLineUuidsForModal(null);
                if (partial) {
                  // Partial payment: the account stays open (no session close, no final order yet) -
                  // just clear the selection and let the shared-order snapshot (already applied by
                  // TableSessionContext) reflect the newly paid lines.
                  setSelectedPaymentLineUuids([]);
                  AlertSmall("Pago parcial confirmado.", "success");
                  return;
                }
                AlertSmall(
                  tableSession?.payment_mode === "PRE_PAY"
                    ? "Pago confirmado. Tu pedido ya se envio a cocina."
                    : "Cuenta cerrada. Gracias por tu visita.",
                  "success"
                );
                // Same destination the rest of the app lands on after any successful payment
                // (see e.g. the Yappy "yappyorder" socket handler above) - without this the customer
                // was left staring at the now-empty table cart instead of an order confirmation.
                if (result?.order_code) {
                  navigate(`/order/${result.order_code}`);
                }
              }}
            />

            {/* Mobile cart banner */}

            {!hasActiveTableSession && (
              <div className=" d-block">
                <MobileCartBanner
                  items={cartItems}
                  deliveryMethod={deliveryMethod}
                  subtotal={subtotal}
                  itbms={totalItbms}
                  shipCost={shipCost}
                  total={total}
                  onOpen={() => setCartModalOpen(true)}
                />
              </div>
            )}

            {hasActiveTableSession ? (
              <div className="ck-cart-items d-block mb-3 d-none ">
                <p className="ck-section-label">Pedido compartido </p>
                <p className="text-muted small">{participants.length || 1} participante{(participants.length || 1) === 1 ? "" : "s"} · Mesa {tableLabel}</p>
                {tableOrderLocked ? (
                  <div className="alert alert-info py-2 mb-2">{t('checkout.accountClosingNote')}</div>
                ) : (
                  <p className="text-muted small fst-italic">{t('checkout.editableUntilKitchenNote')}</p>
                )}
                <RenderIf isTrue={orderedSharedLines.length > 0}>
                  {
                    orderedSharedLines.map((line) => {
                      const isAccepted = line.kitchen_status === "COOKING";
                      return (
                        <div className="co-item" key={line.key}>
                          {isAccepted ? (
                            <div className="co-thumb-ph border"><i className="bi bi-check2-circle" /></div>
                          ) : isPrepayTable && line.claimable && line.line_status !== "DRAFT" ? (
                            <div className="co-thumb-ph border d-flex align-items-center justify-content-center">
                              <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={selectedPaymentLineUuids.includes(line.line_uuid)}
                                onChange={() => togglePaymentLineSelection(line.line_uuid)}
                                title="Seleccionar para pagar"
                              />
                            </div>
                          ) : (
                            <div className="co-thumb-ph border"><i className="bi bi-bag" /></div>
                          )}
                          <div className="co-item-body">
                            <div className="co-item-top">
                              <span className="ck-item-name">{sharedLineName(line)}</span>
                              {isAccepted ? (
                                isPrepayTable ? (
                                  <span className="badge bg-success text-white">Pagado</span>
                                ) : (
                                  <span className="badge bg-success text-white">{t('checkout.sentToKitchen')}</span>
                                )
                              ) : isPrepayTable && line.payment_status === "PAYMENT_PENDING" ? (
                                <span className="d-flex align-items-center gap-2">
                                  <span className="badge bg-warning text-dark">Cobrando</span>
                                  {line.payment_pending_id ? (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-danger"
                                      disabled={cancelingPendingLineId === line.payment_pending_id}
                                      onClick={() => handleCancelPendingLinePayment(line)}
                                    >
                                      {cancelingPendingLineId === line.payment_pending_id ? "Cancelando..." : "Cancelar"}
                                    </button>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="badge bg-secondary text-white">{t('checkout.pendingStatus')}</span>
                              )}
                            </div>
                            <span className="ck-item-desc">
                              {isAccepted
                                ? (isPrepayTable ? "" : (line.note || ""))
                                : (line.is_owner ? "Tus productos" : (line.participant_display_name || line.display_name || "Otro participante"))}
                            </span>
                            <div className="co-controls">
                              {!isAccepted && line.editable && !tableOrderLocked ? (
                                <div className="d-flex align-items-center gap-2">
                                  <div className="co-qty">
                                    <button className="co-qty-btn" type="button" onClick={() => changeSharedLineQuantity(line, -1)}>−</button>
                                    <span className="co-qty-num">{sharedLineQuantity(line)}</span>
                                    <button className="co-qty-btn" type="button" onClick={() => changeSharedLineQuantity(line, 1)}>+</button>
                                  </div>
                                  <button
                                    className="btn btn-link text-danger p-0"
                                    type="button"
                                    title="Eliminar producto"
                                    onClick={() => deleteSharedTableLine(line)}
                                  >
                                    <i className="bi bi-trash" />
                                  </button>
                                </div>
                              ) : <span>{sharedLineQuantity(line)} unidad{sharedLineQuantity(line) === 1 ? "" : "es"}</span>}
                              {line.total !== undefined ? <span className="ck-item-price">{fmt(line.total)}</span> : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </RenderIf>
                {collaborativeOrder?.totals?.total !== undefined ? <div className="fw-bold text-end mt-2">Total de la mesa: {fmt(collaborativeOrder.totals.total)}</div> : null}
                {isPrepayTable && claimablePendingLines.length > 0 ? (
                  <div className="ck-prepay-payment-actions mt-3 pt-2 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">{selectedPaymentLineUuids.length} producto{selectedPaymentLineUuids.length === 1 ? "" : "s"} seleccionado{selectedPaymentLineUuids.length === 1 ? "" : "s"}</span>
                      <span className="fw-bold">{fmt(selectedPaymentSubtotal)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-dark w-100 mt-2"
                      disabled={selectedPaymentLineUuids.length === 0}
                      onClick={openPaySelectedLines}
                    >
                      Pagar seleccionados
                    </button>
                  </div>
                ) : null}
                {isPrepayTable ? (
                  <button
                    type="button"
                    className="btn btn-outline-dark w-100 mt-2"
                    disabled={isFinalizingAccount}
                    onClick={handleFinalizeAccount}
                  >
                    {isFinalizingAccount ? "Finalizando..." : "Finalizar cuenta"}
                  </button>
                ) : null}
              </div>
            ) : null}
            {/* ── 2. LAYOUT MESA: columna principal + resumen de pago ───────── */}
            {hasActiveTableSession ? (
              <div className={`rcv-x-layout${isPrepayTable ? " rcv-x-layout-split" : ""}`}>
                <div className="rcv-x-main">
                  {/* ── 1. INFORMACIÓN DE LA MESA ───────────────────────────────── */}
                  <section className="rcv-x-table-card">
                    <div className="rcv-x-table-head">
                      <span className="rcv-x-table-glyph" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18M5 9v10M19 9v10M8 9v5h8V9M12 5v4" /></svg>
                      </span>
                      <div className="rcv-x-table-id">
                        <h2 className="rcv-x-table-name">Mesa {tableLabel}</h2>
                        <p className="rcv-x-table-sub">
                          {participants.length || 1} participante{(participants.length || 1) === 1 ? "" : "s"}
                        </p>
                        {tableAreaName ? (
                          <span className="rcv-x-chip">
                            {t('checkout.environmentLabel', { name: tableAreaName })}
                          </span>
                        ) : null}
                      </div>
                      <p
                        className={`rcv-x-sync ${socketConnected ? "rcv-x-sync-live" : "rcv-x-sync-wait"}`}
                        role="status"
                        aria-live="polite"
                      >
                        <span className="rcv-x-dot" aria-hidden="true" />
                        {socketConnected ? "Activa" : "Reconectando"}
                      </p>
                    </div>

                    {(participant?.role === "PRIMARY" || isDonleeBusiness) ? (
                      <div className="rcv-x-table-foot">
                        {participant?.role === "PRIMARY" ? (
                          <button
                            className="rcv-x-btn rcv-x-btn-ghost"
                            type="button"
                            onClick={openTableInvite}
                          >
                            <i className="bi bi-plus-lg" aria-hidden="true" /> Invitar a la mesa
                          </button>
                        ) : null}

                        {isDonleeBusiness ? (
                          <button
                            className={`rcv-x-btn rcv-x-btn-ghost${donleePhone ? " rcv-x-btn-linked" : ""}`}
                            type="button"
                            onClick={() => setShowDonleeModal(true)}
                          >
                            {donleePhone ? <><i className="bi bi-check2" aria-hidden="true" /> Paisanitos: {donleePhone}</> : <><i className="bi bi-plus-lg" aria-hidden="true" /> Paisanitos</>}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {(tableOrderNotice || tableOrderWarning || lastMutationError) ? (
                      <div className="rcv-x-notes">
                        {tableOrderNotice ? (
                          <p className="rcv-x-note rcv-x-note-info">
                            <span className="rcv-x-dot" />
                            {tableOrderNotice}
                          </p>
                        ) : null}

                        {tableOrderWarning ? (
                          <p className="rcv-x-note rcv-x-note-warn">
                            <span className="rcv-x-dot" />
                            {tableOrderWarning}
                          </p>
                        ) : null}

                        {lastMutationError ? (
                          <p className="rcv-x-note rcv-x-note-warn">
                            <span className="rcv-x-dot" />
                            {lastMutationError.message}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </section>

                  {/* ── PEDIDO COMPARTIDO ── */}
                  <section className="rcv-x-section">
                    <div className="rcv-x-section-head">
                      <h3 className="rcv-x-section-title">Tu pedido</h3>
                      <span className="rcv-x-section-meta">
                        {orderedSharedLines.length} producto{orderedSharedLines.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    {isPrepayTable && claimablePendingLines.length > 0 ? (
                      <p className={`rcv-x-selcount${selectedPaymentLineUuids.length > 0 ? " is-on" : ""}`} aria-live="polite">
                        {selectedPaymentLineUuids.length} seleccionado{selectedPaymentLineUuids.length === 1 ? "" : "s"}
                      </p>
                    ) : null}

                    {tableOrderLocked ? (
                      <p className="rcv-x-note rcv-x-note-info">
                        <span className="rcv-x-dot" aria-hidden="true" />
                        {t('checkout.accountClosingNote')}
                      </p>
                    ) : (
                      <p className="rcv-x-hint">{t('checkout.editableUntilKitchenNote')}</p>
                    )}

                    <RenderIf isTrue={orderedSharedLines.length > 0}>
                      <ul className="rcv-x-list">
                        {orderedSharedLines.map((line) => {
                          const isAccepted = line.kitchen_status === "COOKING";
                          // En mesas pre-pago, kitchen_status==="COOKING" normalmente SOLO pasa porque la
                          // linea se pago (confirmPaymentClaim/beginPrepayPayment son los unicos que mueven
                          // kitchen_action a send_to_kitchen alli) - pero el POS tambien tiene un boton
                          // manual de "Enviar a cocina" (pensado para post-pago) que un mesero puede usar
                          // igual en una mesa pre-pago (ej. para probar la impresora), lo que deja la linea
                          // en send_to_kitchen SIN que se haya cobrado. "Pagado" tiene que depender del pago
                          // real (payment_status), nunca de si ya se envio a cocina.
                          const isPaid = line.payment_status === "PAID";
                          const isSelectable = isPrepayTable && line.claimable && line.line_status !== "DRAFT";
                          const isSelected = selectedPaymentLineUuids.includes(line.line_uuid);
                          const isEditable = !isAccepted && line.editable && !tableOrderLocked;

                          return (
                            <li
                              className={`rcv-x-item${isSelectable ? " rcv-x-item-selectable" : ""}${isSelected ? " rcv-x-item-sel" : ""}`}
                              key={line.key}
                            >
                              {isSelectable ? (
                                <label className="rcv-x-check">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => togglePaymentLineSelection(line.line_uuid)}
                                    title="Seleccionar para pagar"
                                  />
                                  <span className="rcv-x-check-box" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
                                  </span>
                                </label>
                              ) : null}

                              <div className="rcv-x-thumb" aria-hidden="true">
                                <i className={(isPrepayTable ? isPaid : isAccepted) ? "bi bi-check2-circle" : "bi bi-bag"} />
                              </div>

                              <div className="rcv-x-item-body">
                                <p className="rcv-x-item-name">{sharedLineName(line)}</p>

                                <div className="rcv-x-item-meta">
                                  <p className="rcv-x-item-author">
                                    {(isPrepayTable ? isPaid : isAccepted)
                                      ? (isPrepayTable ? "" : (line.note || ""))
                                      : (line.is_owner ? "Tus productos" : (line.participant_display_name || line.display_name || "Otro participante"))}
                                  </p>

                                  {isPrepayTable ? (
                                    isPaid ? (
                                      <p className="rcv-x-status rcv-x-status-done">
                                        <span className="rcv-x-dot" aria-hidden="true" />
                                        Pagado
                                      </p>
                                    ) : line.payment_status === "PAYMENT_PENDING" ? (
                                      <p className="rcv-x-status rcv-x-status-busy">
                                        <span className="rcv-x-dot" aria-hidden="true" />
                                        Cobrando
                                        {line.payment_pending_id ? (
                                          <button
                                            type="button"
                                            className="rcv-x-link-danger"
                                            disabled={cancelingPendingLineId === line.payment_pending_id}
                                            onClick={() => handleCancelPendingLinePayment(line)}
                                          >
                                            {cancelingPendingLineId === line.payment_pending_id ? "Cancelando..." : "Cancelar"}
                                          </button>
                                        ) : null}
                                      </p>
                                    ) : (
                                      <p className="rcv-x-status rcv-x-status-wait">
                                        <span className="rcv-x-dot" aria-hidden="true" />
                                        {t('checkout.pendingStatus')}
                                      </p>
                                    )
                                  ) : isAccepted ? (
                                    <p className="rcv-x-status rcv-x-status-done">
                                      <span className="rcv-x-dot" aria-hidden="true" />
                                      {t('checkout.sentToKitchen')}
                                    </p>
                                  ) : (
                                    <p className="rcv-x-status rcv-x-status-wait">
                                      <span className="rcv-x-dot" aria-hidden="true" />
                                      {t('checkout.pendingStatus')}
                                    </p>
                                  )}
                                </div>

                                {line.discount_request?.status === "PENDING" ? (
                                  <p className="rcv-x-dsc rcv-x-dsc-wait">Descuento solicitado · en revisión</p>
                                ) : line.discount_request?.status === "APPROVED" ? (
                                  <p className="rcv-x-dsc rcv-x-dsc-ok">
                                    Descuento aplicado{line.discount_request.discount_name ? `: ${line.discount_request.discount_name}` : ""}
                                    {line.discount_request.rate ? ` (${line.discount_request.rate}%)` : ""}
                                  </p>
                                ) : line.discount_request?.status === "REJECTED" ? (
                                  <p className="rcv-x-dsc rcv-x-dsc-no">
                                    Descuento no aprobado{line.discount_request.rejection_note ? `: ${line.discount_request.rejection_note}` : ""}
                                  </p>
                                ) : null}
                                <div className="rcv-x-item-tags">
                                  {line.can_request_discount ? (
                                    <button type="button" className="rcv-x-dsc-link" onClick={() => openDiscountRequest(line)}>
                                      <i className="bi bi-tag" aria-hidden="true" />
                                      {line.discount_request?.status === "REJECTED" ? "Volver a pedir descuento" : "Pedir descuento"}
                                    </button>
                                  ) : null}

                                  {/* Elegido por producto al agregarlo (ver Segments.jsx) - siempre visible,
                                  no solo dentro de "Detalles", para que no haya que adivinar si algo
                                  quedo para llevar. */}
                                  <span className={`rcv-x-delivery-chip${line.delivery_type === "para llevar" ? " rcv-x-delivery-chip-togo" : ""}`}>
                                    <i className={line.delivery_type === "para llevar" ? "bi bi-bag" : "bi bi-cup-hot"} aria-hidden="true" />
                                    {line.delivery_type === "para llevar" ? "Para llevar" : "Comer aquí"}
                                  </span>

                                  {/* Modificadores elegidos (ej. "Ingrediente Porcion Chica arroz: Pollo") -
                                  colapsado por defecto, el cliente lo abre tocando "Detalles" si quiere
                                  confirmar que selecciono. Sin esto no habia forma de ver que se eligio
                                  una vez agregado al pedido, mismo problema que tenia el POS. */}
                                  {Array.isArray(line.segments) && line.segments.length > 0 ? (
                                    <details className="rcv-x-segments">
                                      <summary className="rcv-x-segments-toggle">
                                        <i className="bi bi-info-circle" /> Detalles
                                      </summary>
                                      <ul className="rcv-x-segments-list">
                                        {line.segments.map((seg, segIndex) => (
                                          <li key={`${line.key}-seg-${segIndex}`}>
                                            <span className="rcv-x-segments-label">
                                              {seg.segment_name ? `${seg.segment_name}: ` : ""}{seg.name}
                                            </span>
                                            {Number(seg.price) > 0 ? (
                                              <span className="rcv-x-segments-price">{fmt(Number(seg.price) * Number(seg.amount || 1))}</span>
                                            ) : null}
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  ) : null}
                                </div>

                                <div className="rcv-x-item-foot">
                                  {isEditable ? (
                                    <div className="rcv-x-qty-group">
                                      <div className="rcv-x-stepper">
                                        <button
                                          className="rcv-x-stepper-btn"
                                          type="button"
                                          aria-label="Disminuir cantidad"
                                          onClick={() => changeSharedLineQuantity(line, -1)}
                                        ><i className="bi bi-dash-lg" aria-hidden="true" /></button>
                                        <span className="rcv-x-stepper-num">{sharedLineQuantity(line)}</span>
                                        <button
                                          className="rcv-x-stepper-btn"
                                          type="button"
                                          aria-label="Aumentar cantidad"
                                          onClick={() => changeSharedLineQuantity(line, 1)}
                                        ><i className="bi bi-plus-lg" aria-hidden="true" /></button>
                                      </div>
                                      <button
                                        className="rcv-x-remove"
                                        type="button"
                                        title="Eliminar producto"
                                        onClick={() => deleteSharedTableLine(line)}
                                      >
                                        <i className="bi bi-trash3" aria-hidden="true" />
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="rcv-x-locked">
                                      {sharedLineQuantity(line)} unidad{sharedLineQuantity(line) === 1 ? "" : "es"}
                                    </span>
                                  )}

                                </div>
                              </div>
                              {line.total !== undefined ? (
                                <span className="rcv-x-price">
                                  {fmt(line.total)}
                                  {Number(line.tax_total) > 0 ? (
                                    <small className="rcv-x-price-tax">Incluye ITBMS {fmt(line.tax_total)}</small>
                                  ) : null}
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </RenderIf>

                    {discountRequestLine ? (
                      <div className="rcv-x-dsc-overlay" role="presentation" onClick={closeDiscountRequest}>
                        <div className="rcv-x-dsc-modal" role="dialog" aria-modal="true" aria-label="Pedir descuento" onClick={(e) => e.stopPropagation()}>
                          <h4 className="rcv-x-dsc-title">Pedir descuento</h4>
                          <p className="rcv-x-dsc-text">
                            <strong>{sharedLineName(discountRequestLine)}</strong>
                            <br />
                            El personal revisará tu solicitud y, si la aprueba, el descuento se aplicará a este producto.
                          </p>
                          <div className="rcv-x-dsc-chips">
                            {DISCOUNT_REASON_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={`rcv-x-dsc-chip${discountReason === option ? " active" : ""}`}
                                onClick={() => setDiscountReason(option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            className="rcv-x-dsc-input"
                            maxLength={120}
                            placeholder="Motivo (opcional)"
                            value={discountReason}
                            onChange={(e) => setDiscountReason(e.target.value)}
                          />
                          <div className="rcv-x-dsc-actions">
                            <button type="button" className="rcv-x-dsc-btn" onClick={closeDiscountRequest} disabled={isSubmittingDiscount}>Cancelar</button>
                            <button type="button" className="rcv-x-dsc-btn rcv-x-dsc-btn-primary" onClick={submitDiscountRequest} disabled={isSubmittingDiscount}>
                              {isSubmittingDiscount ? "Enviando..." : "Enviar solicitud"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </section>

                  {hasActiveTableSession && tableOrderLocked ? (
                    <div className="alert alert-info mt-4 mb-0">{t('checkout.accountClosingNote')}</div>
                  ) : hasActiveTableSession ? (
                    <div className="rcv-x-add-wrap">
                      {!isPrepayTable && cartItems.length > 0 ? (
                        <button
                          className="ck-btn-primary"
                          onClick={submitCartToTable}
                          disabled={isSubmittingTableOrder || tableOrderLoading}
                          type="button"
                        >
                          {isSubmittingTableOrder ? t('checkout.sending') : "Agregar productos pendientes"}
                        </button>
                      ) : null}
                      {/* <button className="ck-btn-add-more" onClick={() => navigate("/menu")} type="button">
                      {t('checkout.addMoreProducts')} 
                    </button> */}
                      <AddMoreButton
                        label={t('checkout.addMoreProducts')}
                        onClick={() => navigate("/menu")}
                      />
                    </div>
                  ) : null}
                </div>

                {/* ── RESUMEN DE PAGO ── */}
                {(collaborativeOrder?.totals?.total !== undefined || isPrepayTable) ? (
                  <aside className="rcv-x-side">
                    <div className="rcv-x-pay">
                      <div className="rcv-x-pay-body">
                        <h3 className="rcv-x-pay-title">Resumen de pago</h3>

                        {/* ── RESUMEN ── */}
                        {collaborativeOrder?.totals?.total !== undefined ? (
                          <div className="rcv-x-summary">
                            <div className="rcv-x-row">
                              <span>Total de la mesa</span>
                              <span className="rcv-x-row-amount">{fmt(collaborativeOrder.totals.total)}</span>
                            </div>
                          </div>
                        ) : null}

                        {isPrepayTable && claimablePendingLines.length > 0 ? (
                          <>
                            <div className="rcv-x-row">
                              <span>Productos seleccionados ({selectedPaymentLineUuids.length})</span>
                              <span className="rcv-x-row-amount">{fmt(selectedPaymentSubtotal)}</span>
                            </div>
                            <hr className="rcv-x-divider" />
                            <div className="rcv-x-total">
                              <span className="rcv-x-total-label">Total a pagar</span>
                              <span className="rcv-x-total-amount" aria-live="polite">{fmt(selectedPaymentSubtotal)}</span>
                            </div>


                          </>
                        ) : null}
                      </div>

                      {/* ── ACCIONES ── */}
                      {isPrepayTable && claimablePendingLines.length > 0 ? (
                        <div className="rcv-x-actions rounded-3">
                          <div className="rcv-x-row rcv-x-row-sel">
                            <span>
                              {selectedPaymentLineUuids.length} producto{selectedPaymentLineUuids.length === 1 ? "" : "s"} seleccionado{selectedPaymentLineUuids.length === 1 ? "" : "s"}
                            </span>
                            <span className="rcv-x-row-amount">{fmt(selectedPaymentSubtotal)}</span>
                          </div>
                          <button
                            type="button"
                            className="rcv-x-btn rcv-x-btn-primary"
                            disabled={selectedPaymentLineUuids.length === 0}
                            onClick={openPaySelectedLines}
                          >
                            {selectedPaymentLineUuids.length === 0
                              ? "Selecciona productos para continuar"
                              : <>Pagar {fmt(selectedPaymentSubtotal)} <i className="bi bi-arrow-right" aria-hidden="true" /></>}
                          </button>

                          {isPrepayTable ? (
                            <button
                              type="button"
                              className="rcv-x-btn rcv-x-btn-quiet d-none d-lg-block d-md-block  "
                              disabled={isFinalizingAccount}
                              onClick={handleFinalizeAccount}
                            >
                              {isFinalizingAccount ? "Cerrando cuenta..." : "Cerrar cuenta"}
                            </button>
                          ) : null}
                          <div className="d-block d-lg-none d-md-none">

                            <AddMoreButton
                              label={t('checkout.addMoreProducts')}
                              onClick={() => navigate("/menu")}
                            />
                          </div>


                        </div>
                      ) : isPrepayTable ? (
                        <div className="rcv-x-actions rounded-3">
                          <button
                            type="button"
                            className="rcv-x-btn rcv-x-btn-quiet"
                            disabled={isFinalizingAccount}
                            onClick={handleFinalizeAccount}
                          >
                            {isFinalizingAccount ? "Cerrando cuenta..." : "Cerrar cuenta"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </aside>
                ) : null}
              </div>
            ) : null}


            <div className="ck-cart-items d-none d-lg-block d-md-block border-danger">
              {hasActiveTableSession && !isPrepayTable && cartItems.length > 0 ? (
                <p className="ck-section-label">{t('checkout.pendingProductsToSend')}</p>
              ) : null}
              {/*hasActiveTableSession && cartItems.length === 0 ? (
                <div className="alert alert-secondary py-2">
                  {t('checkout.noNewProductsToSend')}
                </div>
              ) : null*/}
              {!hasActiveTableSession && cartItems.map(item => (
                <div className="co-item" key={item.id}>
                  {/* thumb */}
                  {item.img
                    ?
                    <>
                      {/* <img className="co-thumb" src={item.img} alt={item.name} loading="lazy" /> */}
                      <img className="co-thumb border" loading="lazy"
                        src={buildCloudinaryImageUrl({ url: item.img, width: 300, quality: "auto", type: "image" })}
                        onError={e => e.target.style.opacity = 0}
                      />
                    </>
                    : <div className="co-thumb-ph border"><i className="bi bi-image" /></div>
                  }

                  {/* body */}
                  <div className="co-item-body">
                    <div className="co-item-top">
                      <span className="ck-item-name">{item.name}</span>
                    </div>

                    <span className="ck-item-desc">{item.desc}</span>

                    <div className="co-controls">
                      <div className="co-qty">
                        <button className="co-qty-btn" onClick={() => updateQty(item.id, -1)}>−</button>
                        <span className="co-qty-num">{item.qty}</span>
                        <button className="co-qty-btn" onClick={() => updateQty(item.id, 1)}>+</button>
                      </div>
                      <span className="ck-item-price">{fmt(item.price * item.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        );

      /* ── STEP: CONTACT ── */
      case 'contact':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.contactInfo')}</p>
            <Field label={t('checkout.fullName')} id="c-name" value={contact.name}
              onChange={v => setContact(c => ({ ...c, name: v }))} placeholder={t('checkout.fullNamePlaceholder')}
              required error={contactErrors.name} />
            <Field label={t('checkout.email')} id="c-email" type="email" value={contact.email}
              onChange={v => setContact(c => ({ ...c, email: v }))} placeholder={t('checkout.emailPlaceholder')}
              required error={contactErrors.email}
              hint={t('checkout.emailHint')} />
            <Field label={t('checkout.phone')} id="c-phone" type="tel" value={contact.phone}
              onChange={v => setContact(c => ({ ...c, phone: v }))} placeholder={t('checkout.phonePlaceholder')}
              required error={contactErrors.phone} />
          </div>
        );

      /* ── STEP: DELIVERY METHOD ── */
      case 'delivery':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.howReceiveOrder')}</p>

            {moduletypeOrder && (deliveryButton || selectedPickAndGoRule) && (
              <div className="ck-radio-grid">
                <RenderIf isTrue={deliveryButton === true}>
                  <RadioCard
                    id="delivery"
                    selected={option === "delivery"}
                    onSelect={() => {
                      setOption("delivery");
                      setDeliveryMethod("delivery");
                    }}
                    icon="🛵"
                    title="Delivery"
                    sub={t('checkout.deliverySub')}
                  />
                </RenderIf>

                <RenderIf isTrue={!!selectedPickAndGoRule}>
                  <RadioCard
                    id="pick"
                    selected={option === "pick"}
                    onSelect={() => {
                      setOption("pick");
                      setDeliveryMethod("pickup");
                    }}
                    icon={usingEventPickRule ? "📅" : "🏬"}
                    title={usingEventPickRule ? t('checkout.eventDeliveryTitle') : t('checkout.pickGoTitle')}
                    sub={t('checkout.pickSub')}
                  />
                </RenderIf>
              </div>
            )}
          </div>
        );

      /* ── STEP: ADDRESS ── */
      case 'address':
        if (showAddressForm) {
          return (
            <div>
              <p className="ck-card-title">{t('checkout.newAddress')}</p>
              <AddressForm
                onSave={handleSaveAddress}
                onCancel={() => setShowAddressForm(false)}
                clientData={clientData}
                deliveryRules={[...deliveryFeeRule, ...deliveryFeeRuleSchedule]}
                contactPhone={contact.phone}
                contactEmail={contact.email}
              />
            </div>
          );
        }

        return (
          <div>
            <p className="ck-card-title">{t('checkout.deliveryAddress')}</p>
            <p className="ck-section-label">{t('checkout.myAddresses')}</p>

            <div className="ck-addr-list">
              {displayAddresses.length > 0 ? (
                displayAddresses.map(addr => {
                  const isSelected = String(selectedAddress) === String(addr.id);

                  return (
                    <div
                      key={addr.id}
                      className={`ck-addr-card${isSelected ? ' selected' : ''}`}
                      onClick={() => handleSelectAddress(addr)}
                    >
                      <div className="ck-radio-indicator">
                        {isSelected ? (
                          <div className="ck-radio-indicator-check">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        ) : (
                          <div className="ck-radio-indicator-dot" />
                        )}
                      </div>

                      <div className="ck-addr-card-icon">{addr.icon || "📍"}</div>

                      <div className="ck-addr-card-body">
                        <p className="ck-addr-card-label">{addr.label || t('checkout.addressFallback')}</p>
                        <p className="ck-addr-card-line">
                          {[addr.house_num, addr.name].filter(Boolean).join(", ") || addr.line}
                        </p>
                        <p className="ck-addr-card-sub">
                          {addr.additional_info || addr.sub || addr.phone || ""}
                        </p>
                        {!!addr.phone && (
                          <p className="ck-addr-card-sub">{t('checkout.telLabel', { phone: addr.phone })}</p>
                        )}
                      </div>

                      <button
                        type="button"
                        className="ck-addr-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAddress(addr.id);
                        }}
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="ck-empty">
                  {t('checkout.noSavedAddresses')}
                </div>
              )}

              <button
                className="ck-add-addr-btn"
                onClick={() => setShowAddressForm(true)}
              >
                <IcoPlus /> {t('checkout.addNewAddress')}
              </button>
            </div>
          </div>
        );

      /* ── STEP: WHEN (delivery only) ── */
      case 'when':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.whenOrder')}</p>

            {option === "delivery" ? (
              <div className="ck-radio-grid">
                <RadioCard
                  id="now"
                  selected={deliveryTiming === "now"}
                  onSelect={(v) => {
                    setDeliveryTiming(v);
                    setWhenDelivery(v);
                    setDeliveryDate(null);
                    setDeliverySlot(null);
                  }}
                  icon="⚡"
                  title={t('checkout.nowTitle')}
                  sub={t('checkout.nowSub')}
                />
                {deliveryFeeRuleSchedule.length > 0 && (
                  <RadioCard
                    id="scheduled"
                    selected={deliveryTiming === "scheduled"}
                    onSelect={(v) => {
                      setDeliveryTiming(v);
                      setWhenDelivery(v);
                    }}
                    icon="📅"
                    title={t('checkout.scheduleDelivery')}
                    sub={t('checkout.chooseDayTime')}
                  />
                )}
              </div>
            ) : (
              <div className="alert alert-success rounded-4">
                {usingEventPickRule ? (
                  <>📅 {t('checkout.selectedEventDelivery')}</>
                ) : (
                  <>🏬 {t('checkout.selectedPickGo')}</>
                )}
              </div>
            )}
          </div>
        );

      /* ── STEP: SHIPPING METHODS ── */
      case 'shipping':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.shippingMethod')}</p>

            {selectedAddress && (
              <div className="ck-delivery-banner">
                {t('checkout.deliverTo')} <strong>{addresses.find(a => a.id === selectedAddress)?.line}</strong>
              </div>
            )}
            {(() => {
              const zoneRule = deliveryFeeRule.find(r => r.fee_type === "zone") || deliveryFeeRuleSchedule.find(r => r.fee_type === "zone");
              if (!zoneRule || !addressNowLocation?.lat || !addressNowLocation?.lng) return null;

              const zoneMatch = resolveZoneFee(zoneRule, addressNowLocation);

              if (!zoneMatch) {
                return (
                  <div className="ck-delivery-banner ck-delivery-banner-error">
                    ⚠️ Tu ubicación está fuera del área de cobertura. Elige otra dirección para continuar.
                  </div>
                );
              }

              return (
                <div className="ck-delivery-banner ck-delivery-banner-success">
                  📍 Zona de cobertura: <strong>{zoneMatch.zone_name}</strong>
                </div>
              );
            })()}
            <StepShippingMethods
              rules={deliveryTiming === "scheduled" ? deliveryFeeRuleSchedule : deliveryFeeRule}
              selectedRuleId={selectedDeliveryRule?.id}
              quotes={shippingQuotes}
              loading={shippingQuotesLoading}
              onSelect={(rule) => {
                setSelectedDeliveryRule(rule);

                if (deliveryTiming === "scheduled") {
                  const def = getDefaultDeliveryDateAndSlot(rule);
                  setDeliveryDate(def?.date || null);
                  setDeliverySlot(def?.slot || null);
                }
              }}
            />
          </div>
        );

      /* ── STEP: DATE + TIME ── */
      case 'datetime':
        return (
          <div>
            <p className="ck-card-title">
              {option === "pick" ? t('checkout.pickupDateTime') : t('checkout.deliveryDateTime')}
            </p>

            {option === "pick" && (
              <StepDateTime
                mode="pick"
                selectedDate={pickDate}
                selectedSlot={pickSlot}
                onDateChange={(iso) => {
                  setPickDate(iso);
                  setPickSlot(
                    iso
                      ? getFirstEnabledSlot(iso, selectedPickAndGoRule)
                      : null
                  );
                }}
                onSlotChange={setPickSlot}
                minDate={pickMinDate}
                maxDate={pickMaxDate}
                isDateDisabled={isDateDisabledPick}
                rule={selectedPickAndGoRule}
                hasActiveEventRules={usingEventPickRule}
              />
            )}

            {option === "delivery" && deliveryTiming === "scheduled" && (
              <StepDateTime
                mode="delivery"
                selectedDate={deliveryDate}
                selectedSlot={deliverySlot}
                onDateChange={(iso) => {
                  setDeliveryDate(iso);
                  setDeliverySlot(
                    iso
                      ? getFirstEnabledSlot(iso, selectedDeliveryRuleRef.current)
                      : null
                  );
                }}
                onSlotChange={setDeliverySlot}
                minDate={deliveryMinDate}
                maxDate={deliveryMaxDate}
                isDateDisabled={isDateDisabledDelivery}
                rule={selectedDeliveryRuleRef.current}
              />
            )}

            {option === "delivery" && deliveryTiming === "now" && (
              <div className="alert alert-info rounded-4">
                {t('checkout.immediateProcessing')}
              </div>
            )}
          </div>
        );

      /*-STEP: STAND (event only) -*/
      case "stand":
        return (
          <div>
            <p className="ck-section-label">{t('checkout.standData')}</p>
            <p className="ck-muted" style={{ marginBottom: "1rem" }}>
              {t('checkout.standRequired')}
            </p>

            <div className="ck-stack">
              <Field
                label={t('checkout.standName')}
                id="stand-name"
                value={standName}
                onChange={setStandName}
                placeholder={t('checkout.standNamePlaceholder')}
                error={standErrors.standName}
              />

              <Field
                label={t('checkout.standNumber')}
                id="stand-number"
                value={standNumber}
                onChange={setStandNumber}
                placeholder={t('checkout.standNumberPlaceholder')}
                error={standErrors.standNumber}
              />
            </div>
          </div>
        );
      /* ── STEP: INVOICE ── */
      case 'invoice':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.customizeInvoice')}</p>
            <StepInvoice
              wantsInvoice={wantsInvoice}
              onToggle={() => setWantsInvoice(v => !v)}
              name={invoiceName}
              ruc={invoiceRuc}
              dv={invoiceDv}
              onNameChange={setInvoiceName}
              onRucChange={setInvoiceRuc}
              onDvChange={setInvoiceDv}
              errors={invoiceErrors}
            />
          </div>
        );

      /* ── STEP: PAYMENT ── */
      case 'payment':
        return (
          <div>
            <p className="ck-card-title">{t('checkout.paymentMethod')}</p>
            <div className="ck-notice">
              <IcoLock /> {t('checkout.sslProtected')}
            </div>
            <PaymentStep
              methods={payments}
              selected={
                selectedPayment
                  ? `${selectedPayment.payment_id}-${selectedPayment.integration?.id || selectedPayment.integration_id || 0}`
                  : ""
              }
              onSelect={setSelectedPayment}
            />

            {selectedPayment && isPowerTranzPayment(selectedPayment) && isClientLoggedInForPayment() && (
              <SavedCardsPanel
                machineId={safeParse(localStorage.getItem("machine"), null)?.id}
                saveCard={saveCardForFuture}
                onSaveCardChange={setSaveCardForFuture}
              />
            )}

            <div className="ck-divider" />
            <div style={{ padding: '.75rem 1rem', background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem', fontWeight: 600, color: 'var(--text)' }}>
                <span>{t('checkout.totalToPay')}</span>
                <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem' }}>{fmt(total)}</span>
              </div>
              {shipCost > 0 && (
                <p style={{ fontSize: '.72rem', color: 'var(--text-3)', marginTop: '.2rem' }}>{t('checkout.includesShipping', { amount: fmt(shipCost) })}</p>
              )}
            </div>
          </div>
        );

      default: return null;
    }
  };

  const isPayStep = currentStepKey === 'payment';
  const isAddressFormOpen = currentStepKey === 'address' && showAddressForm;
  const nextButtonLabel = currentStepKey === "invoice"
    ? wantsInvoice
      ? isValidatingInvoice
        ? t('checkout.validating')
        : invoiceStepValidated
          ? t('checkout.next')
          : t('checkout.validate')
      : t('checkout.next')
    : t('checkout.next');
  const showNextArrow = nextButtonLabel === t('checkout.next');

  if (loginRequired) {
    return (
      <LoginUser
        modalUser={modalUser}
        setModalUser={setModalUser}
        shoppingCart={true}
        closedModalLoginAndRegister={closedModalLoginAndRegister}
      />
    );
  }

  if (PreOrder_id && isDirectOrderLoading) {
    return (
      <div className="ck-root">
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div className="ck-header">
            <h1 className="ck-title">{t('checkout.finishOrder')} </h1>
          </div>

          <div className="ck-card ck-direct-loading-card">
            <div className="ck-direct-loading-spinner" aria-hidden="true" />
            <p className="ck-direct-loading-title">{t('checkout.loadingOrder')}</p>
            <p className="ck-direct-loading-text">
              {t('checkout.loadingOrderSubtitle')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ck-root">
      <div style={{ maxWidth: hasActiveTableSession ? 1240 : 1060, margin: '0 auto' }}>
        <div className={`ck-header${hasActiveTableSession ? " rcv-x-page-head" : ""}`}>
          {/* <p className="ck-logo">Bussines nAme </p> */}
          <h1 className="ck-title">{t('checkout.finishOrder')}</h1>
          {hasActiveTableSession ? (
            <p className="rcv-x-head-meta">
              <span>Mesa {tableLabel}</span>
              <span className="rcv-x-head-sep" aria-hidden="true">·</span>
              <span>{participants.length || 1} participante{(participants.length || 1) === 1 ? "" : "s"}</span>
              <span className="rcv-x-head-sep" aria-hidden="true">·</span>
              <span className={`rcv-x-live${socketConnected ? "" : " rcv-x-live-wait"}`}>{socketConnected ? "En vivo" : "Reconectando"}</span>
            </p>
          ) : null}
        </div>

        {/* Desktop stepper */}
        {!hasActiveTableSession && (
          <StepperBar steps={steps} currentStep={currentStep} onGoToStep={setCurrentStep} />
        )}
        {/* Mobile stepper */}
        {!hasActiveTableSession && (
          <MobileStepperBar steps={steps} currentStep={currentStep} onGoToStep={setCurrentStep} />
        )}


        <div className={`ck-inner${hasActiveTableSession && isPrepayTable ? " ck-inner-solo" : ""}`}>
          {/* LEFT: Step content */}
          <div>
            <div
              className={`ck-card${["datetime", "payment"].includes(currentStepKey) ? " ck-card-long-step" : ""}`}
              key={currentStepKey}
            >
              {["datetime", "payment"].includes(currentStepKey) ? (
                <div className="ck-card-scroll-content">{renderStep()}</div>
              ) : (
                renderStep()
              )}

              {/* Actions */}
              {!isAddressFormOpen && !(hasActiveTableSession && currentStepKey === "cart") && (
                <div className="ck-actions notranslate" translate="no">
                  {currentStep > 0 && (
                    <button className="ck-btn-back" onClick={goPrev}>
                      <span className="ck-btn-icon-slot" aria-hidden="true">
                        <IcoArrow dir="left" />
                      </span>
                      <span className="ck-btn-label">{t('common.back')}</span>
                    </button>
                  )}
                  {isPayStep ? (
                    <button
                      className="ck-btn-pay"
                      onClick={handleNext}
                      disabled={!canProceed() || isSubmittingPayment}
                    >
                      <span className="ck-btn-icon-slot" aria-hidden="true">
                        <IcoLock />
                      </span>
                      <span className="ck-btn-label">
                        {isSubmittingPayment
                          ? t('checkout.processing')
                          : totalsNeedRefresh
                            ? t('checkout.updatingTotal')
                            : t('checkout.payAmount', { amount: fmt(total) })}
                      </span>
                    </button>
                  ) : (
                    <button
                      className="ck-btn-primary"
                      onClick={handleNext}
                      disabled={
                        currentStepKey === "invoice"
                          ? wantsInvoice
                            ? isValidatingInvoice || (!invoiceStepValidated && (!invoiceName.trim() || !invoiceRuc.trim()))
                            : false
                          : !canProceed()
                      }
                    >
                      <span className="ck-btn-label">{nextButtonLabel}</span>
                      <span className="ck-btn-icon-slot" aria-hidden="true">
                        {showNextArrow ? <IcoArrow /> : null}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Order summary (desktop only) - Pre-pago has no whole-account "Pagar": payment
              happens per-line via "Pagar seleccionados"/"Finalizar cuenta" in "Pedido compartido"
              on the left, so this panel has nothing useful to add there. */}
          {hasActiveTableSession && !isPrepayTable ? (
            <TableRestaurantPaymentSummary
              tableLabel={tableLabel}
              branchName={tableBranchName}
              areaName={tableAreaName}
              sessionStatus={tableSessionDisplayStatus}
              pendingCount={ownDraftLines.reduce((sum, item) => sum + sharedLineQuantity(item), 0)}
              acceptedCount={sharedTableLines.filter((item) => item.line_status !== "DRAFT").reduce((sum, item) => sum + sharedLineQuantity(item), 0)}
              orderStatus={tableOrderStatus}
              paymentAuthorized={Boolean(tableSession?.payment_authorized)}
              paymentRequested={Boolean(tableSession?.payment_requested)}
              paymentMode={tableSession?.payment_mode}
              hasOrderLines={sharedTableLines.length > 0}
              onPay={() => {
                // Whole-account payment (pre-existing behavior) - explicitly null so it's never
                // confused with a lingering partial-payment selection from paymentLineUuidsForModal.
                setPaymentLineUuidsForModal(null);
                setShowTablePaymentMethods(true);
              }}
              onRequestPayment={handleRequestPayment}
            />
          ) : !hasActiveTableSession ? (
            <OrderSummary
              items={cartItems}
              deliveryMethod={deliveryMethod}
              subtotal={subtotal}
              itbms={totalItbms}
              shipCost={shipCost}
              total={total}
              tip={tip}
            />
          ) : null}
        </div>
      </div>

      {/* Cart modal (mobile) */}
      {!hasActiveTableSession && cartModalOpen && (
        <CartModal
          items={cartItems}
          deliveryMethod={deliveryMethod}
          subtotal={subtotal}
          itbms={totalItbms}
          shipCost={shipCost}
          total={total}
          tip={tip}
          onClose={() => setCartModalOpen(false)}
        />
      )}

      {/* Yappy modal */}
      {!hasActiveTableSession && showYappyBtnModal && (
        <ModalYappyBtn
          showModal={showYappyBtnModal}
          setShowModal={(value) => {
            setShowYappyBtnModal(value);
            if (value === false) {
              localStorage.removeItem("YappyBtnOrderParams");
              localStorage.removeItem("yappy_transaction_id");
              releasePaymentLock();
            }
          }}
          nextStepYappy={nextStepYappy}
          phone={contact.phone || ""}
        />
      )}

      {!hasActiveTableSession && (
        <TilopayPaymentModal
          show={showTilopayModal}
          onClose={closeTilopayModal}
          payer={{
            name: contact.name || clientData?.name || "",
            email: contact.email || clientData?.email || "",
            accountEmail: clientData?.email || "",
            phone: contact.phone || clientData?.phone || "",
            // Entrega: la direccion del cliente; recoger en tienda: la de la sucursal (Tilopay exige una direccion de facturacion)
            address: (option === "delivery" ? addressNowLocation?.line : "") || generalData?.[0]?.data?.branchoffice?.direction || ""
          }}
          prepareSession={prepareTilopaySession}
          createPreOrder={createTilopayPreOrder}
          onApproved={handleTilopayApproved}
        />
      )}

      {!hasActiveTableSession && (
        <ModalStatusPayment
          showModalStatusPayment={showModalStatusPayment}
          setShowModalStatusPayment={setShowModalStatusPayment}
          paymentInfo={selectedPayment || {}}
          paymentApproval={paymentApproval}
          invoiceSubtotal={subtotal}
          invoiceTotal={total}
          invoiceTotalDiscount={totalDiscount}
          invoiceTotalItbms={totalItbms}
          invoiceTip={tip}
          invoiceDeliveryFee={shipCost}
        />
      )}

      {/**Power tranz iframe */}
      {!hasActiveTableSession && htmlData && (
        <Modal
          centered
          className="modal-dialog-kk"
          fullscreen
          show={showIframe}
        >
          <Modal.Body className="section-message-yappy">
            <iframe
              srcDoc={htmlData}
              title="PowerTranz Payment"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "10px",
                background: "transparent"
              }}
              sandbox="allow-scripts allow-forms allow-same-origin"
            />
            <button
              className="ck-btn-back"
              style={{ position: "absolute", top: 16, right: 16, zIndex: 20 }}
              onClick={closeIframe}
            >
              {t('common.cancel')}
            </button>
          </Modal.Body>
        </Modal>
      )}

      {/* La sesion de mesa que este cliente tenia activa termino - el mesero cerro/anulo la cuenta, o
          el propio cliente termino de pagar en otra pestaña/dispositivo. Sin esto, TableSessionContext
          ya vacio su estado (clearSessionState) y esta pagina cae de vuelta al carrito normal (vacio,
          sin relacion con la mesa) sin ninguna explicacion - ver closedSessionNotice en
          TableSessionContext.jsx. Overlay a proposito (no un return temprano): cubre lo que sea que
          esta pantalla haya quedado mostrando por debajo, sin tener que tocar esa logica. */}
      {closedSessionNotice ? (
        <div className="ck-session-ended-overlay" role="dialog" aria-modal="true" aria-label="Cuenta cerrada">
          <div className="ck-session-ended-card">
            <div className="ck-session-ended-icon" aria-hidden="true">
              <i className="bi bi-check-circle-fill" />
            </div>
            <h2 className="ck-session-ended-title">Tu cuenta se cerró</h2>
            <p className="ck-session-ended-text">
              Gracias por tu visita. Si sigues en el restaurante y quieres pedir algo más, escanea de nuevo el código QR de tu mesa.
            </p>
            <button
              type="button"
              className="ck-session-ended-btn"
              onClick={() => { dismissClosedSessionNotice(); navigate("/"); }}
            >
              Entendido
            </button>
          </div>
        </div>
      ) : null}

    </div>
  );
}

export default Checkout;